import {_, action, autorun, axios, computed, jsonComputedOptions, jsondiff, memoize, moment, observable, observe, pluralize, reaction, runInAction, tinycolor, toJS, toJSRecurse, when} from '@api'
import {CustomAttributeDefinition, CustomAttributeType, UNASSIGNED} from '@api/graphql'
import {TypeColumn} from '@inovua/reactdatagrid-enterprise/types'
import {AbstractSidebarStore, AssortmentRootStore, BuilderCardFieldStore, BuilderCardStore, C8HierarchyStore, CardViewToolbarMenuPanelStore, CatalogFiltersChipsStore, CatalogItemDetailsStore, CatalogItemsListStore, CatalogRootStore, ChangeLogFlexLayoutTabStore, CollectionTargetsToolbarStore, Dashboard, DataGridStore, dataMapperConfig, FILTER_UNASSIGNED_ID, FilterChipStore, ICatalogItemDetailsPathElement, OFFLINE_FILTER_VALUE_ID, PresentationModeContentStore, SeasonSelectorFilterDropdownModalStore, SeasonSelectorFilterDropdownStore, SeasonSelectorHierarchyDropdownStore, SeasonSelectorStore, SortMenuItemsStore, ToggleFilterParams, useStores, VirtualizedProductViewStore, WorkspaceRelationshipStore} from '@modules'
import {CatalogReportViewStore} from '@modules/catalog/internal/gridList/components/groupBy/GroupByCatalog/GroupByCatalog_CatalogItems/ReportView/CatalogReportViewStore'
import {CatalogDataItemUIStore} from '@modules/catalog/sidebar/CatalogItemsList'
import {CatalogSearcherStore} from '@modules/catalogSearcher/CatalogSearcherStore'
import {
  ActionButtonsConfig,
  aws,
  BillOfMaterialsRollupConfig,
  CatalogActionConfig,
  CatalogConfig,
  CatalogImageService,
  CatalogImageSourceConfig,
  CatalogPDFStore,
  CatalogPerspectiveConfig,
  CatalogSerializableSettings,
  CollectionTargetsToolbarConfig,
  configs,
  DataPng,
  deployments,
  DetailsViewConfig,
  DetailsViewsByIndex,
  DragDropEventHandlers,
  globalStores,
  GroupByGrouping,
  ICatalogFeaturesConfig,
  ICatalogFieldConfig,
  ICustomVlpHierarchy,
  IDataMapConfig,
  IDragDropElement,
  IGroupOrderBy,
  IOfflineCatalogItem,
  ISectionConfig,
  isOfflineId,
  IWorkspacePreset,
  LibraryItemConfig,
  LibraryItemConfigWrapper,
  nav,
  OFFLINE_ID,
  onSiteError,
  SeasonSelectorFieldConfig,
  shouldIgnoreCatalogField,
  StoresInstance,
  TabNode,
  ui,
  VLP_COLLECTION_GROUP,
  VLP_FOLDER_GROUP,
  VLP_PLAN_GROUP,
  Workspace,
  WorkspaceConfig,
  WorkspaceExportConfig,
  DeploymentPdfConfig,
  WorkspaceElasticSearchIndex
} from '@stores'
import {PdfArtifactDropdownMenuStore} from '@modules/pdf/serverRenderedPDF/components'
import {imageBreakpoints} from '@stores'
import {CatalogAggregationStore} from '@stores/catalog/CatalogAggregationStore'
import {CatalogFooterStore} from '@stores/catalog/CatalogFooterStore'
import {IAggregationResult} from '@stores/elasticsearch'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {VLP_SUBCATEGORIES} from '@stores/vlp'
import {authStore, C8ImageSize, guid, ImageRatio, recursivelyRemovePrivateFields, removeUndefinedsNested} from '@utils'
import {CancelTokenSource} from 'axios'
import {toHeaderCase} from 'js-convert-case'
import {IObservableArray, IReactionDisposer, ObservableMap, trace} from 'mobx'
import {computedFn, IDisposer} from 'mobx-utils'
import {CatalogCardViewStore, CatalogCreateItemStore, CatalogDialogsStore, CatalogElasticSearchStore, CatalogMutationsStore, CatalogNotesStore, CatalogPresentationStore, CatalogRelationshipsStore, CatalogSelectionStore, GroupByStore, OfflineCatalogItemDatabase, OfflineItemWrapper} from './'
import {CatalogAssortmentsStore} from './CatalogAssortmentsStore'
import {CatalogSettingsStore, DEFAULT_CATALOG_USER_SETTINGS, ICatalogUserSettings} from './CatalogSettingsStore'
import {MassProductCreateStore} from './MassProductCreateStore'

export type RefetchType = 'catalogItems' | 'libraries' | string

export const CATALOG_ID_SEPARATOR = '.'  // Breaks things when there's a . in the name of a group - Suggest changing to something like \0 or a unicode hyphen or .

/// region Constants

export type CatalogItemRecord = any

/**
 * We show a spinner until the catalog loads and can console.group to debug all the things that we block on before we can do our first card paint (counts, filters, config, etc)
 */
export const LOG_GLOBAL_CATALOG_LOAD = DEBUG && false

/// endregion

export type IParentRecord = { id: string, __parent: IParentRecord, __path?: string }

export interface ImageRecord {
  exId: string,
  __parent?: IParentRecord
  __path?: string
  width?: number
  height?: number
  autoTransparency?: boolean
  file?: { lastModified?: string }
}

export interface IValidCatalogItem {
  [k: string]: boolean
}

export type TooltipLocation = | 'bottom-end'
  | 'bottom-start'
  | 'bottom'
  | 'left-end'
  | 'left-start'
  | 'left'
  | 'right-end'
  | 'right-start'
  | 'right'
  | 'top-end'
  | 'top-start'
  | 'top'

export type Tense = 'singular' | 'plural'

const centricImageTypeMap: { [size in C8ImageSize]: string } = {
  smallImage: 'SmallImage',
  largeImage: 'LargeImage', // This sucks, but Viewable/LargeImage can be 8+ megs on Gant
  thumbnailImage: 'Thumbnail',
  image: 'Viewable'
}

export interface OrderByField {
  field: string
  text: string
  type?: string
}

interface ICatalogComponentStores {
  presentation?: PresentationModeContentStore
  dashboard?: Dashboard
  viewOptionsToolbar?: CardViewToolbarMenuPanelStore
  targetsToolbar: CollectionTargetsToolbarStore
  virtualizer?: VirtualizedProductViewStore

  currentCardPopover: BuilderCardStore
  cards: { [id: string]: BuilderCardStore }
  itemsListItem: { [id: string]: CatalogDataItemUIStore }
  contextWorkspaceRelationship: WorkspaceRelationshipStore
  reportView: CatalogReportViewStore
  itemsList: CatalogItemsListStore
  root: CatalogRootStore

  sort: SortMenuItemsStore
  dataGridStore?: DataGridStore
  assortmentRoots?: Record<string | 'catalog', AssortmentRootStore>
  changeLogTabStores?: ChangeLogFlexLayoutTabStore
  pdfArtifactDropdownMenu?: PdfArtifactDropdownMenuStore
  seasonSelectorDetailsDropdown?: SeasonSelectorFilterDropdownModalStore
  filterChips?: CatalogFiltersChipsStore
}

export const C8_TO_IMAGE_SIZE_ENUM = {
  'smallImage': '!400,400',
  'thumbnailImage': '!400,400',
  'mediumImage': '!800,800',
  'largeImage': '!1600,1600',
  'image': 'full'
}

export class CatalogStore {
  //catalogGridStore: WijmoCatalogGridStore // Lazy initialized, don't include here to avoid bundle growth
  @observable.ref settingsStore: CatalogSettingsStore
  @observable.ref relationships: CatalogRelationshipsStore
  @observable.ref dialogs: CatalogDialogsStore
  @observable componentStores: Partial<ICatalogComponentStores> = {itemsListItem: {}, cards: {}, assortmentRoots: {}}
  @observable.ref aggregation: CatalogAggregationStore
  @observable.ref stores: StoresInstance
  @observable.ref assortments: CatalogAssortmentsStore
  @observable.ref img: CatalogImageService
  @observable.ref presentation: CatalogPresentationStore
  @observable.ref pdfCatalogStore: CatalogPDFStore

  @observable searchActive: boolean = false

  get ui() { return this.stores.ui}

  hideAttributeFromSidebar = new Set([])

  toDispose: Array<Function> = []

  @computed get canShowAggs() { return this.features?.groupBy?.detailAggregations != null }

  get currentUser() { return this.stores.currentUser ?? globalStores?.currentUser}
  get strings() { return this.stores.strings ?? globalStores?.strings}

  get contextMenu() { return this.stores.contextMenu ?? globalStores?.contextMenu}

  get flexLayout() { return this.stores.flexLayout ?? globalStores?.flexLayout}

  get filter() {return this.stores.filter ?? globalStores?.filter}

  get dragDrop() { return this.stores.dragDrop ?? globalStores?.dragDrop}

  get vlp() { return this.stores.vlp ?? globalStores?.vlp}

  get pdf() { return this.stores.pdf ?? globalStores?.pdf}

  get c8() { return this.stores.c8 ?? globalStores?.c8}

  get presets() { return this.stores.presets }

  get sidebar() { return this.stores.sidebar ?? globalStores?.sidebar}

  get workspaces() { return this.stores?.workspaces ?? globalStores?.workspaces}

  get site() { return this.stores.site ?? globalStores?.site}

  @computed get foreignKeyFields() {
    return this.catalogConfig.fields.filter(f => f.esIndex)
  }

  @computed get isDataTableView() {
    const {settings: {viewMode}} = this
    return _.includes(['table', 'report', 'grid'], viewMode)
  }

  get workspace() {
    return this._workspace ?? this.stores?.workspace ?? this.stores?.workspaces?.workspace
  }

  @action onError = (err: any) => {
    this.stores.site.onError(err)
  }

  /**
   * After we duplicate, the newly created item may not be visible in the catalog due to the load more behavior - the user will still want to see it though so we need to be sure to query for these as well
   */
  newlyCreatedIds = observable.array<string>([], {deep: false})

  /**
   * YFTS-6813 When we edit / modify items they may be being displayed elsewhere (such as in the right sidebar details).  If so, we need to either copy or refetch the data in our respective other components
   */
  @observable lastModifiedItem?: { index: string, id: any, data?: any }

  get queryParamsFor() { return this.settingsStore?.queryParamsFor}

  @computed get perspectives() { return this.catalogConfig?.perspectives ?? [] }

  @computed get imageRatios(): Array<{ label: string, id: ImageRatio }> {
    const {strings} = this
    return [
      {
        label: strings.square,
        id: 'square'
      },
      {
        label: strings.portrait,
        id: 'portrait'
      },
      {
        label: strings.landscape,
        id: 'landscape'
      },
      {
        label: strings.auto,
        id: 'auto'
      }
    ]
  }

  /**
   * When we inline change an item it may move between groups
   * After we refetch groups, expand to the group containing the item again, and finally render the card, we want to scroll it into view
   *
   * turned off - jarring and slow
   */
  // @observable savingItemId: string


  @computed get canShowAssortment() {
    return _.size(this.assortments?.assortmentsById) > 0
  }

  @computed get isShowingAssortment() {
    return this.settings?.assortment?.id != null
  }

  get cloudPDF() { return this.stores.cloudPDF}

  @observable tableColumns: Array<TypeColumn> = []

  @computed get showTable() {
    return !this.hasLoadedSettings ? false : this.settings.viewMode === 'table'
  }

  @computed get canAddImage() {
    // if (_.isFunction(this.catalogConfig?.actionButtons?.uploadImages)) {
    //   const {users: {_currentUserAndRoles: user}} = this
    //   return this.catalogConfig?.actionButtons?.uploadImages({user})
    // }
    // return this.catalogConfig?.actionButtons?.uploadImages !== false
    return this.isActionButtonEnabled('uploadImages')
  }

  @computed get id() { return this.workspaceConfig?._id}

  /**
   * Lots of future TODO with this
   * We get constructed for 3 reasons right now:
   * 1.  Globally, so that we can load things for the site header like workspace counts as well as have access to utility functions like idFor that are used everywhere
   * 2.  By {@see Workspace} in order to facilitate finding fields by names, relationships, etc.  Stuff like this that is consistent across the deployment as a whole should be moved elsewhere and shared
   * 3.  By a given render tree in our react UI where we want to instance the stores, such as separate flex tabs {@see RootCatalogViewFlexLayoutTab}
   * @param _workspace
   * @param stores
   */
  constructor(public _workspace?: Workspace, stores?: StoresInstance) {
    /** When tab is activated, invalidate our current queries as another window may have changed things **/
    // document.onvisibilitychange = (e) => {
    //   if (document.visibilityState === 'visible') {
    //     this.refetch()
    //   }
    // }

    this.stores = stores ?? globalStores
    const {_initialize} = this
    // Wait to do this as we may not have processed the URL yet to pick up search params or other things that might affect our initial query
    if (_workspace || !stores.isGlobalStore) {
      _initialize()
    } else {
      synchronizeStores(_initialize)
    }
  }

  footer: CatalogFooterStore
  searcher: CatalogSearcherStore
  selection: CatalogSelectionStore
  notes: CatalogNotesStore
  createItem: CatalogCreateItemStore
  cv: CatalogCardViewStore

  @computed get hasMerch() {
    return _.isNumber(this.workspaceConfig.merchFolderLevel)
  }

  get debugId() {
    return `[CatalogStore] - [${this.stores.storeId}]${this.workspace ? ` - [${this.workspace.id}]` : ''}`
  }

  @computed get isCurrentWorkspace() {
    const {_workspace, stores: {workspace}} = this
    return workspace?.isCurrentWorkspace //!_workspace || _workspace?.isCurrentWorkspace
  }

  /**
   * We create catalog stores to get access to dictionaries and such for specific workspaces but they are not meant for rendering
   *
   */
  @computed get isOnlyForWorkspaceSanitize() {
    return this._workspace != null
  }

  _toDispose: Array<IDisposer> = []
  @action dispose = () => {
    this._toDispose.forEach(d => d())
    this.groupBy.dispose()
    this.seasonSelector?.dispose?.()
    this.selection?.dispose()
    this.searcher?.dispose()
    this.cv?.dispose()

    //this.settingsStore.dispose()
  }

  @computed get additionalFilterClauses() {
    const {stores, catalogConfig: {additionalFilterClauses}} = this
    if (!additionalFilterClauses) return []

    return additionalFilterClauses.map(f => {
      if (_.isFunction(f)) {
        return f(stores)
      }

      return f
    }).filter(f => !_.isEmpty(f))
  }

  @observable seasonSelectorsModal: { [field: 'hierarchy' | string]: SeasonSelectorFilterDropdownStore | SeasonSelectorFilterDropdownModalStore | SeasonSelectorHierarchyDropdownStore } = {}
  @observable seasonSelectors: { [field: 'hierarchy' | string]: SeasonSelectorFilterDropdownStore | SeasonSelectorFilterDropdownModalStore | SeasonSelectorHierarchyDropdownStore } = {}
  get seasonSelectorsCatalog() { return _.pickBy(this.seasonSelectors, ss => ss instanceof SeasonSelectorFilterDropdownStore) as Record<string, SeasonSelectorFilterDropdownStore>}

  @computed get focusedCatalogItemId() { return this.settings.focusedCatalogItemId}

  set focusedCatalogItemId(v) {
    this.settings.focusedCatalogItemId = v
  }

  @computed get isCatalogStores() {
    return this.stores.purpose == null || this.stores.purpose === 'catalog'
  }

// get focusedCatalogItemId() { return this.settings.focusedCatalogItemId }
  //
  // set focusedCatalogItemId(v) { this.settings.focusedCatalogItemId = v }

  private _initialize = () => {
    const {stores: {purpose}, isCatalogStores} = this
    if (!this.isOnlyForWorkspaceSanitize) {
      this.settingsStore = new CatalogSettingsStore(this)
      this.groupBy = new GroupByStore(this)
      this.es = new CatalogElasticSearchStore(this)
      this.relationships = new CatalogRelationshipsStore(this)
      this.globalC8Hierarchy = new C8HierarchyStore(this, null)
      this.dialogs = new CatalogDialogsStore(this)
      this.footer = new CatalogFooterStore(this)
      this.cv = new CatalogCardViewStore(this)
      this.img = new CatalogImageService(this)
      this.pdfCatalogStore = new CatalogPDFStore(this)

      if (!this.isGlobalStore) {
        if (!this.stores.purpose) {
          this.searcher = new CatalogSearcherStore(this)
          this.assortments = new CatalogAssortmentsStore(this)
        }

        this.mutations = new CatalogMutationsStore(this)
        this.createItem = new CatalogCreateItemStore(this)
        this.notes = new CatalogNotesStore()
        this.selection = new CatalogSelectionStore(this)

        this.seasonSelector = new SeasonSelectorStore(this)
        this.aggregation = new CatalogAggregationStore(this)
        if (isCatalogStores) {
          this.presentation = new CatalogPresentationStore(this)
        }
      }
    }

    if (isCatalogStores) {
      this.relationships = new CatalogRelationshipsStore(this)
    }

    const {_toDispose, isGlobalStore, debugId, componentStores, sidebar, _workspace, stores, isCurrentWorkspace, groupBy, dragDrop} = this

    if (this._isFirstLoad && LOG_GLOBAL_CATALOG_LOAD) {
      console.groupCollapsed(`${debugId} - [initializing]`, {isCurrentWorkspace, _workspace, stores})
      // console.trace()
    }

    if (!isCatalogStores || (this._workspace && this.stores === globalStores) || (this._workspace && !this._workspace.config?.catalog)) {
      LOG_GLOBAL_CATALOG_LOAD && console.log(`${this.debugId} - Skipping autoruns/reactions in catalog as this was created specifically for parsing a Workspace class or inline-details`)
      this.initialized = true
      this._isFirstLoad = false
      this.loadingCounts = false
      console.groupEnd()

      return
    }

    if (_workspace) {
      this.cv.resetCardViewToDefault()
      this.loadingCounts = false
    }

    _toDispose.push(
      this.selectFocusedCatalogItemInRightDetailPaneWhenMaximized(),

      reaction(() => this._isFirstLoad && !this.loading, (done) => {
        if (done) {
          // console.debug(`Done loading ${this.debugStoreIdPrefix}`)
          isGlobalStore && console.groupEnd()
          this._isFirstLoad = false
        }
      }),

      reaction(() => this.es.catalogItemFilter, () => this.newlyCreatedIds.clear(), {name: 'Clear newlyCreatedIds '})
    )
    // Why aren't we just creating a computed that combines the attributes with the card view instead of modifying our observable whenever we load attributes?

    const {currentWorkspaceChanged} = this

    !isGlobalStore && _toDispose.push(
      autorun(() => {
        const {settings, stores: {es}} = this
        if (settings?.focusToHierarchy && this.hasLoadedConfig) {
          this.tryFocusToHierarchy()
        }
      }))

    !isGlobalStore && _toDispose.push(
      observe(this, 'workspaceConfig', ({oldValue}) => {
        this._priorWorkspace = oldValue as WorkspaceConfig
      }))

    //!isGlobalStore && _toDispose.push(reaction(() => this.workspaceConfig, currentWorkspaceChanged, {name: 'Reset card view data when board changes'}))

    _toDispose.push(
      reaction(() => authStore.userIsAuthenticated, (auth) => !auth && this.clearOnLogout(), {name: 'Reset card view data when board changes'}))

    // This will cause the catalog panel to blank between filter changes
    // reaction(() => this.es.catalogItemFilter?.filter, () => {
    //   this.itemWrappers.clear()
    // })

    _toDispose.push(
      reaction(() => ({modifiedItems: this.catalogItemsWithUnsavedChangesMap, showModified: this.settings.showOnlyItemsWithChanges}), ({modifiedItems, showModified}) => {
        if (_.isEmpty(modifiedItems) && showModified) this.toggleShowOnlyItemsWithChanges()
      }, {name: `Turn off showOnlyModifiedItems when there are none`}))

    if (!isGlobalStore) {
      _toDispose.push(
        autorun(this.fetchWorkspaceCounts, {name: 'Load workspace counts'}),
        autorun(() => {
          const {esProductSortBy, catalogItemFilter, groupBy: {catalogItemsGroup}} = this
          if (catalogItemsGroup) {
            const r = {
              ...(catalogItemsGroup?.functionScoreTerm
                  ? {
                  query: {...catalogItemsGroup?.functionScoreTerm},
                  ...esProductSortBy
                }
                  : {
                  ...catalogItemFilter.filter,
                  ...esProductSortBy
                })
            }

            runInAction(() => {
              if (!_.isEqual(r, this.queryVariablesEs)) {
                this.queryVariablesEs = r
              }
            })
          } else {
            runInAction(() => {
              if (!_.isEmpty(this.queryVariablesEs)) {
                this.queryVariablesEs = {}
              }
            })
          }
        }))
    } else {
      this._hasLoadedCounts = true
    }

    // autorun(() => {
    //   const {workspaceConfig} = this
    //   if (workspaceConfig?.catalog) {
    //     const {_id, catalog: {perspectives}} = workspaceConfig
    //
    //     if (!_.isEmpty(perspectives) && !perspectives.some(p => p.id == _id)) {
    //       console.warn('There are NO perspectives defined for this board which match the board ID, this is a bug in your config(!) which will prevent counts from being displayed')
    //     }
    //   }
    // }, {name: `Warn when main perspective is missing for catalog`})

    isGlobalStore && _toDispose.push(
      autorun(async () => {
        if (deployments.deploymentConfig) {
          CatalogStore.offlineItemsLoaded = false
          CatalogStore._offlineItemsDb = new OfflineCatalogItemDatabase()

          await runInAction(async () => {
            await Promise.all([this._loadOfflineItemsFromDb()])
          })
          this._isFirstLoad = false
        }
      }, {name: `Initialize Pending Items db in catalog`}))

    !isGlobalStore && _toDispose.push(
      reaction(() => groupBy.hierarchy, () => {
        this.selectAll = false
      }, {name: `Turn off select all when hierarchy changes`}))

    _toDispose.push(
      autorun(() => {
        if (CatalogStore.offlineItemsLoaded && this.offlineItemCount === 0) {
          this.settings.showOnlyOfflineItems = false
        }
      }, {name: `Turn off showOnlyOfflineItems when offline count is 0`}))

    !isGlobalStore && isCatalogStores && _toDispose.push(
      autorun(() => {
        const {sectionById, es: {/*catalogItemFilter, refetchId*/}, workspaceConfig} = this
        this._getDynamicSectionValues()
      }, {name: 'Load dynamic section values (secondary names for example)'}),

    !isGlobalStore && isCatalogStores && autorun(() => {
                     if (this.hoverPopoverItemId && !_.isEmpty(dragDrop.dragItem)) {
                       this.hoverPopoverItemId = null
                     }
                   }, {name: 'Close inline card popover when dragging and dropping'})
    )

    // !isGlobalStore && _toDispose.push(
    //   reaction(() => {
    //     const {vlp, filter: {seasonSelectorFilters}, catalog: {dialogs}} = this.stores
    //     trace()
    //     return [...seasonSelectorFilters.map(v => v.selected.size), toJS(vlp.hierarchy.selected)]
    //   }, () => {
    //     runInAction(() => {
    //       if (!this.showPdf && this.onlineItemCount > 0 && !this.vlp?.isRefreshingSeasons && (this.workspaceConfig?.seasonIndex || this.workspaceConfig?.seasonSelectors?.length > 0)) {
    //         this.loadingState.loading = true
    //       }
    //     })
    //   }, {name: `When season selectors change, mark catalog as loading`}))

    // autorun(async () => {
    //   const {catalogConfig} = this, {indexes, unwrapHitsResult} = es
    //   this._hasLoadedCAs = false
    //   if (catalogConfig?.index) {
    //     const {items} = unwrapHitsResult<TagType>(await es.search({index: indexes.customAttributeDef, body: { query: { term: { esIndex: {value: catalogConfig.index }}}, sort: [{ index: {order: 'asc'}}]}, size: 10000}))
    //     this.caDefs = items
    //     this._hasLoadedCAs = true
    //   }
    // })

    !isGlobalStore && console.groupEnd()

    this.initialized = true
  }

  @observable initialized = false
  @observable loadingCounts = true

  /**
   * _tableRefreshDataByIds: get the ids of data need to refresh after updated from DnD or sidebar update
   */
  @observable _tableRefreshDataByIds = []

  /**
   * In product view, this is a dict of virtual row -> loaded data items
   */
  itemWrappers = observable.map<string, Array<any>>({}, {deep: false})

  private clearOnLogout = () => {
    console.debug(`Logging out of catalog ${this.stores._id}`)
    this.settings.searcherSearchText = ''
    this._workspaceCounts = {}
    this._priorWorkspace = null
    this.loadingCounts = true
    this._isFirstLoad = true
    this._hasLoadedCounts = false
  }

  get hasAggregations() { return this.aggregation?.hasAggregations === true}

  @computed get isBreadcrumbSourceC8() {
    if (!this.workspaceConfig) return false
    const {workspaceConfig: {breadcrumbSource}} = this
    return breadcrumbSource === 'c8' || false
  }

  @computed get customHierarchy(): Map<string, ICustomVlpHierarchy> {
    if (!this.workspaceConfig) return new Map([])
    const {workspaceConfig: {vlp}} = this
    return new Map(vlp?.customHierarchy?.map(f => [f.defaultName, f]) ?? [])
  }

  @computed get catalogItems(): Array<CatalogItemRecord> {
    const {isProductView, workspace_offlineItems, groupBy: {lastGroupInHierarchy}} = this
    const onlineCatalogItems = isProductView ? _.flatMap(Array.from(this.itemWrappers.values()))
                                             : _.flatMap(lastGroupInHierarchy?.groupsInOrder?.map(g => g?.productStore?.catalogItemsArray) ?? [])

    return [...onlineCatalogItems, ...workspace_offlineItems].filter(_.identity)
  }

  @computed get catalogItemById(): { [id: string]: CatalogItemRecord } {
    const {catalogItems} = this
    return _.keyBy(catalogItems, v => this.idFor(v))
  }

  @computed get showUnsavedChangesButton() {
    const {catalogItemsWithUnsavedChanges, settings: {autoSaveInlineChanges}} = this
    return !(autoSaveInlineChanges || catalogItemsWithUnsavedChanges.length == 0 || ui.settings.showUnsavedPerspectiveMenu === false)
  }

  @computed get catalogItemsWithUnsavedChanges() {
    const {catalogItemById, settings: {perItem}} = this
    return _.keys(perItem).filter(id => perItem[id]?.unsavedChanges).map(this.parseIdFromPath).map(id => catalogItemById[id] ?? {id, exId: id, _id: id}).filter(_.identity)
  }

  @computed get catalogItemsWithUnsavedChangesMap() {
    const {idFor, catalogItemsWithUnsavedChanges} = this
    return _.keyBy(catalogItemsWithUnsavedChanges, item => idFor(item))
  }

  private itemName: string

  @computed get catalogItemName_ItemCountSensitive() {
    return pluralize(this.catalogItemName, this.itemCount)
  }

  @computed get catalogItemName() {
    if (!this.workspaceConfig && !this.itemName) return this.strings.emptyLabel
    if (!this.workspaceConfig && this.itemName) return this.itemName

    const {workspaceConfig: {itemName}, perspective, isShowingPerspective} = this
    this.itemName = !isShowingPerspective ? itemName : (perspective?.label ?? perspective?.id ?? itemName)
    return this.itemName
  }

  @computed get detailViewsForWorkspace(): DetailsViewsByIndex {
    const {deploymentConfig, workspaceConfig} = this.workspaces
    return {
      ...(deploymentConfig?.detailViews ?? {}),
      ...(workspaceConfig?.detailViews ?? {})
    }
  }

  detailViewFor = (key: string): DetailsViewConfig => {
    return this.detailViewsForWorkspace[key] ?? {}
  }

  get showGroupHeader() { return this?.catalogConfig?.features?.groupBy?.showGroupLabel}

  @computed get catalogItemFilter() { return _.cloneDeep(this.es.catalogItemFilter) ?? []}

  get catalogItemLibrary() {return this.es.catalogItemLibrary ?? []}

  @computed get libraries(): Array<LibraryItemConfigWrapper> {
    const r = this.relationships?.librariesOfWorkspace(this.workspaceConfig) ?? []
    return r
  }

  get workspaceByIndex(): { [esIndex: string]: WorkspaceConfig } { return this.workspaces.byIndexScalar}

  @computed get librariesWithWorkspaces(): Array<LibraryItemConfigWrapper> {
    return this.relationships.librariesWithWorkspacesFor(this.workspaceConfig)
  }

  @computed get fieldByIndex() : Record<WorkspaceElasticSearchIndex, Array<ICatalogFieldConfig>> { return _.groupBy(this.catalogConfig?.fields.filter(f => !(f.refField || f.library?.hide)), f => f.esIndex) }

  get libraryById(): { [id: string]: LibraryItemConfigWrapper } { return _.keyBy<LibraryItemConfigWrapper>(this.libraries, l => l.id) ?? {}}

  @computed get renderEmptyGroups() {
    /*const {groupBy: {hierarchy}} = this
    return hierarchy?.reduce((acc, h) => {
      if (!acc) acc = this._fieldByName[h]?.def?.type === 'Enum'
      return acc
    }, false)*/
    return !this.groupBy.isProductView
  }

  dataMapForLibrary = (library: LibraryItemConfig, useCardUi = true) => {
    return this._dataMapForLibrary(library, useCardUi)
  }

  private _dataMapForLibrary = computedFn((library: LibraryItemConfig, useCardUi = true) => {
    const {dataMapFor, dataMapForWorkspace_index} = this
    if (!useCardUi && library?.dataMap) {
      return dataMapFor(library?.dataMap)
    }
    if (library?.cardUI?.main?.dataMap) {
      return {
        ...dataMapperConfig.specDataMapperConfig, ...(library?.cardUI?.main?.dataMap || {})
      }
    } else {
      return library?.dataMap ? dataMapFor(library?.dataMap) : dataMapForWorkspace_index(library?.index)
    }
  }, {keepAlive: true})

  dataMapForWorkspace_index = (index: string) => {
    const {dataMapForWorkspace, workspaceByIndex} = this
    return dataMapForWorkspace(index ? workspaceByIndex[index] : null)
  }

  dataMapForWorkspace = (workspace: WorkspaceConfig) => {
    return this.dataMapFor(workspace?.dataMap)
  }

  dataMapFor = (dataMap?: IDataMapConfig) => {
    return {
      ...dataMapperConfig.specDataMapperConfig, ...(dataMap || {})
    }
  }

  catalogFieldsForLibrary = computedFn((libraryName: string) => {
    const {_fieldByName} = this,
      library = this.libraryById[libraryName]

    return library?.index ? _.values(_fieldByName).filter(f => f.esIndex === library?.index) : []
  })
  /**
   * The fields of a given library used across all detail views in the current catalog
   */
  fieldsForLibrary = computedFn((libraryName: string) => {
    const detailsView = this.detailViewFor(libraryName)
    return _.uniq(
      detailsView ? [
        ..._.flatMap(detailsView?.sidebar?.sections?.map(s => s.fields)),
        ..._.flatMap(_.values(detailsView.modal)),
        ..._.flatMap(detailsView.modal?.tabs?.map(t => [...(t?.fields ?? []), ...(t?.sections ?? []).map(s => s.fields)]))
      ] : [])
  })

  @computed get imageEditableLibraries() {
    const {getLibraryFieldDef, libraryByIndex} = this
    const libraries = _.values(libraryByIndex).filter(l => getLibraryFieldDef('images', l.index)) // todo need to verify editable for user
    return libraries
  }

  isLibrarySelected = (id: string) => {
    const {left: {open, tab, selectedPathItem}} = this.sidebar
    return open && tab == 0 && selectedPathItem?.libraryId == id
  }

  @action getLibraryFieldDef = (field, index) => {
    const {workspaceByIndex, fieldForWorkspace} = this
    if (!index) return null
    const ws = workspaceByIndex[index],
      def = fieldForWorkspace(field, ws)
    return def ?? null
  }

  @action getTagLibraryClause = () => {
    const {catalogConfig: {tagType}} = this
    return tagType && {'term': {type: {value: tagType}}} || null
  }

  @action navigateToLibrary = (id: string, open = false) => {
    const {flexLayout: {isMaximized, toggleKnownTab, model}, sidebar} = this
    sidebar.left.navigateToLibrary({libraryId: id}, open)
    toggleKnownTab('libraries', open)
  }

  /**
   * User clicked a catalog item from the searcher store
   * @param libraryId
   * @param _id
   * @param librarySidebar
   */
  @action navigateToLibraryItem = (libraryId: string, _id: string, librarySidebar?: AbstractSidebarStore) => {
    (librarySidebar ?? this.sidebar.left).navigateToLibrary({libraryId, where: [{value: _id}]}, true)
  }

  @action navigateToFilter = () => {
    const {flexLayout: {isMaximized, toggleKnownTab}} = this
    if (!isMaximized) {
      toggleKnownTab('filters', true)
    }
  }

  @computed get dragLibraries() {
    const {libraries, index} = this
    return _.uniq(libraries.filter(l => l.index != index && l.assignTo))
  }

  @computed get dragLibraryByIndex(): { [esIndex: string]: LibraryItemConfig } {
    return _.keyBy(this.dragLibraries, l => l.index)
  }

  @computed get dragLibraryByName(): { [name: string]: LibraryItemConfig } {
    return _.keyBy(this.dragLibraries, l => l.name)
  }

  @computed get index() { return this.catalogConfig?.index ?? this.workspaces.workspaceConfig?.library?.index}

  @computed get isGroupedView() { return this.groupBy.isGroupedView}

  @computed get isProductView() { return this.groupBy.isProductView}

  @computed get detailAggregationFields() {
    const {features} = this
    return _.flatMap((features?.groupBy?.detailAggregations ?? []).map(agg => _.flatMap(agg.sections.map(s => s.fields.filter(f => !f.hidden)))))
  }

  /// region c8 Hierarchy

  globalC8Hierarchy : C8HierarchyStore
  get c8hierarchy() { return (this.seasonSelectorsModal[`hierarchy_0`] as SeasonSelectorHierarchyDropdownStore)?.c8hierarchy || this.globalC8Hierarchy}

  @computed get showHierarchyDropDown() {
    const {catalogConfig: {itemCreation}} = this
    return itemCreation && itemCreation.hierarchy !== false
  }

  @computed get seasonSelectorFieldsById() {
    return _.keyBy(this.seasonSelectorFields, f => f.field)
  }

  @computed get itemCreation() {
    const {perspective, catalogConfig} = this
    return (perspective && perspective?.itemCreation)
           ? perspective?.itemCreation
           : catalogConfig?.itemCreation
  }

  itemCreationWithDependsFor = (itemCreation): string[] => {
    return (itemCreation?.depends) ?
           _.compact([...itemCreation?.required, ...itemCreation?.depends]) :
           itemCreation?.required
  }

  @computed get itemCreationWithDepends(): string[] {
    const {vlp, perspective, catalogConfig} = this,
      {itemCreationWithDependsFor} = this

    return (perspective && perspective?.itemCreation) ?
           itemCreationWithDependsFor(perspective?.itemCreation) :
           vlp?.hierarchy?.hasVlpHierarchy ? vlp.hierarchyPath :
           itemCreationWithDependsFor(catalogConfig?.itemCreation)
  }

  @computed get nonHierarchyItems(): SeasonSelectorFieldConfig[] {
    const {itemCreationWithDepends, _fieldByName} = this
    return _.chain(itemCreationWithDepends)
      .map(f => _fieldByName[f])
      .filter(f => (f?.hasSelection && _.isEmpty(f.hierarchyElements)))

      .value()
  }

  @computed get hasHierarchyItem(): boolean {
    const {itemCreationWithDepends, _fieldByName} = this

    return !_.chain(itemCreationWithDepends)
      .map(f => _fieldByName[f])
      .filter(f => (f?.hasSelection && !_.isEmpty(f.hierarchyElements)))
      .isEmpty()
      .value()
  }

  @computed get c8CatalogFields(): ICatalogFieldConfig[] {
    const {allWorkspaceFields} = this
    let r = _.filter(_.values(allWorkspaceFields) || [], (f: ICatalogFieldConfig) => f.c8?.serviceName) as ICatalogFieldConfig[]
    r.push(
      {
        field: 'season',
        dependants: ['category1', 'category2', 'collection'],
        hierarchyElements: ["category1s", "category2s", "collections"],
        c8: {
          serviceName: "getSeasonC8"
        }
      }
    )

    return _.uniqBy(r, v => v.field)
  }

  @computed get hierarchyTopFieldDetails(): ICatalogFieldConfig {
    const {itemCreationWithDepends, _fieldByName} = this

    const r = _.chain(itemCreationWithDepends)
      .map(f => _fieldByName[f])
      .compact()
      .filter(i => !i.depends)
      .head()
      .value()

    return r
  }

  /// endregion
  _seasonSelectorFields = memoize((seasonSelectors) => {
    return _.uniq(!seasonSelectors
                  ? []
                  : seasonSelectors.reduce(
        (acc, {type, fields}) =>
          acc.concat((fields ?? []).length
                     ? fields
                     : type === 'merch'
                       ? [{field: 'collection', align: 'left'}, {field: 'plan', align: 'left'}]
                       : type === 'seasonal'
                         ? [{field: 'season', align: 'right'}, {field: 'category1', align: 'right'}, {field: 'category2', align: 'right'}]
                         : []), [])
    ).filter(_.identity)
  })

  @computed(jsonComputedOptions) get filterTermsByPerspective() {
    const {workspaceByIndex, _seasonSelectorFields, catalogItemFilter, pdf, filter: {state: {groups}}, vlp} = this

    return _.keys(workspaceByIndex).reduce((acc, index) => {
      const workspace = workspaceByIndex[index],
        {seasonSelectors} = workspace,
        {_fieldByName} = this
      return _.assign(acc, {
        [index]: [
          ...(index === this.index ? (catalogItemFilter?.filter?.query?.bool?.filter ?? []) : []),
          ...(index === this.index ? [] : _seasonSelectorFields(seasonSelectors).filter(({field, lookup}) => (pdf.isPlanHierarchy && (groups[field]?.selected.size || groups[lookup]?.selected.size))).map(
            ({field, lookup}) => ({
              terms: {
                [`${field}.name` + (_fieldByName[field]?.keyword ? '.keyword' : '')]: groups[field]?.selected.size
                                                                                      ? Array.from(groups[field]?.selected.keys()).filter(_.identity)
                                                                                      : Array.from(groups[lookup]?.selected.keys()).filter(_.identity)
              }
            }))),
          ..._seasonSelectorFields(seasonSelectors).filter(({field, lookup}) => field === 'plan').map(
            () => _.isArray(vlp.hierarchy.plans.plans) ? {terms: {'plan.id': vlp.hierarchy.plans.plans.map(p => p.id)}} : null)
        ].filter(_.identity)
      })
    }, {})
  }

  debounceFetchWorkspaceCounts = _.debounce(() => {
    this.fetchWorkspaceCounts()
  }, 100, {leading: false, trailing: true})

  @observable.ref workspaceCountsCancelToken: CancelTokenSource

  selectedViewOptionsForDynamicSection = computedFn((section: string): Set<string> => {
    const {cardView, sectionValuesFor, sectionConfigFor} = this,
      sectionConfig = sectionConfigFor(section)

    if (sectionConfig?.viewOptions?.field) {
      const {path, viewOptions: {field}} = sectionConfig,
        fullPath = `${path}.${field}`,
        sectionValues = sectionValuesFor(section)?.map(({key}) => key) ?? [],
        selectedViewOptions = new Set(sectionValues.filter(key => cardView && cardView[`${fullPath}.${key}`]?.show) ?? [])

      if (!selectedViewOptions.size) {
        return new Set(sectionValues)
      }
      return selectedViewOptions
    }

    return new Set([])
  })

  sectionPathFor = computedFn((sectionConfig: ISectionConfig) => {
    return `${sectionConfig.path}.${sectionConfig.viewOptions.field}`
  })

  @computed
  private get _perspectiveCountAggs() {
    const {sectionPathFor, catalogConfig, sectionConfigFor, selectedViewOptionsForDynamicSection} = this

    return (catalogConfig.perspectives ?? []).reduce((acc, p) => {
      const index = p.index ?? this.index

      if (p.field) {
        const id = p.id ?? p.field,
          sectionConfig = sectionConfigFor(id),
          field = sectionConfig?.viewOptions?.field ? sectionPathFor(sectionConfig) : `${p.field}.${p.idField ?? 'id'}`,
          selectedViewOptions = selectedViewOptionsForDynamicSection(sectionConfig?.id)

        let innerAggs: any = {
          [id]: {
            value_count: {field}
          },
          [`${id}.unique`]: {
            cardinality: {field}
          },
          [`${id}.unassigned`]: {
            filter: {
              bool: {
                must_not: [
                  {exists: {field}}
                ]
              }
            }
          }
        }

        if (sectionConfig?.path) {
          innerAggs = {
            [id]: {
              nested: {
                path: sectionConfig.path
              },
              aggs: _.isEmpty(selectedViewOptions) ? innerAggs : {
                filtered: {
                  filter: {
                    terms: {
                      [sectionPathFor(sectionConfig)]: Array.from(selectedViewOptions.keys())
                    }
                  },
                  aggs: innerAggs
                }
              }
            }
          }
        }

        return _.assign(acc, {
          [index]: {
            ...(acc[index] ?? {}),
            ...innerAggs
          }
        })
      } else if (p.filter) {
        return _.merge(acc, {
          [index]: {
            [p.id]: {
              filter: p.filter
            }
          }
        })
      }

      return acc
    }, {})
  }

  @observable private _hasLoadedCounts = false

  get flexLayoutCatalogRoot() { return this.stores?.flexLayoutCatalogRoot}

  @computed get isCatalogRootTab() {
    return this.stores.flexLayoutCatalogRoot != null
  }

  @computed get flexTab(): TabNode {
    const {stores: {flexLayoutCatalogRoot, catalogTab}} = this
    return flexLayoutCatalogRoot?.tab ?? catalogTab?.tab
  }

  /**
   * Subtly different than checking if the filters have actually run a query.  If we have no filter terms, we need not wait on the query before getting our counts
   */
  @computed get haveFiltersLoaded() {
    const {filter} = this
    return !filter.hasFilterTerms || filter.hasLoaded
  }

  @computed.struct get workspaceCountsQueryPayload() {
    const {queryVariablesEs, _perspectiveCountAggs, settings: {activeSearchText}} = this
    return removeUndefinedsNested({
      size: 0,
      runtime_mappings: this.queryVariablesEs?.runtime_mappings,
      query: /*es.filterForNonDeletedItems ?? */ {bool: {}},
      aggs: _.keys(this.workspaceByIndex).reduce((acc, index) => _.assign(acc, {
        [index]: {
          filter: {
            bool: {
              must: _.uniqWith(!_.isEmpty(activeSearchText) && index === this.index ? [
                {term: {_index: index}},
                {...queryVariablesEs.query?.function_score ? queryVariablesEs.query?.function_score.query?.bool?.must : queryVariablesEs.query?.bool?.must},
                ...this.filterTermsByPerspective[index]
              ].filter(v => !_.isEmpty(v)) : [
                {term: {_index: index}},
                ...this.filterTermsByPerspective[index]
              ], _.isEqual)
            }
          },
          aggs: {
            ...(_perspectiveCountAggs[index ?? this.index] ?? {})
          }
        }
      }), {})
    })
  }

  fetchWorkspaceCounts = () => {
    const {hasLoadedConfig, stores, debugId, flexTab, workspaceCountsQueryPayload} = this

    if (stores.isInlineDetailsStore || stores.isFilterLibrariesStore || stores.isLibraryItemsList) {
      this.loadingCounts = false
      return
    }

    if (!this.hasLoadedConfig || (flexTab && !flexTab.isVisible)) {
      LOG_GLOBAL_CATALOG_LOAD && console.debug(`Not loading catalog counts due to never being visible and tabbed`)
      return
    }

    // trace()

    if (!this.haveFiltersLoaded || !authStore.userIsAuthenticated || !stores.es.axiosInstance || !this.groupBy.initialized || !this.groupBy.catalogItemsGroup || !this.hasLoadedSeasons || (this.vlp?.hierarchy?.hasVlpHierarchy && !this.vlp.hierarchy.hasLoadedHierarchy) /*|| !this.c8hierarchy.hasLoadedHierarchy*/) {
      //console.log({hasLoadedCAs, filterParsed: filter.parsedUrl, filterHasLoaded: filter.hasLoaded, workspaceConfig, userIsAuthenticated: authStore.userIsAuthenticated, axiosInstance: stores.es.axiosInstance, hasLoadedHierarchy: this.c8hierarchy.hasLoadedHierarchy })
      LOG_GLOBAL_CATALOG_LOAD && !this._hasLoadedCounts && console.debug(
        `${debugId} - [counts] Waiting on:`,
        !hasLoadedConfig
        ? 'No config'
        : !stores.es.axiosInstance ? 'No Axios'
                                                                          : !this.groupBy.catalogItemsGroup
                                                                            ? 'catalog item group' : 'Unknown')
      return
    }

    const {stores: {es}} = this

    // trace()

    runInAction(`Fetch workspace counts`, async () => {
      try {
        LOG_GLOBAL_CATALOG_LOAD && console.debug(`${debugId} - [counts] - Fetching workspace ${this.workspaceConfig._id} counts...`)
        if (this.workspaceCountsCancelToken) {
          LOG_GLOBAL_CATALOG_LOAD && console.debug(`${debugId} - Cancelling and refetching workspace counts for ${this.workspaceConfig._id}... (inefficient!)`)
          this.workspaceCountsCancelToken.cancel(`${debugId} - fetchWorkspaceCounts autorun triggered`)
        } else {
          LOG_GLOBAL_CATALOG_LOAD && console.debug(`${debugId} - Fetching workspace counts for ${this.workspaceConfig._id}...`)
        }

        this.loadingCounts = true
        this.workspaceCountsCancelToken = axios.CancelToken.source()

        //trace()
        // console.log(`Loading workspace counts...`)
        const {aggregations} = await es.search({
          meta: `${debugId} - catalog.fetchWorkspaceCounts()`,
          size: 0,
          cancelToken: this.workspaceCountsCancelToken.token,
          index: null,
          body: workspaceCountsQueryPayload,
        }, {log: {query: false}})

        runInAction(`Loaded counts for workspaces`, () => {
          this._workspaceCounts = aggregations
          //console.log(toJS(workspaceCounts))
          this.workspaceCountsCancelToken = null
          this.loadingCounts = false
          this.switchingWorkspaces = false
          this._hasLoadedCounts = true

          if (!this.onlineItemCount) {
            this.loadingState.loading = false
          }
        })
      } catch (err) {
        if (!axios.isCancel(err)) {
          onSiteError(err)

          runInAction(() => {
            this.loadingCounts = false
            this.switchingWorkspaces = false
            this.workspaceCountsCancelToken = null

            if (!this.onlineItemCount) {
              this.loadingState.loading = false
            }
          })
        }
      }
    })
  }

  private _workspaceConfiguredReactions: Array<IReactionDisposer> = []
  @observable switchingWorkspaces = false
  private _priorWorkspace: WorkspaceConfig = null

  @action switchToWorkspace = (workspace: Partial<WorkspaceConfig>) => {
    this.switchingWorkspaces = true

    this.stores.presets.selectedPresetId = undefined

    setTimeout(() => {
      nav.siteHeader_navigateToWorkspace(workspace)
      this.switchingWorkspaces = false
    }, 100)
  }

  @action currentWorkspaceChanged = async () => {
    if (this._workspace && this._workspace !== this.workspaces.workspace) return

    const {sidebar, groupBy, _priorWorkspace, catalogConfig, workspaceConfig, _workspaceConfiguredReactions, searcher, pdf, vlp, stores, stores: {filter}} = this

    _workspaceConfiguredReactions.forEach(d => d())

    if (_priorWorkspace) {
      this.switchingWorkspaces = true
    }

    await runInAction(`Reset parts of catalog when workspace changes`, async () => {
      //if (this.index) { workspaces.currentBoard = this.index }
      filter.storeByLibraryId.clear()

      /** If we switched boards (not just loaded our initial config) then reset the filters and card view to defaults **/
      if (_priorWorkspace && _priorWorkspace?._id != this.workspace?._id) {
        console.log(`${this.debugId} - Switch workspace from ${_priorWorkspace.title} to ${this.workspace?.config?.title}`)
        const {settings} = this
        pdf && pdf._resetPDF()
        this.isShowingClientPDF && this.toggleShowPdf()
        this._hasLoadedCounts = false
        this.sectionValues.clear()
        this.loadingState = {}
        // filter.reset()  Filter store will handle this itself
        this.cv.resetCardViewToDefault()
        this.itemWrappers.clear()
        settings.levelOfDetail = 0
        groupBy.hierarchy = []
        settings.showAggs = false
        settings.showAggDetailsWhenCollapsed = false
        settings.showOnlyOfflineItems = false
        settings.showOnlyInlineCatalogItems = false
        settings.showInlineCatalogItemsFirst = false
        settings.perspective = undefined

        if (workspaceConfig?.catalog?.features?.imageSources?.default) {
          const {default: defaultImageSource} = workspaceConfig.catalog.features.imageSources

          if (!_.isEmpty(defaultImageSource.ids)) { settings.imageSource.ids = defaultImageSource.ids.slice()}
          if (defaultImageSource.view) { settings.imageSource.view = defaultImageSource.view}
        }

        vlp?.reset()
        searcher?.reset()
      }

      this._workspaceConfiguredReactions = workspaceConfig?.reactions ? workspaceConfig.reactions(stores) : []

      if (_priorWorkspace || this.justLoggedIn) {
        if (catalogConfig?.defaultView) {
          const {defaultView: {filterConfig, ...defaultView}} = catalogConfig

          _.merge(this, defaultView)

          filterConfig && _.forEach(filterConfig.by, (f: ToggleFilterParams) => filter.toggleFilterBatched(f))
        } else {

        }
      }

      this.justLoggedIn = false
    })
  }

  @observable justLoggedIn = false

  getSectionFields = (data: any): Array<ICatalogFieldConfig> => {
    const {deploymentConfig: {detailViews}} = deployments,
      {index, _fieldByName} = this,
      modal = detailViews[index]?.modal,
      {settings: {detailLoc}} = this
    if (detailLoc == 'modal') {
      return modal?.tabs?.reduce((acc, {sections}) => {
        sections?.forEach(({fields: fieldsRaw}) => {
          const fields = _.isArray(fieldsRaw) ? fieldsRaw : _.isFunction(fieldsRaw) ? fieldsRaw(data, this.stores) : []

          acc.push(...fields.map(f => _fieldByName[f.field] ?? f))
        })

        return acc
      }, []) ?? []
    }
    return []
  }

  @action validateCatalogItem: (data) => IValidCatalogItem = (data) => {
    const {catalogConfig: {itemCreation}, getSectionFields, _fieldByName, sidebar: {right: {details}}} = this,
      strValidation = (d) => _.isString(d) ? !_.isEmpty(_.trim(d)) : d
    return removeUndefinedsNested({
      ...(itemCreation?.required ?? []).reduce((acc, require) => _.assign(acc, {
        [require]: (strValidation(_.get(data, require)) || !!details?.itemDetailsStore?.mutationData?.changes?.[require]) ? undefined : false
      }), {}),
      ...(getSectionFields(data) ?? []).reduce((total, {field}) => {
          const catalogField = _fieldByName[field]
          return _.assign(total, {
            [field]: catalogField?.editor?.require ? strValidation(_.get(data, field)) ? undefined : false : undefined
          })
        }
        , {})
    }) as IValidCatalogItem
  }

  _base64Image: (image) => boolean = (image) => {
    const i = image?.image?.image?.image ?? image?.image?.image

    if (!i) return null

    const ext = i.includes('data:image') ? i.substring('data:image/'.length, i.indexOf(';base64')) : i.substring('data:text/'.length, i.indexOf(';base64'))

    return ui.constants.VALID_IMAGE_EXTENSION.includes(ext)
  }

  imageUrlForDataMap = ({data, dataMap}: { data: any, dataMap: IDataMapConfig }) => {
    if (!data) return null

    const images = _.get(data, dataMap?.images ?? 'images')
    return this.imageUrlFor(_.first(images ?? []))
  }

  forceC8RenderImageIds = observable.set<string>();

  imageUrlFor: (image, params?: { c8Size?: C8ImageSize, imageSize?: { width, height }, source?: 'c8' | 'cloudfront', isBackgroundImage?: boolean, autoTransparency?: boolean, imageType?: 'jpg' | 'png' | 'webp' | 'gif' | 'jp2' }) => string = (image, params) => {
    if (!image) return null

    if (_.isArray(image)) {
      image = _.first(image)
    }
    // if (image?.exId && deployments.deploymentConfig.imageSource != 'graphql') {

    if (image?.rgba) {
      const {rgba: {r, g, b, a}} = image
      return `rgba(${r},${g},${b},${a ?? 1})`
    }

    let {imageSize, source, imageType, isBackgroundImage} = params ?? {imageSize: null, source: aws.useCloudFront ? 'cf' : 'c8', autoTransparency: false, imageType: 'png', c8Size: 'smallImage', isBackgroundImage: false},
      autoTransparency = params?.autoTransparency ?? image?.autoTransparency ?? false,
      id = _.isString(image) ? image : (image?.exId ?? image?.id),
      {stores: {config, authStore}, features: {alwaysShowFullSizeImages}, forceC8RenderImageIds} = this,
      c8Size = params?.c8Size ?? 'smallImage',
      c8ImageType = centricImageTypeMap[c8Size]

    if (deployments.deploymentConfig?.settings?.images?.alwaysUseC8 || forceC8RenderImageIds.has(id)) {
      source = 'c8'
      c8ImageType = 'Viewable' // Otherwise transparency won't work
    }

    // console.log({autoTransparency, image, params})

    if (!c8ImageType) { throw new Error(`Unknown c8 image size specified '${c8Size}'`)}

    if (id) {
      if (_.isString(id)) {
        if (id.startsWith(`data:`) || id.startsWith('#')) { return id }
      }

      let imageNumber = !alwaysShowFullSizeImages && imageSize ? Math.max(imageSize.width, imageSize.height) : null
      if (!alwaysShowFullSizeImages && imageNumber) {
        imageNumber = imageBreakpoints.find(v => v >= imageNumber) ?? _.last(imageBreakpoints)
      }

      const baseFilename = alwaysShowFullSizeImages ? 'full' : `${imageNumber ? `!${imageNumber},${imageNumber}` : C8_TO_IMAGE_SIZE_ENUM[c8Size]}`

      if ((!source || source === 'cf') && aws.useCloudFront) {
        return aws.imageUrlForC8Image({id, filename: `${baseFilename}${autoTransparency ? '-tr' : ''}`, extension: 'png', isBackgroundImage})
      }

      let r = `${configs.config.imgProxyUrl}/csi-requesthandler/RequestHandler?URL=${encodeURIComponent(id)}&Download=false&Attribute=${c8ImageType}&Module=Publisher&Operation=GetFromNode&SecurityTokenURL=${authStore.securityTokenURL}`

      return r
    } else if (this._base64Image(image)) { return image?.image?.image } else {

      let img = image ? image[c8Size] ? image : image.image : null // deal with old prisma image containers

      return img && img[c8Size] ? `${configs.config.graphqlEndpoint}images/${img[c8Size]}?token=${authStore.accessToken}` : (img ? img : '')
    }

    return ''
  }

  activeSourceImages = computedFn((data) => {
    const {imageUrlFor, imagesForCatalogItem} = this
    let images = imagesForCatalogItem({data, takePer: 1})
    return _.reverse(_.uniqBy(_.reverse(images).filter(_.identity).map(img => ({
      ...img,
      largeImageURL: imageUrlFor(img, {c8Size: this.isDataTableView ? 'smallImage' : 'largeImage'})
    })), i => i.exId ?? i.id ?? i.file)) as any
  })

  @computed get _fieldGroups() {
    const {detailViewFor, index} = this,
      detailsView = detailViewFor(index)

    return _.flatMap(detailsView?.sidebar?.sections, section => section.fields).filter(f => f?.fieldGroup)
  }

  reset = () => {
    this.massCreate.reset()
    this.groupBy.reset()
  }

  @action clearSelection = () => {
    const {selection: {selectedIds}, groupBy: {groupingByName}} = this
    Array.from(groupingByName.values()).forEach(g => {
      g.selected.clear()
    })
    selectedIds.clear()
    this.selectAll = false

  }

  @computed get itemCount() {
    return this.itemCountFor(this.catalogConfig.index)
  }

  @observable.ref _workspaceCounts: { [key: string]: IAggregationResult } = {}
  @observable sortBySection = null

  @computed get offlineItemCount() {
    return this.filteredOfflineItems.length
  }

  @computed get onlineItemCount() {
    return (this._workspaceCounts ?? {})[this.index]?.doc_count ?? 0
  }

  @computed get hasLoadedConfig() { return this.workspaces.workspaceConfig != null}

  get urlProcessed() { return this.settingsStore?.urlProcessed}

  @computed get assignableFields(): Array<ICatalogFieldConfig> {
    const {libraries, features, isFieldEditable, checkSectionVisibility} = this

    // check visibility of the attributes that needs to hide from the sidebar
    checkSectionVisibility(this.catalogConfig?.fields ?? [])

    return _.orderBy(
      (this.catalogConfig?.fields ?? []).filter(
        f =>
          !f.hidden
          && f.showAsLibrary !== false
          && !shouldIgnoreCatalogField(this.workspaceConfig, f.field)
          && f.def
          && !libraries.some(l => l.assignTo === f.field) // Don't show in both libraries + assignable fields
          // && !f.esIndex  // Index items will be libraries elsewhere
          && (f.showAsLibrary || !_.includes(['Int', 'DateTime', 'Double', 'Float', 'String'], f?.def?.type)) // todo - remove 'String' and support it with virtualization like we do for non-chip items
          && (f.filter != false || f.card?.hide !== true)
          && (features.library?.show === 'all' || isFieldEditable(f.field))
      ),
      f => this.labelForFieldName_currentCatalog(f.field)
    )
  }

  /**
   * check the fields if any of them needs to be hidden from the library section explicitly
   * @param fields assignable fields to loop upon
   */
  checkSectionVisibility = (fields) => {
    let {hideAttributeFromSidebar} = this
    if (fields && fields.length > 0) {
      fields.map(item => {
        if (item.hideFromLibrarySidebar) {
          hideAttributeFromSidebar.add(item.section)
        }
      })
    }
  }

  /**
   * We have attribute values as keys into a dictionary but we need to pass the real attribute value to graphql
   * @param def
   * @param value
   */
  convertAttributeValue = (def: CustomAttributeDefinition, value: any) => {
    switch (def.type) {
      case 'Boolean':
        return new Boolean(value)
      case 'DateTime':
        return new Date(parseInt(value))

      case 'Float':
      case 'Int':
        return _.toNumber(value)

      default:
        return value
    }
  }

  @observable.ref colorwayCustomAttributes = []

  @computed get commonDefsDetail() {
    const {hasLoadedConfig} = this

    if (!hasLoadedConfig) return []

    const {workspaces: {workspaceConfig}} = this
    return _.keyBy(workspaceConfig?.catalog?.commonDefsDetail, f => f.type)
  }

  //@observable.ref customAttributeDefs: Array<CustomAttributeDefinition> = []

  @computed get fieldsWithDefs() {
    const {hasLoadedConfig} = this

    if (!hasLoadedConfig) return []

    const {workspaces: {workspaceConfig}} = this
    return workspaceConfig?.catalog?.fields?.filter(f => f.def)
             .map(f => ({name: f.agg ? f.field : (f.esField ?? f.field), ...this.commonDefsDetail[f.def?.type], ...f.def})) ?? []
  }

  get fieldByWorkspaceIndex() { return this.workspaces.fieldByWorkspaceIndex }

  @computed get computedFields() {
    const {fieldByWorkspaceIndex, index} = this,
      {fields} = fieldByWorkspaceIndex[index] ?? {}
    return _.values(fields).filter(f => f?.computed)
  }

  @computed get specDefs() {
    const {hasLoadedConfig} = this

    if (!hasLoadedConfig) return []

    const {workspaces: {workspaceConfig}} = this
    return workspaceConfig?.catalog?.fields?.filter(f => ((!f.def && f.card?.default) || f.esIndex)).map(f => ({name: f.field, label: f.label})) ?? []
  }

  @computed get enumFields() {
    const {hasLoadedConfig} = this

    if (!hasLoadedConfig) return []

    const {workspaces: {workspaceConfig}} = this
    return workspaceConfig?.catalog?.fields?.filter(f => (f.def && (f.def?.type === 'Enum' || f.def?.type === 'EnumList'))) ?? []
  }

  @computed get lookupItemFields() {
    const {hasLoadedConfig} = this

    if (!hasLoadedConfig) return []

    const {workspaces: {workspaceConfig}} = this
    return workspaceConfig?.catalog?.fields?.filter(f => (f.def && (f.def?.type === 'LookupItem' || f.def?.type === 'LookupItemList'))) ?? []
  }

  @computed get dataMap() {
    return _.merge({}, {
        exId: 'exId',
        images: 'images',
        title: 'name',
        subtitle1: 'description'
      }
      , this.workspaceConfig?.dataMap)
  }

  //
  // @computed get customAttributeDefById(): { [id: string]: CustomAttributeDefinition } {
  //   return _.keyBy([...(this.customAttributeDefs || []), ...(this.tags || [])], ca => ca.id)
  // }

  @computed get caDefByName(): { [id: string]: CustomAttributeDefinition } {
    return _.keyBy(this.fieldsWithDefs, ca => ca.name)
  }

  @observable.ref customAttributesGroupByCount

  @computed get imageRatioById() {
    return _.keyBy(this.imageRatios, ir => ir.id)
  }

  /**
   * Currently selected image ratio type
   */
  @computed get imageRatio() {
    return this.imageRatioById[this.settings.imageRatio ?? 'square']
  }

  // region Card View (what fields is the user seeing on their cards)

  // endregion
  // region

  @observable.shallow vlpLibraryTypes: { [name: string]: { id, name, displayName: { singular?: string, plural?: string } } } = {
    merchOptions: {id: null, name: 'merchOption', displayName: {singular: 'MPV', plural: 'Merch Product Versions'}},
    merchProducts: {id: null, name: 'merchProducts', displayName: {singular: 'Product', plural: 'Products'}},
    merchCollections: {id: null, name: 'merchCollections', displayName: {singular: 'Merch Collection', plural: 'Merch Collections'}},
    merchOptionSecondaries: {id: null, name: 'merchOptionSecondaries', displayName: {singular: 'Merch Option Secondary', plural: 'Merch Option Secondaries'}},
    merchSeason: {id: null, name: 'merchSeason', displayName: {singular: 'Season', plural: 'Seasons'}}
  }

  // endregion

  // region URL Handling

  /** Todo - this is no longer valid in ES**/
  get libraryTypes() { return this.fieldByName}

  //this.selectedPerspectiveId = this.hasPerspectives ? (perspective ?? _.first(Object.keys(perspectiveDict))) : _.first(Object.keys(perspectiveDict))
  @action onSetPerspective = (p: CatalogPerspectiveConfig) => () => {
    if (this.perspective === p) {
      this.settings.perspective = undefined
    } else {
      this.selection.clear()
      this.setPerspective(p)
    }
  }

  @action setPerspective = (p: CatalogPerspectiveConfig) => {
    const {settings, workspaceConfig, groupBy: {hierarchyGroups}, perspectivesById} = this
    settings.perspective = p.id === workspaceConfig?._id ? undefined : p.id
    hierarchyGroups.forEach(g => Array.from(g.settingsByGroup.values()).forEach(s => s.perspective = undefined))
  }

  @computed get isShowingPerspective() {
    const {hasPerspectives, workspaceConfig, perspective} = this
    return hasPerspectives ? perspective?.id !== workspaceConfig?._id && perspective?.field != null : false
  }

  @computed get perspectivesById() {
    return _.keyBy(this.catalogConfig?.perspectives, p => p.id)
  }

  /**
   * Catalog index, respective of perspective
   */
  @computed get currentIndex() { return this.perspective?.index ?? this.index}

  @computed get perspectiveSectionConfig() {
    return this.sectionConfigFor(this.perspective?.id)
  }

  @computed get hasLoadedSettings() {
    return this.settingsStore?.urlProcessed === true
  }

  @computed get perspective(): CatalogPerspectiveConfig {
    if (!this.hasLoadedSettings) return null

    const {settings: {perspective}, perspectivesById} = this
    return !perspective ? null : perspectivesById ? perspectivesById[perspective] : null
  }

  @computed get headerPerspectives() {
    let r = this.catalogConfig?.perspectives?.filter(p => !p.hideInHeader) || []
    if (r.length === 1 && r[0].id === this.workspaceConfig._id) { return [] }
    return r
  }

  @computed get showPerspectiveButtons() {
    const {headerPerspectives, showUnsavedChangesButton, filteredOfflineItemsIgnorePerspectives, filter} = this
    return !_.isEmpty(headerPerspectives) || showUnsavedChangesButton || (!_.isEmpty(filteredOfflineItemsIgnorePerspectives) && filter.hasLoaded)
  }

  @computed get hasPerspectives() {
    const {catalogConfig} = this
    return catalogConfig?.perspectives?.length > 1
  }

  /**
   * The current 'perspective' of the catalog.
   * Usually the root catalog item (Product for example).  {@see perspective}
   * Generally guaranteed to be non-null (assuming config has been loaded)
   */
  @computed get perspectiveId() {
    return this.perspective?.id ?? this.workspaceConfig?._id
  }

  @computed get isShowingMainCatalogItemAsImageSource() {
    if (!this.hasLoadedSettings) return true
    const {settings: {imageSource}, imageSourceById, index} = this
    return _.isEmpty(imageSource.ids) || imageSource.ids.some(id => imageSourceById[id].index === index)
  }

  @computed get isShowingPresentation() {
    return this.presentation?.settings?.show === true
  }

  get reportSettings() { return this.settingsStore.reportSettings}

  @computed get settings(): Partial<CatalogSerializableSettings> {
    const {settingsStore} = this

    //if (_workspace?.isCurrentWorkspace) return stores.catalog.settings

    if (!settingsStore?.settings) debugger // should be avoided
    return settingsStore?.settings ?? _.cloneDeep(DEFAULT_CATALOG_USER_SETTINGS)
  }

  @action toggleShowEmptyGroups = () => {
    this.settings.groupBy.showEmptyGroups = !this.settings.groupBy.showEmptyGroups
  }

  @action toggleAutoSaveInlineChanges = () => {
    this.settings.autoSaveInlineChanges = !this.settings.autoSaveInlineChanges
  }

  @action excludeFieldsToExport = (fieldDef) => {
    const fieldArray = fieldDef?.field?.split('.') ?? []
    // TODO: support to include xrefs type of fields and their attributes in excel export
    return (!fieldArray.some(f => _.includes(['stylecandidates', 'secondaries', 'colorways'], _.toLower(f))) && fieldDef?.card?.hide !== true)
  }

  exportableFieldsByIndex = computedFn((workspaceIndex: string) => {
    const {strings, workspaceByIndex, cardView, cv: {overriddenVisibleFields}, fieldForWorkspace, index, excludeFieldsToExport} = this
    let exportableFields = []
    exportableFields = _.map(overriddenVisibleFields.filter(f => excludeFieldsToExport(f)), (field) => this.mapExportField(field))
    exportableFields = _.uniqBy(_.concat(exportableFields, _.map(Object.keys(cardView).filter(k => cardView[k]?.show === true), (field) => {
      const fieldDef = fieldForWorkspace(field, workspaceByIndex[workspaceIndex ?? index])
      return fieldDef && excludeFieldsToExport(fieldDef) ? this.mapExportField(fieldDef) : null
    })), f => f?.field)

    return exportableFields.filter((fields) => fields?.label !== strings.noValue && fields !== null)
  }, {name: 'exportableFieldsByIndex'})

  @action mapExportField = (field) => {
    const {labelForField, findFieldInTree} = this,
      {def} = field
    const type = field?.def?.type ?? undefined,
      label = labelForField(field, '', def?.type === 'TagList' || def?.type === 'EnumList' ? 'plural' : 'singular', findFieldInTree(field.field, true) != null) ?? this.strings.noValue,
      precision = def?.precision
    return {field: field.field, label, type, precision}
  }

  // endregion

  seasonSelector: SeasonSelectorStore

  // region Item Creation
  @action createProduct = async () => {
    // this.massCreate.loading = true
    // this.massCreate.uploadingMessage = 'Creating Product...'
    //
    // try {
    //   const {nav: {match: {params: {season, category1, category2}}}, product: {selected}, filter: {state: {groups: {tags}}}} = this,
    //     {groupBy} = this
    //   let productInput: any = {
    //     seasonId: season,
    //     category1Id: category1,
    //     category2Id: category2
    //   }
    //
    //   if (tags?.selected?.size) {
    //     productInput.tags = Array.from(tags.selected.keys())
    //   }
    //   const {data: {createProduct: product}} = await ProductService.createProduct(
    //     {
    //       input: {...productInput, ...props.productInput}
    //     },
    //     null,
    //     {/*FilterQuery, data: this.productInput,*/ isFormatted: true, skipOptimisticResponse: true, updateQueryVariables: stores.catalog ? {where: gqlApi.whereProductsClause} : null, ...props})
    //
    //   runInAction(() => {
    //     selected.clear()
    //     selected.add(product.id)
    //     groupBy.queryManager.refetchAll()
    //   })
    // } finally {
    //   runInAction(() => {
    //     this.massCreate.loading = false
    //     this.massCreate.uploadingMessage = ''
    //     stores.product.sidebarUpdated = true
    //     stores.product.sidebarAction = 'create'
    //   })
    // }
  }

  massCreate = new MassProductCreateStore(this)
  groupBy: GroupByStore

  // endregion

  // region Custom Attribute Helpers

  attrForEsField = (attrName: string) => {
    const {caDefByName: d, hasLoadedConfig} = this
    if (!hasLoadedConfig) { return null}

    let key = attrName

    if (d[key]) {
      return d[key]
    }

    key = key.replace('attributes.', '')

    if (d[key]) {
      return d[key]
    }

    //console.warn(`Unable to find matching attr definition for ${attrName}`)
    return null
  }

  aggregateAttrValueLabelFor: ({v: Array, def: CustomAttributeDefinition}) => string = _.memoize(({v, def}: { v: Array<any>, def: CustomAttributeDefinition }) => {
    const {attrValueLabelFor} = this,
      counts = _.countBy(v.filter(v => !_.isUndefined(v) && !_.isNull(v)).map(e => e?.name ?? e))
    const label = _.keys(counts).map(val => {
      const transformedVal = (val === 'true' || val === 'false') ? val === 'true' ? true : false : val,
        count = counts[val] == 1 ? '' : `(${counts[val].toLocaleString()})`
      return `${attrValueLabelFor({v: transformedVal, def})} ${count}`
    }).join(', ')
    return label
  })

  attrValueLabelForFieldName: ({v: any, field: string}) => string = computedFn(({v, field}: { v: any, field: string }) => {
    const {strings, _fieldByName} = this
    if (!field) return v ?? strings.noValue
    const f = _fieldByName[field]
    if (!f?.def) return v

    return this.attrValueLabelFor({v, def: f.def})
  })

  attrValueLabelFor: ({v: any, def: CustomAttributeDefinition}) => string = _.memoize(({v, def}: { v: any, def: CustomAttributeDefinition }) => {
    const {strings, labelFor} = this
    if (!def) return v === null ? strings.noValue : _.trim(v)
    const {type, booleanLabels} = def
    switch (type) {
      case 'Boolean':
        return _.isNull(v) ? strings.emptyLabel : v ? (booleanLabels?.trueLabel ?? 'Yes') : (booleanLabels?.falseLabel ?? 'No')
      case 'DateTime': {
        if (!v || v === '0') return strings.noValue
        // const date = new Date(v)
        // return date.toLocaleString(undefined, {year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric'})
        const r = moment(v).format('MM/DD/YYYY')

        //console.log({v, r})
        return r
      }

      case 'String':
        return _.isNull(v) ? strings.emptyLabel : _.isEmpty(v) ? strings.noValue : _.trim(v)
      case 'TagList':
        return !v || v.length == 0 ? strings.emptyLabel : _.isString(v) ? v : _.isArray(v) ? v.map(v => v?.name ?? strings.noValue).join(', ') : v?.name
      default:
        return _.isArray(v)
               ? v.length == 0 ? strings.emptyLabel : v.map(v => _.isObject(v) ? labelFor(v) : v).join(', ')
               : _.isObject(v) ? labelFor(v) : v
    }
  })

  /**
   * We get back 1/0 for boolean attributes from ES, convert them to true/false like the client expects
   */
  attrKeyFor = _.memoize(({key, def}: { key: any, def: CustomAttributeDefinition }) => {
    if (!def) return key
    const {type} = def
    switch (type) {
      case 'Boolean':
        return !!key
      default:
        return key
    }
  })

  esFieldFor_byField = (field: string, isGrouping = false) => {
    const {esFieldFor, index, _fieldByName, fieldByName, catalogConfig, workspaceConfig} = this
    if (field === index) {
      if (catalogConfig?.initialSort) {
        const def = _fieldByName[catalogConfig?.initialSort]
        if (!def) throw new Error(`Catalog ${workspaceConfig?._id} is incorrectly configured - initial sort is set to a field not defined in the config: ${catalogConfig?.initialSort}`)
        return esFieldFor(def, isGrouping)
      } else {
        return esFieldFor({field: '_id'}, isGrouping)
      }
    } else {
      const f = _fieldByName[field]
      return f ? esFieldFor(f, isGrouping) : field
    }
  }

  private queryParamFieldFor = (field: string): { qpField: string, isArray: boolean } => {
    switch (field) {
      case 'plan':
        return {qpField: 'plans', isArray: true}

      case 'folder':
        return {qpField: 'folders', isArray: true}

      case 'collection':
        return {qpField: 'collections', isArray: true}
    }

    return {qpField: field, isArray: false}
  }

  get hasVlpHierarchy() { return this.vlp?.hierarchy?.hasVlpHierarchy === true}

  @action navigateToHierarchy = (data) => {
    const {queryParamFieldFor, seasonSelectorFields, vlp: {hierarchy}, isProductView, filter} = this

    let queryParams: { plans?, collections?, merchSeason?, season?, category1?, category2?, folders? } = {}
    seasonSelectorFields.map(({field, hideInCatalog}) => {
      const v = data[field]

      if (v && !_.isArray(v)) {
        const {id, name} = v,
          {state: {groups}} = filter,
          filterField = groups[field]
        if (filterField && filterField?.selected.size === 0 && !hideInCatalog) {
          const val = filterField?.groupedByValue?.[name]
          filterField?.selected.set(val?.name, {...val, exId: id})
        } else {
          const {qpField, isArray} = queryParamFieldFor(field)
          queryParams = {
            ...queryParams,
            [qpField]: isArray ? [id] : id
          }
        }
      }
    })

    if (!_.isEmpty(queryParams)) {
      const folders = [data?.subCategories?.map(sub => sub.id)] ?? [],
        selected = {...queryParams, folders}

      if (!_.isEqual(selected, hierarchy.selected)) {
        // YFTS-6523 - Omitting collectionVersion as we do not need that in hierarchy
        hierarchy.selected = _.omit(selected, 'collectionVersion')
        // Avoid rendering old cards while switching seasons
        isProductView && this.itemWrappers.clear()
      }
    }
  }

  /**
   * Adds .keyword if needed
   */
  esFieldFor = (fieldConfig: Partial<ICatalogFieldConfig>, isGrouping = false, isSorting = false) => { //removed memoize as giving older id when in pdf store
    if (!fieldConfig) return null

    const {esField, def, filter, section, sort} = fieldConfig

    const field = esField ?? fieldConfig.field,
      keyword = fieldConfig.keyword // || (def?.type == 'String' && (filter != null || sort != null))  // THings are inconsistent right now, mpvs are keyword first text second, style/colorway are the opposite,
    // text with keyword fields

    let r = def ? this.attrFieldFor({...def, name: field}, keyword) : `${field}${keyword ? '.keyword' : ''}`

    if (isGrouping) {
      if (isSorting && fieldConfig.group?.sortField) {
        r += `.${fieldConfig.group.sortField}`
      } else if (fieldConfig.group?.field) {
        r += `.${fieldConfig.group.field}`
      } else if (fieldConfig.esIndex || isTagField(fieldConfig)) {
        r += `.name`
      }
    }

    return r
  }

  attrFieldFor: (def: CustomAttributeDefinition, keyword: boolean) => string = _.memoize((def: CustomAttributeDefinition, keyword?: boolean) => {
    return def ? `${def.name}${(keyword/* || def.type == 'String'*/) ? '.keyword' : ''}` : null
  })

  // endregion

  // TODO - remove this, replace in board config - no special cases should exist for customers
  @action sortedVersions = (versions) => {
    if (ui?.settings?.showSortedVersions) {
      const version = {}
      versions.map(v => {
        const year = v.substring(0, 2)
        if (!version[year]) version[year] = []
        version[year].push(v)
      })
      const values = []
      for (var v in version) { values.push(...version[v].sort().reverse()) }
      return values
    }
    return versions.sort()
  }

  // region ElasticSearch specific

  // private _lastQueryVariablesEs
  @observable.ref queryVariablesEs: any = {}

  @action clearSeasonSelection = () => {
    const {seasonSelectorFields, filter} = this
    seasonSelectorFields.forEach(f => {
      filter.clearSelection(f.field)
    })
  }

  @computed get seasonSelectorFields(): Array<SeasonSelectorFieldConfig> {
    const {workspaceConfig, _seasonSelectorFields} = this
    if (!workspaceConfig?.seasonSelectors) return []

    const {seasonSelectors} = workspaceConfig
    return _seasonSelectorFields(seasonSelectors) as Array<SeasonSelectorFieldConfig>
  }

  @computed get allSeasonSelectorFields(): Array<SeasonSelectorFieldConfig> {
    const {_seasonSelectorFields} = this,
      {workspaces: {availableWorkspaces}} = this

    const seasonSelectors = availableWorkspaces.reduce((acc, v) => [...acc, ...(v.seasonSelectors ?? [])], [])
    return _seasonSelectorFields(seasonSelectors) as Array<SeasonSelectorFieldConfig>
  }

  get refetchId() { return this.es.refetchId }

  @action refetch = async (refetchType?: RefetchType) => {
    // console.trace(`refetch`)
    this.es.refetchId = guid()
    this.es.lastRefetchType = refetchType

    if (!refetchType) {
      const {filter, debounceFetchWorkspaceCounts} = this
      await Promise.all([
        debounceFetchWorkspaceCounts(),
        filter.refetch(true)
      ])
    }

    // TODO - all of this should be done via the server pushing notifications to us instead of blind queries
    const {flexLayout} = this
    flexLayout.refetchAllCatalogRelatedTabs(false)
  }

  @computed
  private get _sourceDynamic_ImageSources() {
    const {imageSourceOptions} = this
    return _.uniq(
      _.flatMap(imageSourceOptions?.length ? _.flatMap(imageSourceOptions.map(o => [o.field, ...(o.fields ?? [])]
        .filter(_.identity)
        .map(f => [
          f,
          `${f}.exId`,
          `${f}.attributes.Active`,
          f.endsWith('.images') ? `${f.substring(0, f.length - 7)}.name` : null,
          f.endsWith('.images') ? `${f.substring(0, f.length - 7)}.exId` : null
        ]))) : ['images.exId']
      ).filter(_.identity)
    )
  }

  @computed get _sourceDynamic() {
    const {groupBy: {hierarchyGroups}, perspective, libraries, _sourceDynamic_ImageSources, cv: {visibleAttributeDefinitionNames, otherVisibleCardFields}, catalogConfig: {fields}, workspaces: {workspaceConfig}} = this,
      source = _.uniq(_.flatMap([
        '_id', '*.Active', this.catalogConfig.openInC8Field,
        ...(workspaceConfig?.catalog?._source || []),
        ..._sourceDynamic_ImageSources,
        ...fields.filter(f => f.___isActiveField).map(f => f.field),
        ...(workspaceConfig?.sections?.filter(s => s.viewOptions?.field).map(s => s.path) ?? []), //We need the full nested object here because the update API will blow away fields we don't specify when updating so if we don't query all fields we'll lose some on edit // `${s.path ? `${s.path}.` : ''}${s?.viewOptions?.field}`) ?? []),
        ...visibleAttributeDefinitionNames,
        ...otherVisibleCardFields,
        ...hierarchyGroups.map(hg => hg?.settingsForLevel?.swatches?.map(s => s.field)),
        perspective?.field,
        libraries.filter(l => l.assignTo).map(l => `${l.assignTo}.id`)
      ]).filter(_.identity)),
      computedSource = this.computedFields.filter((field: ICatalogFieldConfig) => source.some(s => s === field.field)).map(field => field.computed?.expression?.fields ?? [])
    return {
      _source: _.uniq(_.flatMap([...source, ...computedSource]))
    }
  }

  // region Sorting

  get catalogItemsOrderBy() {
    return this.groupBy?.catalogItemsGroup?.orderBy
  }

  onClickSortMenu = (item) => (e?) => {
    this.groupBy.catalogItemsGroup.toggleSortField(item.field)(e)

    // this.catalogItemsOrderBy.field = item.field
    // if (item.field === 'hasImage') {
    //   this.catalogItemsOrderBy.order = 'asc'
    // }
    // this.catalogItemsOrderBy.custom = !!item.sort?.catalogItem
  }

  /**
   * Sorting is complex due to custom sort and multi-level sorting
   */
  @computed get esProductSortBy() {
    const {groupBy: {catalogItemsGroup}, newlyCreatedIds, esFieldFor_byField, catalogConfig, _fieldByName} = this,
      defaultOrderByArr = catalogConfig?.defaultSort ?? [],
      orderBy = catalogItemsGroup?.orderBy ?? [{field: catalogConfig?.initialSort ?? 'merchOption.name', order: 'asc', custom: false}] as IObservableArray<IGroupOrderBy>

    return {
      sort: [
        //newlyCreatedIds.length > 0 && {"_script": "doc['exId'].value "},
        ..._.uniqBy([
        ...orderBy.slice().reduce((acc, {field, order, custom}) => {
          const sortField = _fieldByName[field]
          return [
            ...acc,
            // For true custom fields (custom turned on AND there's a custom order defined), sort by the _score function
            sortField?.agg === 'exists' || (custom && sortField?.sort?.custom) ? {
              _score: {
                order: 'asc' // If you sort by two custom fields and one is asc and the other desc, you won't know what the right answer is, so we change the scores to always return in the correct ascending order
              }
            } : {
              [sortField ? `${this.esFieldFor(sortField)}${sortField.sort?.subField ? `.${sortField.sort.subField}` : ''}` : field]: {
                missing: field === 'images' ? order === 'asc' ? '_last' : '_first' : undefined,
                order: order.toLowerCase(),
                unmapped_type: 'keyword'
              }
            }
          ]
        }, []),
        ...defaultOrderByArr.map((defaultOrderBy) => {
          const field = esFieldFor_byField(defaultOrderBy.field)

          if (field) {
            return {
              [field]:
                {
                  missing: defaultOrderBy.field === 'images' ? defaultOrderBy.order === 'asc' ? '_last' : '_first' : undefined,
                  order: defaultOrderBy.order.toLowerCase(),
                  unmapped_type: 'keyword'
                }
            }
          } else { return null }
        })
      ].filter(_.identity), v => _.first(_.keys(v)))
      ].filter(_.identity)
    }
  }

  @computed get sortableFields() {
    return _.values(this._fieldByName)
      .filter(field => field?.sort !== null && field?.sort && _.isObject(field.sort) && field.sort?.catalogItem !== false)
  }

  @computed get fieldsWithCustomSorts() {
    return this.sortableFields.filter(field => field.sort._func != null || field.sort.custom?._key?.order)
      .map(({field, sort: {_func, custom}}) => ({
        field,
        _func,
        customOrder: new Map<string, number>((custom?._key?.order ?? []).map((v, i) => [v, i]))
      }))
  }

  @computed get fieldsByNameWithCustomSorts() {
    const {fieldsWithCustomSorts} = this
    return _.keyBy(fieldsWithCustomSorts, 'field')
  }

  @computed get catalogItemOrderByFields(): Array<OrderByField & { text: string } & Partial<ICatalogFieldConfig>> {
    const {sortBySection, fieldBySection, labelForField, workspaces: {workspaceConfig, deploymentConfig}} = this

    if (!workspaceConfig) { return [] }

    const {catalog: {fields}} = workspaceConfig

    const sortableFields = (!sortBySection ? fields : fieldBySection[sortBySection])
      .filter(f => f?.sort !== null && f?.sort?.catalogItem !== false && (!sortBySection || f))
      .map(f => ({...f, text: labelForField(f, null, 'singular', true)}))

    const defaultAttributes = sortableFields.filter(f => !f.def || f?.card?.default),
      sortByCustomer = ui?.settings?.sortDefaultAttributes ? _.sortBy(defaultAttributes, f => f.text) : defaultAttributes

    return _.sortBy(
      [
        ...sortByCustomer,
        ...sortableFields.filter(f => f.def && !f?.card?.default)
      ], f => f.text)
  }

  //endregion
  es: CatalogElasticSearchStore

  @computed(jsonComputedOptions) get searchFieldsEs(): string[] {
    const {
      cv: {visibleCardFields}, isTextField, availableCardFields, esFieldFor, catalogConfig, _fieldByName
    } = this
    // Todo - should be configurable and gotten from the server, scoring and custom attributes too...
    return _.uniq([
      ...(catalogConfig._searchFields || []),
      //...(["merchProduct.name"])
      ...(catalogConfig._source || []).filter(f => _fieldByName[f]?.def?.type != 'Boolean').map(f => _fieldByName[f]?.def?.type === 'String' ? `${f}.text` : f),
      ...visibleCardFields.filter(f => f.keyword || f.search || f?.def?.type == 'String' || f?.def?.type == 'Enum' || f?.def?.type == 'RefList' || f?.def?.type == 'TagList').map(f => `${f.field}.text`),
      ...availableCardFields.filter(f => f.search).map(f => esFieldFor(f))
    ]).filter(f => {
      if (!_.identity(f)) return false

      return isTextField(f)
    })
  }

  // region Image Source

  @computed get imageSourceOptions(): Array<CatalogImageSourceConfig> {
    return this.catalogConfig?.imageSources ?? [{field: 'images', id: this.index}]
  }

  @computed get imageSourceById(): { [id: string]: CatalogImageSourceConfig } {
    return _.keyBy(this.imageSourceOptions, o => o.id)
  }

  fieldFor = computedFn(({field, id}: { id?: string, field?: string }) => {
    return this._fieldByName[field ?? id]
  })

  @computed get title() {
    return this.workspace?.config?.title ?? this.strings.emptyLabel
  }

  @computed get shortTitle() {
    return this.workspace?.config?.shortTitle ?? this.title
  }

  @action toggleImageSource = (id) => {
    const {fieldFor, settings} = this

    const field = this._fieldByName[id],
      field2 = fieldFor({id}),
      is = this.imageSourceById[id]
    //imageSourceView = is?.view ?? (is?.multiple === false ? 'first' : isArrayCatalogField(field) && (this.catalogConfig?.features?.imageSources?.multiple !== false) ? 'stack' : 'first')

    if (field != field2) {
      debugger
    }

    //console.debug(`Setting '${title}' image source to ${id}` , { field, field2, is})

    const {imageSource, imageSource: {ids}} = settings
    imageSource.ids = ids.includes(id) ? ids.filter(_id => _id != id) : [...ids, id]
  }

  @computed get catalogItemDetailView() {
    return this.detailViewFor(this.index)
  }

  @computed get imageSource() { return _.cloneDeep(this.settings.imageSource) }

  @computed get selectedImageSources(): Array<CatalogImageSourceConfig> {
    const {imageSource, imageSourceById, imageSourceOptions} = this

    if (_.isEmpty(imageSource.ids)) {
      return imageSourceOptions
    } else {
      return imageSource.ids.map(id => imageSourceById[id]).filter(_.identity)
    }
  }

  @computed get selectedImageSourceFields() {
    return _.uniq(_.flatMap(this.selectedImageSources.map(is => is.field ? [is.field] : is.fields)))
  }

  // @computed.struct get imageSource(): CatalogImageSourceConfig  {
  //   //if (!this.isGlobalStore && this.isCurrentWorkspace) return this.globalCatalog.imageSource
  //
  //   // trace()
  //   const {imageSourceOptions, settings: {imageSource}} = this
  //   const r = imageSourceOptions?.find((v, i) => imageSource.ids.has(v.id)) ?? _.first(imageSourceOptions)
  //   return { /*new CatalogImageSource()*/
  //     ...defaultImageSourceConfig,
  //     ..._.omit(r, 'field'),
  //     fields: !r ? [] : r?.fields ?? [r.field],
  //     view: imageSource.view ?? r.view
  //   }
  // }

  @computed get objectFit() {
    const {selectedImageSourceFields, workspaceByIndex, libraries, features} = this
    if (features?.card?.objectFit) return features?.card?.objectFit

    const library = libraries.find(l => (selectedImageSourceFields ?? []).some(f => l.assignTo && f?.startsWith(`${l.assignTo}.`)))

    if (library) {
      const ws = workspaceByIndex[library.index]

      if (ws?.catalog?.features?.card?.objectFit) return ws.catalog.features.card.objectFit
    }

    return 'contain'
  }

  // @action cycleImageSpec = () => {
  //   const {imageSourceOptions, settings: {imageSource: {id}}} = this
  //   let index = !id ? 0 : imageSourceOptions?.findIndex((v, i) => v.id == id || i.toString() == id)
  //   index++
  //
  //   this.settings.imageSource.id = index >= imageSourceOptions.length ? null : (imageSourceOptions[index].id ?? index.toString())
  // }

  // endregion

  @action mapCatalogItemResults = (data?: Array<{ _id, _source: any, fields }>) => {
    const {strings, dataMap} = this

    const dataArray = data?.map(({_id, _source, fields}) => ({
      // Used for the tooltip
      title: (dataMap?.title ? _.get(_source, dataMap?.title) : null) ?? strings.emptyLabel,
      subtitle1: (dataMap?.subtitle1 ? _.get(_source, dataMap?.subtitle1) : null) ?? strings.emptyLabel,
      _id, id: _id, // Synthetic ID field for _id
      ..._source,
      ...(fields || {})
    }))

    return dataArray
  }

  pathForData = (image: ImageRecord, data: BuilderCardStore | any, workspace: Partial<WorkspaceConfig>) => {
    const {idFor, _fieldByName, libraryForField} = this

    let item = image?.__parent,
      r: ICatalogItemDetailsPathElement[] = []

    while (item != null) {
      const field = item.__path ? _fieldByName[item.__path] : undefined,
        library = libraryForField(field),
        pathItem = {
          where: [{value: idFor(item, false)}],
          libraryId: library?.id,
          catalogField: field?.field,
          esIndex: library?.index ?? field?.esIndex ?? workspace.catalog.index
        }

      if (!pathItem.esIndex) {
        DEBUG && console.debug(`Unable to determine index for `, {path: item.__path, parent: item.__parent, item}, 'Your configuration needs to include field and index information so that a child image double click can open the correct related object.  Ignoring.')
        // debugger
      } else {
        //const library = catalog.libraryById[field.section]

        r.splice(0, 0, removeUndefinedsNested(pathItem))
      }
      item = item.__parent
    }

    if (_.first(_.first(r)?.where)?.value !== data.id) {
      r.splice(0, 0, removeUndefinedsNested({
        where: [{value: data.id}], esIndex: workspace.catalog.index
      }))
    }

    return r
  }

  // region Config

  /***
   * The _current_ workspace
   *
   * A customer deployment will typically have a number of 'spaces':  Merchandising, Style Catalog, Vendor Samples, etc
   * Each workspace generally has a catalog configuration describing the main 'catalogItem' of the board and associated libraries which can be associated with the items on the board
   */
  @computed get workspaceConfig(): WorkspaceConfig {
    const {workspaces, _workspace, isGlobalStore} = this
    if (_workspace) return _workspace?.config

    return isGlobalStore ? globalStores?.workspaces?.workspaceConfig : workspaces?.workspaceConfig
  }


  @computed get openInC8Field() {
    return this.catalogConfig.openInC8Field ?? '_id'
  }

  @computed get catalogConfig(): CatalogConfig {
    const {workspaceConfig} = this
    const r = workspaceConfig?.catalog || {fields: [], _source: [], _searchFields: []}

    // Check for bad data
    if (DEBUG) {
      const byField = _.groupBy(r?.fields, f => f.field ?? f.esField)
      Object.keys(byField).forEach(k => {
        if (byField[k].length > 1) {
          console.debug(`${this.debugId} - [warning] - Duplicate field names detected in config for workspace '${workspaceConfig?._id}', the field '${k}' appears ${byField[k].length} times.  This will cause undefined behavior.`, {items: toJSRecurse(byField[k])})
        }
      })
    }

    return r
  }

  @computed get allWorkspaceFields() {
    const {fieldByWorkspaceIndex, workspaces: {availableWorkspaces}} = this

    /*const chain = _.chain(availableWorkspaces);
    const indexs = chain.map(d => d.catalog.index);
    const entries = indexs.map(d => {
      return Object.entries(fieldByWorkspaceIndex[d].fields).map(
        ([fk, fv]) => {
          return { [`${fieldByWorkspaceIndex[d].workspace._id}.${fk}`]: fv }
        })
    })
    return entries.flatten()
      .reduce((fields, d) => { return fields = { ...fields, ...d } }, {})*/

    return _.chain(availableWorkspaces)
      .filter(ws => ws.catalog?.index != null)
      .map(ws => ws.catalog.index)
      .map(index => Object.entries(fieldByWorkspaceIndex[index].fields).map(([fk, fv]) => ({[`${fieldByWorkspaceIndex[index].workspace._id}.${fk}`]: fv})))
      .flatten()
      .reduce((fields, d) => _.assign(fields, d), {})
      .value()
  }

  @computed get availableSwatchFields() {
    return _.values<ICatalogFieldConfig>(this._fieldByName).filter(f => (f.esIndex && f.group?.hideSwatch !== true))
  }

  @computed get vlpDynamicCatalogFields(): { [field: string]: ICatalogFieldConfig } {

    const {strings, vlp, customHierarchy} = this
    if (!vlp?.hierarchy?.hasVlpHierarchy) return {}

    const {hierarchy: {planById, collectionById, folderById, foldersByDepth}} = vlp
    return {
      [VLP_PLAN_GROUP]: {
        field: VLP_PLAN_GROUP,
        esField: 'plan',
        label: 'Plan',
        group: {
          field: 'exId',
          sortField: 'name',

          filter: ({keys}) => {
            const keyValue = keys[VLP_PLAN_GROUP]
            return planById[keyValue] != null
          },

          titleFor: ({keys}) => {
            const keyValue = keys[VLP_PLAN_GROUP]

            if (keyValue) {
              return planById[keyValue]?.name ?? keyValue
            }

            return null
          }
        }
      },
      // VLP_FOLDER_###
      ..._.keys(foldersByDepth).reduce<{ [groupName: string]: ICatalogFieldConfig }>((acc, depthAsString) => {
        const groupName = `${VLP_FOLDER_GROUP}_${depthAsString}`,
          depth = parseInt(depthAsString)

        return ({
          ...acc, [groupName]: {
            field: `${VLP_FOLDER_GROUP}_${depth}`,
            editable: true,
            label: customHierarchy?.get(`Folder ${depth + 1}`)?.assignedName ?? `Folder ${depth + 1}`,
            esField: 'subCategories',
            group: {
              field: 'exId',
              sortField: 'name',
              filter: ({keys}) => {
                const keyValue = keys[groupName]
                return folderById[keyValue] != null
              },
              titleFor: ({keys}) => {
                const {folderById} = vlp.hierarchy,
                  keyValue = keys[groupName]

                if (keyValue) {
                  return folderById[keyValue]?.name ?? keyValue
                }

                return null
              },
              subtitle: _.range(depth + 1).map(d =>
                ({
                  field: d === 0 ? VLP_PLAN_GROUP : `${VLP_FOLDER_GROUP}_${d - 1}`,
                  valueFor: data => {
                    const folder = folderById?.[data?.keys?.[groupName]]

                    if (folder) {
                      if (d == 0) {
                        return {item: folder.plan, label: folder.plan.name}
                      } else if (folder?.parent?.id) {
                        let parent = folderById[folder.parent.id]
                        for (var i = folder.level; i > d + 1 && parent?.parent?.id; i--) {
                          parent = folderById[parent.parent.id]
                        }

                        return {item: parent, label: parent?.name ?? strings.emptyLabel}
                      }
                    }

                    return null
                  }
                })),
              filterResultBuckets: results => {
                const folders = vlp.hierarchy.folderByDepthForSelectionDict[depth]
                return _.isEmpty(folders) ? [] : results.filter(({key, keys}) => folders[keys ? keys[groupName] : key[groupName]])
              }
            }
          } as ICatalogFieldConfig
        })
      }, {}),
      [VLP_COLLECTION_GROUP]: {
        field: VLP_COLLECTION_GROUP,
        editable: true,
        label: customHierarchy?.get('Collection')?.assignedName ?? 'Collection',
        esField: 'collection',
        group: {
          field: 'exId',
          sortField: 'name',
          filter: ({keys}) => {
            const keyValue = keys[VLP_COLLECTION_GROUP]
            return collectionById[keyValue] != null
          },
          titleFor: ({keys}) => {
            const keyValue = keys[VLP_COLLECTION_GROUP]

            return !keyValue ? null
                             : (collectionById[keyValue]?.name ?? strings.emptyLabel)
          },
          subtitle: _.range(_.keys(vlp.hierarchy.foldersByDepth).length + 1).map(d =>
            ({
              field: d === 0 ? VLP_PLAN_GROUP : `${VLP_FOLDER_GROUP}_${d - 1}`,
              valueFor: ({keys}) => {
                const keyValue = keys[VLP_COLLECTION_GROUP],
                  collection = keyValue ? collectionById[keyValue] : null

                // console.log(collection, d)
                if (!collection) return null
                else if (d === 0) return {item: collection, label: collection?.plan?.name ?? strings.noValue}
                else {
                  let folder = folderById[collection.folder.id],
                    parent = folder
                  for (var i = folder.level; i > d && parent?.parent?.id; i--) {
                    parent = folderById[parent.parent.id]
                  }

                  return {item: parent, label: parent?.name ?? strings.noValue}
                }
              }
            }))
        }
      } as ICatalogFieldConfig
    }
  }

  @computed get toolbarSwitchByField() {
    return _.keyBy(this.features.toolbarSwitches, v => v.field)
  }

  @computed get _fieldByName(): { [field: string]: ICatalogFieldConfig } {
    const {vlpDynamicCatalogFields, fieldByWorkspaceIndex} = this

    return {
      ...vlpDynamicCatalogFields,
      ...(fieldByWorkspaceIndex[this.index]?.fields ?? {})
    }
  }

  fieldByName = (field: string) => {
    if (!field || field.trim() === 'all') return null

    const r = this._fieldByName[field]

    if (!r && !_.isEmpty(this._fieldByName)) {
      debugger
      throw new Error(`Unable to find field '${field}'`)
    }

    return r
  }

  // endregion

  //endregion

  // region Derived from the current board

  // region Card View

  @computed get defaultImageRatio(): ImageRatio { return _.first(this.imageRatios).id }

  // @computed get defaultFooterCardView(): CardViewConfig {
  //   const {defaultImageRatio} = this
  //   return {
  //     ir: defaultImageRatio
  //   }
  // }

  get cardView() {
    return this.settings.cardView
  }

  defaultValueForAttribute = computedFn((attributeName: string) => {
    const {caDefByName} = this
    if (!attributeName) return undefined
    const attr = caDefByName[attributeName]
    if (!attr) return undefined
    return attr[`default${attr.type}`]
  }, {name: 'defaultValueForAttribute'})

  getVisibleSpecs = (data) => {
    const {libraryTypes, cv: {cardViewWithAttributes}} = this

    return Object.keys(libraryTypes || {}).filter(library => {
      if (cardViewWithAttributes[library]?.show == true || cardViewWithAttributes[_.trimEnd(library, 's')]?.show == true) {
        const specItem = data && data[library]

        return specItem && specItem.isVisible
      }
    })
  }
  @action cardView_setImageRatio = (imageRatio) => {
    this.settings.imageRatio = imageRatio
    // YFTS-3909 because of isDirty check, the config gets the stale value in first undo.commit() call
  }

  // region card labels
  @action toggleShowCardLabel = () => {
    this.settings.showCardLabels = !this.settings.showCardLabels
  }

  //endregion

  @computed get localLODCatalogItems() {
    if (!this.hasLoadedSettings) return []
    const {settings: {perItem}} = this
    return _.keys(perItem).filter(k => perItem[k].lod)
  }

  @computed get anyLocalLODCatalogItems() {
    return this.localLODCatalogItems.length > 0
  }

  @computed get hasExpandedCatalogItems() {
    if (!this.hasLoadedSettings) return false
    const {groupBy: {hasExpandedCatalogItems}} = this
    if (hasExpandedCatalogItems) {
      return true
    }

    const {settings: {perItem}, catalogItemById} = this
    return _.keys(perItem).some(k => catalogItemById[k])
  }

  @action collapseAllLocalLOD = () => {
    const {settings: {perItem}, localLODCatalogItems, isGroupedView} = this
    localLODCatalogItems.forEach(k => perItem[k].lod = null)

    if (isGroupedView) {
      const {groupBy: {hierarchyGroups}} = this
      hierarchyGroups.forEach(hg => {
        if (hg) {
          hg.groupsInOrder.forEach(g => {
            if (g?.store?.settings) {
              g.store.settings.levelOfDetail = 0
            }
          })

          Array.from(hg.settingsByGroup.values()).forEach(v => {
            if (v.levelOfDetail) {
              v.levelOfDetail = 0
            }
          })
        }
      })
    }
    // If showOnlyInlineCatalogItems is true then it should set to false as there won't be any option available for user after collapse is selected
    if (this.settings.showOnlyInlineCatalogItems) {
      this.settings.showOnlyInlineCatalogItems = false
      this.settings.showOnlyOfflineItems = false
    }
  }

  @computed get sectionIds(): Array<string> {
    const {workspaceConfig} = this
    return _.uniq([workspaceConfig?._id, ...(workspaceConfig?.sections?.filter(s => !s?.viewOptions?.field).map(f => f.id) ?? [])]).filter(_.identity)
  }

  @computed get sectionsForGroupBy() {
    const {fieldBySection, sectionIds, groupBy: {groupingForField}} = this

    return sectionIds.filter(s => _.some(fieldBySection[s], f => groupingForField(f) != null))
  }

  @computed get sectionById(): { [id: string]: ISectionConfig } {
    const r = _.keyBy(this.sections, f => f.id)
    return r
  }

  @computed get sectionByField(): { [id: string]: ISectionConfig } {
    const r = _.keyBy(this.sections, f => f.field)
    return r
  }

  labelForSection = (section: string | ISectionConfig, field?: ICatalogFieldConfig) => {
    const {strings, sectionById, workspaceConfig: {_id: boardId, itemName}} = this

    if (!section || section == boardId) return itemName

    if (_.isString(section)) {
      section = sectionById[section]
    }

    if (section) {
      if (section.label) return section.label
      else if (field) {
        const associatedWorkspace = this.associatedWorkspaceFor(field)
        if (associatedWorkspace) {
          return associatedWorkspace.catalog.labelForSection(section)
        }
      }
      return toHeaderCase(section.id)
    }

    return strings.noValue
  }

  @computed get sections() : Array<ISectionConfig> {
    const {workspaceConfig, features: {sectionOverrides}} = this
    return (workspaceConfig?.sections ?? []).map(s => {
      const o = sectionOverrides[s.id]

      return !o ? s : {...s, ...o}
    })
  }

  @computed get sectionsWithDynamicFields(): Array<ISectionConfig> {
    return (this.workspaceConfig?.sections ?? []).filter(s => s?.viewOptions?.field)
  }

  @computed get hasViewOptionSections() {
    return _.keys(this.fieldBySection).length > 1
  }

  private _hasAddedMissingSectionConfigs = false

  @computed get fieldBySection(): { [section: string]: Array<ICatalogFieldConfig> } {
    if (!this.workspaceConfig) return {}

    const {catalogConfig: {fields}, workspaceConfig: {sections, _id, title}, workspaceConfig} = this

    const result = _.groupBy(fields, f => f.section ?? workspaceConfig._id)

    const missingSectionDefinitions = Object.keys(result).filter(key => key != _id && !(sections || []).find(s => s.id == key))
    if (missingSectionDefinitions.length && !this._hasAddedMissingSectionConfigs) {
      console.info(`${this.debugId} - The following section definitions are missing from the board's configuration`, {missingSectionDefinitions, result})
      false && setTimeout(() => {
        this._hasAddedMissingSectionConfigs = true
        workspaceConfig.sections = [
          ...(sections || []),
          ...missingSectionDefinitions.map(s => {
            const esField = this.findFieldInTree(result[s][0]?.field, true),
              ws = esField ? this.associatedWorkspaceFor(esField) : null

            return ({id: s, workspace: ws?.config, label: ws?.catalog?.itemName ?? (s == _id ? title : _.capitalize(s))})
          })
        ]
      }, 0)
    }
    return result
  }

  hasSection = ({section, fieldName}: { fieldName: string, section?: string }) => {
    if (_.isUndefined(section) || section === 'all') return true
    const {workspaceConfig, _fieldByName, sectionById, sectionDynamicFields} = this

    if (workspaceConfig?.sections?.length < 1) return true

    if (sectionDynamicFields.has(fieldName)) return section == sectionDynamicFields.get(fieldName)

    const sectionConfig = section ? this.sectionById[section] : null,
      f = _fieldByName[fieldName],
      isDefault = !section || workspaceConfig._id == section,
      r = (!f && isDefault)
          || (isDefault && (!f.section || f.section == section))
          || f?.section == section

    //console.log({section, fieldName, r, isDefault, f})
    // console.log({fieldName, section, r, f})
    return r || (sectionConfig?.viewOptions?.field && fieldName.startsWith(`${sectionConfig.path}.${sectionConfig.viewOptions.field}.`))
  }

  /***
   * Dynamic Field Name -> section name
   */
  @computed get sectionDynamicFields() {
    const {sectionValues, sectionById} = this
    return new Map<string, string>(
      _.flatMap(Array.from(sectionValues.entries()).map(([section, values]) => {
        if (sectionById[section]) {
          const {path, viewOptions: {field}} = sectionById[section]
          return values.map(({key}) => [`${path}.${field}.${key}`, section])
        }
        return []
      })))
  }

  @computed get availableCardFieldBySection(): Array<{ section?: ISectionConfig, fields: Array<ICatalogFieldConfig> }> {
    const {availableCardFields, workspaceConfig, sectionById, itemName} = this
    const grouped = _.groupBy(availableCardFields, f => f.section ?? this.index)

    return _.orderBy(_.keys(grouped), k => !k || k === this.catalogConfig?.index ? ` ${itemName}` : (sectionById[k]?.label ?? toHeaderCase(k)))
      .map(sectionId => ({
        section: sectionById[sectionId],
        fields: grouped[sectionId]
      }))
  }

  @computed get availableCatalogFields(): Array<ICatalogFieldConfig> {
    const {workspaces: {workspaceConfig}} = this

    if (!workspaceConfig?.catalog) return []

    const {catalog: {fields}} = workspaceConfig,
      {labelForField, sectionById} = this

    return _.orderBy(
      (fields ?? []).filter(f => f?.field != null && (!f.section || sectionById[f.section]?.visible !== false)),
      f => labelForField(f))
  }

  cardFieldViewOptionsSortFunction = (f: ICatalogFieldConfig) => `${!f.section ? ' ' : this.labelForSection(f.section)}_${f.card?.default === false || f?.card?.default === undefined ? '1' : '0'}_${this.labelForField(f)}`

  @computed get availableCardFields(): Array<ICatalogFieldConfig> {
    const {workspaces: {workspaceConfig}} = this

    if (!workspaceConfig?.catalog) return []

    const {availableCatalogFields, cardFieldViewOptionsSortFunction} = this

    return _.orderBy(
      availableCatalogFields.filter(f => !f.card?.hide && (f.card || (f.def && f.card != false))), cardFieldViewOptionsSortFunction)
  }

  @computed get pdfCardFields(): Array<ICatalogFieldConfig> {
    const {workspaceConfig} = this

    if (!workspaceConfig?.catalog) return []

    const {catalog: {fields}} = workspaceConfig

    return (fields ?? []).filter(f => f?.card?.pdf === true)
  }

  @computed get cardGroupsByType() {
    const {availableCardFields, groupBy: {groupingByName}} = this
    return _.groupBy(availableCardFields.map(f => groupingByName.get(f.field)).filter(_.identity), f => {
      //console.log(f)
      return f?.fieldDefinition?.card?.category || (f?.def ? 'attribute' : 'library')
    })
  }

  @computed get cardFieldsByType() {
    const {availableCardFields} = this
    return _.groupBy(availableCardFields, f => {
      //console.log(f)
      return f?.card?.category || (f?.def ? 'attribute' : 'library')
    })
  }

  //endregion

  @computed get fieldsByLabel() {
    return _.groupBy(this.catalogConfig.fields, f => this._labelForFieldRaw(f))
  }

  labelForFieldValue = (field: ICatalogFieldConfig, val: any) => {
    const {def} = field

    if (def?.type === 'Boolean') {
      return (def.booleanLabels ?
              (val ? def.booleanLabels.trueLabel : def.booleanLabels.falseLabel)
                                : (val ? 'Yes' : 'No'))
    }

    return val ?? this.strings.emptyLabel
  }

  labelFor = (thing: any) => {
    const {strings} = this
    if (thing === null) return this.strings.emptyLabel
    const ldCheck = thing
    if (!_.isObject(ldCheck) && !_.isArray(ldCheck)) {
      return thing
    }

    const {caDefByName} = this
    const definition = thing.def ? thing.def : thing.customAttributeDefinition && caDefByName[thing.customAttributeDefinition.name]

    if (thing.title && (!definition || definition.type != 'Boolean')) {
      return thing.title
    } else if (definition) {
      let result = ''
      switch (definition.type) {
        case 'Boolean':
          const boolVal = thing.boolVal
          result = (definition && definition.booleanLabels ?
                    (boolVal ? definition.booleanLabels.trueLabel : definition.booleanLabels.falseLabel)
                                                           : (boolVal ? 'Yes' : 'No'))
          break
        default:
          throw new Error('???')
      }
      if (_.isUndefined(result)) {
        return _.startCase(UNASSIGNED)
      }
      return !_.isUndefined(result) && !_.isNull(result) && result.toLocaleString()
    }
    return thing?.label ?? thing?.title ?? thing?.name ?? thing?.id ?? strings.emptyLabel
  }

  labelForField = ((field: Partial<ICatalogFieldConfig>, defaultLabel = '???', tense: Tense = 'singular', guaranteeUnique: boolean = false, includePath = false) => this.fnlabelForField(field, defaultLabel, tense, guaranteeUnique, includePath))

  private fnlabelForField = computedFn((field: ICatalogFieldConfig, defaultLabel = 'Unknown', tense: Tense = 'singular', guaranteeUnique: boolean = false, includePath: boolean = false) => {
    const r = this._labelForFieldRaw(field, defaultLabel, tense)

    if (field?.section) {
      // Verify our name doesn't conflict with another field on the page

      const other = this.fieldsByLabel[this._labelForFieldRaw(field, defaultLabel, 'singular')],
        sectionLabel = this.labelForSection(field.section, field)

      if (other?.length > 1) {
        // console.log(`appending ${field.section} to ${r}`, toJS(other, {recurseEverything:true}))

        if (!guaranteeUnique || ! sectionLabel) {
          return r
        } else {
          return `${r} (${pluralize(sectionLabel, 1)})`
        }
      }
    }

    if (includePath) {
      const parent = field.field.substring(0, field.field.lastIndexOf('.'))
      if (parent && parent != field.field) {
        const parentField = this._fieldByName[parent]
        if (parentField) {
          return `${this.fnlabelForField(parentField, defaultLabel, tense, guaranteeUnique, includePath)} > ${r}`
        }
      }
    }

    return r
  }, {keepAlive: true})

  private _labelForFieldRaw(field: ICatalogFieldConfig, defaultLabel = 'Unknown', tense: Tense = 'singular') {
    let r: string
    if (field?.def?.displayName) {
      return field?.def?.displayName[tense]
    } else {
      const v = field?.label || toHeaderCase(_.last(field?.field?.split('.'))) || defaultLabel
      return tense == 'plural' ? pluralize(v) : v
    }

    return r
  }

  labelForLibrary = ({id}: { id: string }, tense: Tense = 'singular', includeWorkspace = true) => {
    const {strings, libraryById} = this,
      library = libraryById[id]

    if (library) {
      return pluralize(`${includeWorkspace && library.workspace && !_.isEmpty(library.where) ? `${library.workspace.title} -> ` : ''}${library.config?.label ?? _.startCase(library.name) ?? strings.noValue}`, tense == 'singular' ? 1 : 2)
    }

    return pluralize(library?.config?.label ?? _.startCase(library?.name) ?? strings.noValue, tense == 'singular' ? 1 : 2)
  }

  labelForFieldName_currentCatalog = (name: string, tense: Tense = 'singular', workspace?: string, guaranteeUnique?: boolean) => {
    const {strings} = this
    if (name == OFFLINE_ID) {
      return tense == 'singular' ? strings.offlineItem : strings.offlineItems
    }

    if (workspace && workspace !== this.index) {
      const w = this.workspaces.byIndexScalar[workspace]
      if (w) {
        const field = w.catalog.fields.find((f: ICatalogFieldConfig) => f.field === name)
        return this.labelForField(field, strings.noValue, tense, guaranteeUnique)
      }
    }

    let f = this._fieldByName[name]
    if (!f) f = this.findFieldInTree(name)
    return this.labelForField(f, strings.noValue, tense, guaranteeUnique)

    //
    // if (!name) {
    //   const {catalogItemName} = this
    //   return tense == 'plural' ? pluralize(catalogItemName) : catalogItemName
    // }
    //
    // const {_fieldByName} = this
    //
    // let r: string
    // if (!_fieldByName[name]) {
    //   r = _.capitalize(name ?? strings.noValue)
    // } else {
    //   const {label, def} = _fieldByName[name]
    //   r = (def?.displayName ? def.displayName[tense] : label) ?? label ?? _.capitalize(name ?? strings.noValue)
    // }
    //
    // return tense == 'plural' && r.toLowerCase() !== 'status' ? pluralize(r) : r
  }

  isFieldEditable = (field: string, getData?: () => any, index?: string) => {
    const {stores, vlp} = this
    if (!vlp) return false

    if (vlp.hierarchy.isMagicHierarchyField(field)) return true

    const ws = this.fieldByWorkspaceIndex[index ?? this.index]
    if (!ws || !ws.fields[field]) return false

    const f = ws.fields[field],
      editable = f.editable ?? f.editor?.editable,
      data = getData ? getData() : null,
      id = data?.id ?? data?.exId ?? data?._id,
      isOffline = id?.startsWith(OFFLINE_ID)

    if (_.isFunction(editable)) {
      return editable({stores, data: getData ? getData() : null, workspace: this.workspaceConfig, isPublished: !isOffline})
    }

    if (_.isUndefined(editable)) {
      // No editable information has be set in config
      // If the field in question is a library on the board, support editable by default

      return (ui.settings.defaultEditable || ws.workspace.config?.catalog?.defaultEditable === true) || (ws.workspace?.config.libraries ?? []).some(l => l.assignTo === field || l.assignTo2?.field === field)
    }

    return editable === true
  }

  @observable createSlider: boolean = false

  mutations: CatalogMutationsStore

  itemCountFor = computedFn((index: string) => {
    const {_workspaceCounts, filteredOfflineItems, filter, offlineItemsByIndex} = this

    if (!_workspaceCounts || _.isUndefined(_workspaceCounts[index])) return undefined

    let itemCount = _workspaceCounts[index]?.doc_count ?? 0

    if (this.index == index) {
      const offlineFilter = filter.groupFor(OFFLINE_ID)
      //console.log(toJS({p, _workspaceCounts, offlineItemsByIndex}, {recurseEverything: true}))
      itemCount = ((offlineFilter?.excluded?.size > 0 && offlineFilter?.excluded.has('online')) || (offlineFilter?.selected?.size > 0 && !offlineFilter.selected.has('online'))) ? 0 : (_workspaceCounts[index]?.doc_count ?? 0)
    }

    if (itemCount === undefined) return undefined

    return itemCount + (this.index == index ? filteredOfflineItems.length : _.size(offlineItemsByIndex[index]))
  }, {name: 'itemCountFor'})

  filterItemsForFilterPerspective = (p: Partial<CatalogPerspectiveConfig>, items: Array<any>) => {
    const {filter, matchCatalogItem} = p,
      {stores} = this

    if (matchCatalogItem) {
      items = items.filter(item => matchCatalogItem(item, {stores}))
    } else if (filter.term) {
      const {term} = filter

      const field = _.keys(term)[0],
        value = term[field]

      items = items.filter(item => {
        return _.get(item, field) === value
      })
    } else {
      console.warn(`Unhandled Pending Item perspective filter: `, toJS(p))
    }

    return items
  }

  itemCountForPerspective = computedFn((p: Partial<CatalogPerspectiveConfig>) => {
    //console.log(`itemCountForPerspective`, p)
    const {index, _workspaceCounts, filteredOfflineItems, filterItemsForFilterPerspective, workspaceConfig: {_id}} = this

    let counts = _workspaceCounts[index]

    if (!counts) return '...'

    let offlineCount = 0

    if (!p.id || p.id == _id) {
      return counts.doc_count
    } else {
      const key = p.id ?? p.field
      if (counts[key]?.doc_count != null) { // nested aggregation
        counts = counts[key]

        if (counts.filtered) {
          counts = counts.filtered
        }
      }

      if (p.filter) {
        offlineCount = filterItemsForFilterPerspective(p, filteredOfflineItems).length
      }

      let r = counts[key]?.value ?? counts?.doc_count ?? ' '

      if (_.isNumber(r)) {
        r += offlineCount
      }

      return r
    }
  }, {name: 'itemCountForPerspective'})

  get itemCreationConfig() {
    return this.catalogConfig?.itemCreation
  }

  @computed get canCreateItem() {
    return !this.isShowingPerspective && this.features.actionButtons?.create !== false && this.isActionButtonEnabled('create')
  }

  @computed get targetsToolbarConfig(): CollectionTargetsToolbarConfig {
    const {catalogConfig, pdfCatalogStore: {isShowingClientPDF}, vlp} = this
    if (!vlp) return null

    const {vlp: {hierarchy: {selected}}, filter, filter: {state}} = this
    return (!filter.hasLoaded || isShowingClientPDF) ? null : _.values(selected).every(v => _.size(v) === 1)
                                                              && catalogConfig?.targetsToolbars?.find(({requirements}) => requirements.every(r => selected[r]?.length === 1 || selected[`${r}s`]?.length === 1 // collection or collections
                                                                                                                                            || filter?.state?.groups[r]?.selected?.size === 1))
  }

  @action getColorValue = (catalogField, color) => {
    const fieldPath = catalogField.field.split('.'),
      _color = tinycolor(color.hex),
      colorType = _.last(fieldPath),
      hsb = _color.toHsv(),
      rgb = _color.toRgb(),
      update = {
        hex: color.hex,
        hsb: {
          brightness: _color.getBrightness(),
          hue: hsb.h,
          saturation: hsb.s * 100
        },
        rgb: {
          red: rgb.r,
          green: rgb.g,
          blue: rgb.b
        }
      }

    let colorVal = update.hex
    switch (colorType) {
      case 'rgb':
        colorVal = catalogField?.def.type == 'Hex' ? `(${color.rgb.r}, ${color.rgb.g}, ${color.rgb.b})` : update.rgb
        break
      case 'hsb':
        colorVal = update.hsb
        break
    }
    return colorVal
  }

  @action onToggleShowTargetsToolbar = () => {
    const {settings} = this
    settings.showTargetsToolbar = !settings.showTargetsToolbar
  }

  // region Navigation to next/previous items

  @computed get isMaximizedCatalogTab() {
    return this.flexLayoutCatalogRoot?.tab?.isMaximized === true
  }

  selectFocusedCatalogItemInRightDetailPaneWhenMaximized = () => autorun(() => {
    const {sidebar: {right}, isMaximizedCatalogTab} = this
    if (isMaximizedCatalogTab && right.open) {
      const {focusedCatalogItemStore} = this
      if (focusedCatalogItemStore) {
        const {id, workspace: ws} = focusedCatalogItemStore

        // Avoid changing the path the user just set via double click
        if (!right.isModal || !right.path.some(v => v.where.some(v1 => v1.value === id))) {
          runInAction(() => {
            right.selectedPathIndex = 0
            right.path = [
              {
                where: [{value: id}]
              }
            ]
          })
        }
      }
    }
  }, {name: `When maximized and the user selects a detail item (can be done through Left/Right nav arrows in details modal popup as well) - change the right sidebar's path to match`})

  /**
   * Only used from the console, don't make computed
   */
  get focusedCatalogItemStore() {
    const {focusedCatalogItemId, componentStores: {cards}} = this
    if (!focusedCatalogItemId) return null
    return cards[focusedCatalogItemId]
  }

  @computed get focusedCatalogItem() {
    const {groupBy, isProductView, focusedCatalogItemId, catalogItemById} = this
    if (!focusedCatalogItemId) return null

    let id = focusedCatalogItemId, group = null
    if (!isProductView) {
      const lastIndexOfDot = focusedCatalogItemId.lastIndexOf('.')

      group = focusedCatalogItemId.substring(0, lastIndexOfDot)
      id = focusedCatalogItemId.substring(lastIndexOfDot + 1)
    }

    if (isOfflineId(id)) {
      return this.offlineItemsMap[id]
    }

    if (isProductView) return catalogItemById[focusedCatalogItemId]

    return _.last<GroupByGrouping>(groupBy.hierarchyGroups).nodes.get(group).productStore.catalogItemsDict[id]
  }

  @computed get focusedIndexInCatalog() {
    const {isProductView, groupBy, sidebar, focusedCatalogItemId, parseIdFromPath} = this
    const id = focusedCatalogItemId ?? (sidebar.right.selectedPathItem?.where?.length == 1 ? sidebar.right.selectedPathItem.where[0].value : null)

    if (id) {
      if (isProductView) {
        return groupBy.catalogItemViewStore?.catalogItemIdsArray?.indexOf(id)
      } else {
        return _.last(groupBy.hierarchyGroups)?.focusedCatalogItemIndex ?? -1
      }
    }

    return -1
  }

  @computed get lastIndexInGroupByCatalog() {
    const hg = _.last(this.groupBy.hierarchyGroups)
    if (hg) {
      const {lastCatalogItemIdInGroup} = hg
      return lastCatalogItemIdInGroup
    }
  }

  @action toggleDetailViewLocation = () => {
    const {settings, settings: {detailLoc}} = this
    settings.detailLoc = detailLoc === 'modal' ? 'sidebar' : 'modal'
  }

  @observable catalogNavDirection: 'left' | 'right' = 'right'

  @computed get canNavigateLeft() {
    const {focusedIndexInCatalog, itemCount, isProductView, focusedCatalogItemId, lastIndexInGroupByCatalog, groupBy: {lastGroupInHierarchy}} = this
    return itemCount > 0 && ((focusedIndexInCatalog > 0 || focusedCatalogItemId === lastIndexInGroupByCatalog) || (!isProductView && lastGroupInHierarchy?.focusedGroup != _.first(lastGroupInHierarchy?.groupsInOrder ?? [])))
  }

  @computed get canNavigateRight() {
    const {focusedIndexInCatalog, focusedCatalogItemId, itemCount, lastIndexInGroupByCatalog} = this,
      {isProductView, groupBy: {catalogItemViewStore, lastGroupInHierarchy}} = this

    return itemCount === 0
           ? false
           : isProductView
             ? !catalogItemViewStore || (!focusedCatalogItemId || focusedIndexInCatalog + 1 < catalogItemViewStore?.catalogItemsArray?.length || catalogItemViewStore?.hasMore)
             : focusedCatalogItemId != null && (focusedCatalogItemId !== lastIndexInGroupByCatalog || lastGroupInHierarchy?.focusedGroup != _.last(lastGroupInHierarchy?.groupsInOrder ?? []))
  }

  @action scrollFocusedItemIntoView = () => {
    const {focusedCatalogItemId: id, groupBy} = this
    runInAction(async () => {
      const {builderCardRefByID} = this
      if (builderCardRefByID.has(id)) {
        // Special logic to deal with sticky headers
        // NVS - removed this due to huge performance issues
        // const behavior: any = groupBy.isProductView ? undefined : actions => {
        // console.log(actions)
        //   actions.forEach(({el, top}) => {
        //     if (el.getAttribute('data-type') == 'CatalogScrollWrapper') {
        //       // console.log(el, top, el.scrollTop)
        //       // const elStyler = styler(el)
        //       // tween({
        //       //   from: el.scrollTop,
        //       //   to: top - (54 * groupBy.hierarchy.length),
        //       //   ease: easing.easeInOut
        //       // }).start(top => elStyler.set('scrollTop', top))
        //     }
        //   })
        // }

        //const scrolled = await scrollIntoView(builderCardRefByID.get(id), {scrollMode: 'if-needed', block: 'start', inline: 'start', duration: 600})
        // // The item can be scrolled off the page due to the sticky headers
        // if (!_.isEmpty(scrolled) && !groupBy.isProductView) {
        //   const {el} = scrolled[scrolled.length - 2]
        //   el.scrollTop -= 62
        // }
      }
    })
  }
  /**
   * Toggles when we switch direction when shift-arrow selecting
   * @private
   */
  @observable private _navReverse = false

  private _navigationWhen: IDisposer
  @action navigateToNextCatalogItem = (event: KeyboardEvent, itemIdArray?: string[]) => {
    const {
      groupBy, focusedIndexInCatalog, focusedCatalogItemId, catalogNavDirection: priorDirection, isProductView, groupBy: {catalogItemViewStore}, selection: {selectedIds},
      settings, settings: {showOnlyInlineCatalogItems, perItem}
    } = this
    if (this.presentation.visible) return

    this._navigationWhen?.()

    // console.trace(`navigateToNextCatalogItem`, {itemIdArray})
    this.catalogNavDirection = 'right'
    let newFocusedId: string = null

    if (isProductView && catalogItemViewStore) {
      let ids = itemIdArray ?? catalogItemViewStore?.catalogItemIdsArray ?? []

      if (focusedIndexInCatalog === -1) {
        newFocusedId = ids[0]
      } else if (focusedIndexInCatalog + 1 < ids.length) {
        newFocusedId = ids[focusedIndexInCatalog + 1]
      } else if (catalogItemViewStore.hasMore) {
        if (!catalogItemViewStore.isLoadingMore) {
          catalogItemViewStore.onPageForward()
        }
        setTimeout(() => this.navigateToNextCatalogItem(event, itemIdArray), 500)
      } else if (ids.length) {
        newFocusedId = ids[0]
      }
    } else if (!isProductView) {
      const {focusedCatalogItemIndex, focusedGroup, groupsInOrder} = groupBy.lastGroupInHierarchy
      if (!focusedGroup && focusedCatalogItemId) {
        when(() => groupBy.lastGroupInHierarchy.focusedGroup != null, () => this.navigateToNextCatalogItem(event, itemIdArray), {name: `When the last group in the hierarchy is focused, navigate automatically to the first catalog item in the group.`})
      } else if (focusedGroup) { // Go to next catalog item in group (or go to next group if we're on the last item in the group)
        const {store: {props: {index: groupIndex}}, productStore} = focusedGroup

        if (!focusedGroup.store.isExpanded) {
          focusedGroup.store.expand()
          when(() => focusedGroup.productStore != null, () => this.navigateToNextCatalogItem(event, itemIdArray), {name: `When expanding a group with catalog items, navigate/focus to the first item in the product panel after the product cards load`})
        } else {
          const {catalogItemIdsArray, totalCount} = productStore

          if (focusedCatalogItemIndex + 1 < totalCount) {
            if (catalogItemIdsArray.length < focusedCatalogItemIndex + 1 && !productStore.isLoadingMore) {
              // Need to load more items
              productStore.timesClicked++
              when(() => !productStore.isLoadingMore, () => this.navigateToNextCatalogItem(event, itemIdArray))
            } else {
              newFocusedId = `${focusedGroup.store.pathLookup}.${catalogItemIdsArray[focusedCatalogItemIndex + 1]}`
            }
          } else {
            // Last item in group, move to next group
            const groupToMoveToIndex = (groupIndex + 1 < groupsInOrder.length) ? groupIndex + 1 : 0,
              groupToMoveTo = groupsInOrder[groupToMoveToIndex]

            groupToMoveTo.store.expand()

            if (!groupToMoveTo.productStore || groupToMoveTo.productStore.isLoadingMore) {
              when(() => groupToMoveTo.productStore && !groupToMoveTo.productStore.isLoadingMore, () => this.navigateToNextCatalogItem(event, itemIdArray))
            } else {
              newFocusedId = `${groupToMoveTo.store.pathLookup}.${groupToMoveTo.productStore.catalogItemIdsArray[0]}`
            }
          }
        }
      }
    }

    if (newFocusedId) {
      if (priorDirection == 'left') {
        this._navReverse = true
      }

      // console.log(`navigateToNextCatalogItem`, {_navReverse: this._navReverse, current: this.focusedCatalogItemId, newFocusedId})

      if (this._navReverse) {
        if (selectedIds.size > 1 && selectedIds.has(focusedCatalogItemId) && selectedIds.has(newFocusedId)) {
          selectedIds.delete(focusedCatalogItemId)
        } else {
          this._navReverse = false
        }
      }
      this.focusedCatalogItemId = newFocusedId

      if (showOnlyInlineCatalogItems && settings.levelOfDetail != 1 && perItem[newFocusedId]?.lod != 1) {
        // Skip this item
        this.navigateToNextCatalogItem(event, itemIdArray)
        return
      }

      if (!event.shiftKey) {
        selectedIds.clear()
        selectedIds.add(newFocusedId)
      } else {
        selectedIds.add(newFocusedId)
      }
      this.scrollFocusedItemIntoView()
    }
  }

  @action navigateToPreviousCatalogItem = (event: KeyboardEvent, itemIdArray?: string[]) => {
    const {
      groupBy, focusedIndexInCatalog, focusedCatalogItemId, settings, settings: {perItem, showOnlyInlineCatalogItems}, catalogNavDirection: priorDirection, isProductView,
      groupBy: {catalogItemViewStore}, selection: {selectedIds}
    } = this

    if (this.presentation.visible) return

    // console.trace(`navigateToPreviousCatalogItem`, {itemIdArray})
    let newFocusedId = null
    this.catalogNavDirection = 'left'
    if (isProductView) {
      if (catalogItemViewStore) {
        const ids = itemIdArray ?? catalogItemViewStore?.catalogItemIdsArray ?? []

        if (focusedIndexInCatalog > 0) {
          newFocusedId = ids[focusedIndexInCatalog - 1]
        } else {
          newFocusedId = _.last(ids)
        }
      }
    } else {
      const {focusedCatalogItemIndex, focusedGroup, groupsInOrder} = groupBy.lastGroupInHierarchy
      if (!focusedGroup && focusedCatalogItemId) {
        when(() => groupBy.lastGroupInHierarchy.focusedGroup != null, () => this.navigateToPreviousCatalogItem(event, itemIdArray))
      } else if (focusedGroup) {
        const {store: {props: {index: groupIndex}}, productStore, productStore: {catalogItemIdsArray, totalCount}} = focusedGroup

        if (focusedCatalogItemIndex > 0) {
          newFocusedId = `${focusedGroup.store.pathLookup}.${catalogItemIdsArray[focusedCatalogItemIndex - 1]}`
        } else {
          // First item in group, move to previous group
          const groupToMoveToIndex = (groupIndex > 0) ? groupIndex - 1 : groupsInOrder.length - 1,
            groupToMoveTo = groupsInOrder[groupToMoveToIndex]

          groupToMoveTo.store.expand()

          if (!groupToMoveTo.productStore || groupToMoveTo.productStore.isLoadingMore) {
            when(() => groupToMoveTo.productStore && !groupToMoveTo.productStore.isLoadingMore, () => this.navigateToPreviousCatalogItem(event, itemIdArray))
          } else {
            newFocusedId = `${groupToMoveTo.store.pathLookup}.${_.last(groupToMoveTo.productStore.catalogItemIdsArray)}`
          }
        }
      }
    }

    if (priorDirection == 'right') {
      this._navReverse = true
    }

    if (this._navReverse) {
      if (selectedIds.size > 1 && selectedIds.has(focusedCatalogItemId) && selectedIds.has(newFocusedId)) {
        selectedIds.delete(focusedCatalogItemId)
      } else {
        this._navReverse = false
      }
    }

    if (newFocusedId) {
      // console.log(`navigateToPreviousCatalogItem`, {_navReverse: this._navReverse, current: this.focusedCatalogItemId, newFocusedId})
      this.focusedCatalogItemId = newFocusedId

      if (showOnlyInlineCatalogItems && settings.levelOfDetail != 1 && perItem[newFocusedId]?.lod != 1) {
        // Skip this item
        this.navigateToPreviousCatalogItem(event, itemIdArray)
        return
      }

      if (!event.shiftKey) {
        selectedIds.clear()
        selectedIds.add(newFocusedId)
      } else {
        selectedIds.add(newFocusedId)
      }

      this.scrollFocusedItemIntoView()
    }
  }

  // endregion

  // region Offline Support (IndexedDB)

  // IndexedDB access via
  @observable.ref static  _offlineItemsDb: OfflineCatalogItemDatabase
  @observable.shallow static _offlineItemsMap: { [id: string]: OfflineItemWrapper } = {}
  get offlineItemsMap() : Record<string, OfflineItemWrapper> { return CatalogStore._offlineItemsMap}

  get offlineItemsDb() { return CatalogStore._offlineItemsDb}

  @computed get isGlobalStore() {
    return this.stores.isGlobalStore || this === globalStores.catalog
  }

  get globalCatalog() { return globalStores.catalog}

  @computed get offlineItemsByIndex(): { [index: string]: { [id: string]: any } } {
    if (!this.isGlobalStore) { return this.globalCatalog.offlineItemsByIndex}

    const {offlineItemsMap} = this,
      byIndex = _.groupBy(_.values(offlineItemsMap).filter(_.identity), i => i.item.index)

    return Object.entries(byIndex).reduce((acc, [index, entries]) => _.assign(acc, {[index]: entries.reduce((acc, v) => ({...acc, [v.item.data._id]: v.item.data}), {})}), {})
  }

  @computed get workspace_offlineItemIds() {
    return _.keys(this.workspace_offlineItemsById)
  }

  @computed get workspace_offlineItems(): Array<any> {
    const {workspace_offlineItemsById} = this
    return _.values(workspace_offlineItemsById)
  }

  @computed get workspace_offlineItemsById() {
    return this.offlineItemsByIndex[this.index] ?? []
  }

  upsertOfflineItem = async ({index, value}: { index: string; value: { _id: string } & any }) => {
    const {offlineItemsMap, offlineItemsDb: {offlineItems, recursivelyRemoveDexieUnserializableFields}} = this,
      {_id} = value,
      newData = recursivelyRemoveDexieUnserializableFields(toJS(value, {recurseEverything: true}))

    if (offlineItemsMap[_id]) {
      // Update
      const existing = offlineItemsMap[_id]

      existing.item.data = newData

      await offlineItems.update(existing.item.id, toJS(existing.item))
      if (this.isDataTableView) this._tableRefreshDataByIds.push(_id)
      return existing.item
    } else {
      // insert
      const offlineItem: IOfflineCatalogItem = {index, data: newData}
      const key = await offlineItems.add(offlineItem)
      offlineItem.id = key
      offlineItemsMap[_id] = new OfflineItemWrapper(this, offlineItem)

      return offlineItem
    }
  }

  getOfflineItem = ({index, _id}: { index: string; _id: string }) => {
    //console.log(index, _id)
    const {offlineItemsByIndex} = this
    const forIndex = offlineItemsByIndex[index]
    if (!forIndex) return {}

    return forIndex[_id] ?? {}
  }

  @observable static offlineItemsLoaded = false

  @action _loadOfflineItemsFromDb = async () => {
    let itemsFromDb = []
    try {
      itemsFromDb = await this.offlineItemsDb.offlineItems.reverse().toArray()
    }
    catch (err) {
      console.error(err)
    }
    runInAction(`Loaded Pending Items from indexed DB`, () => {
      CatalogStore.offlineItemsLoaded = true
      let newItems = {}
      itemsFromDb.forEach(dbItem => {
        newItems[dbItem.data._id] = new OfflineItemWrapper(this, recursivelyRemovePrivateFields(dbItem))
      })

      // const newItems = itemsFromDb.reduce((acc, dbItem) => {
      //   return ({...acc, [dbItem.data._id]: new OfflineItemWrapper(dbItem)})
      // }, {})
      CatalogStore._offlineItemsMap = newItems
      _.values<OfflineItemWrapper>(newItems).forEach(v => v.registerEffects())
    })
  }

  @observable localCatalogItemsStateLoaded = false

  @computed get areAllOfflineCatalogItemsSelected() {
    const {workspace_offlineItemIds, selection: {selectedIds}} = this
    return workspace_offlineItemIds.every(id => selectedIds.has(id))
  }

  @action toggleShowOnlyItemsWithChanges = () => {
    const {settings} = this
    settings.showOnlyItemsWithChanges = !settings.showOnlyItemsWithChanges
  }

  @action cancelAllOfflineChanges = () => {
    const {settings: {perItem}, itemDetailStores} = this

    _.keys(perItem).forEach(id => {
      const store = itemDetailStores[id]
      if (store) {
        store.cancelMutations()
      } else {
        const v = perItem[id]
        v.unsavedChanges = undefined
      }
    })

    this.settings.showOnlyItemsWithChanges = false
  }

  @action saveAllOfflineChanges = () => {
    const {settings: {perItem}, itemDetailStores, idFor} = this
    _.keys(perItem).forEach(id => {
      const store = itemDetailStores[id]
      store?.applyMutations(false)
    })
    this.settings.showOnlyItemsWithChanges = false
  }

  @action toggleShowOnlyOfflineCatalogItems = () => {
    const {strings, settings, selection: {selectedIds}, filter} = this
    settings.showOnlyOfflineItems = !settings.showOnlyOfflineItems

    // YFTS-8516: Commented this when we toggle to Pending Items, then it shows Pending Items with lod 1 in gallery view
    // workspace_offlineItemIds.forEach(item => {
    //   localData[item] = Object.assign({}, localData[item], {lod: settings.showOnlyOfflineItems ? 1 : 0})
    // })

    if (settings.showOnlyOfflineItems) {
      const item = {
        compoundId: OFFLINE_FILTER_VALUE_ID,
        id: OFFLINE_FILTER_VALUE_ID,
        label: _.capitalize(strings.yes),
        name: _.capitalize(strings.yes),
        type: 'Boolean'
      }
      filter.toggleFilter({key: OFFLINE_ID, value: OFFLINE_FILTER_VALUE_ID, item, forceSet: false})
    } else {
      filter.remove(OFFLINE_ID, OFFLINE_FILTER_VALUE_ID)
    }
    selectedIds.clear()
  }

  @action uploadAllOfflineItems = () => {
    const {validOfflineItems: items, createItem: {createObjectInRemote}} = this
    items.forEach(offline => createObjectInRemote(offline))
  }

  /**
   * Pending Items which pass validation checks and could be accepted by the backend
   */
  @computed get validOfflineItems() {
    const {workspace_offlineItemIds: items, offlineItemsByIndex, index, validateCatalogItem} = this
    return items.map(item => {
      const offline = offlineItemsByIndex[index][item]
      if (_.isEmpty(validateCatalogItem(offline))) {
        return offline
      }
    }).filter(_.identity)
  }

  @computed get offlineItemsOfCurrentWorkspace() {
    const {index, offlineItemsByIndex, isGroupedView, settings: {showOnlyInlineCatalogItems}, localLODCatalogItems} = this,
      items = [..._.values(offlineItemsByIndex[index])]

    if (!isGroupedView && showOnlyInlineCatalogItems) {
      return items.reduce((acc, item) => {
        if (localLODCatalogItems.includes(item._id)) {
          acc.push(item)
          return acc
        }
        return acc
      }, [])
    }

    return items
  }

  _filterOfflineItems = computedFn((ignoreFilter: string, offlineItemsOfCurrentWorkspace: any, searchText: string, ignoreOtherPerspectives): Array<{ _id: string } & any> => {
    if (this.isGlobalStore) {
      return offlineItemsOfCurrentWorkspace
    }

    const {filter, stores: {es}} = this
    let items = [...offlineItemsOfCurrentWorkspace]

    if (_.isEmpty(items)) return []

    const {perspective, filterItemsForFilterPerspective} = this
    if (perspective?.filter) {
      items = filterItemsForFilterPerspective(perspective, items)
    }

    const {vlp: {hierarchy: {hasVlpHierarchy, hasHierarchyItemsSelected}}, filter: {filtersWithSelections}, groupBy: {vlpHierarchyGroups, isGroupedInVlpHierarchy}} = this

    if (items.length && (filtersWithSelections.length || hasHierarchyItemsSelected)) {
      const {selection: {filteredCatalogIds}, seasonSelectorFields} = this
      items = _.uniq(items.reduce((acc, item) => {
        const matchesFilters = (filteredCatalogIds.size === 0 || filteredCatalogIds.has(item._id)) && filtersWithSelections.every(({state: {def, selected, excluded, store}}) => {
            if (ignoreFilter === OFFLINE_ID || (ignoreFilter && def?.name == ignoreFilter)) return true

            const {_fieldByName, sectionConfigFor} = this,
              field = _fieldByName[def?.name],
              sectionConfig = sectionConfigFor(field?.section)

            if (!field) {
              if (store?.groupName == OFFLINE_ID) {
                if (excluded.has('offline')) return false

                if (selected.has('offline') || excluded.has('online')) {
                  return true
                }
              } else {
                return true
              }
            } else if (field?.agg === 'exists') {
              const has = (_.has(item, field.field) && _.get(item, field.field) !== '') || (field.orFields ?? []).some(f => _.has(item, f) && _.get(item, f) !== ''),
                r = !selected.size ? true : (selected.has('true') && has && item) ? true : (selected.has('false') && !has && item) ? true : false

              return r && (excluded.size == 0 || !excluded.has(has.toString()))
            } else if (field.def?.type === 'Boolean' && excluded &&
                       excluded.size == 1 && Array.from(excluded.keys()).includes('false' || false)) {
              if (_.get(item, field.field))
                return true
            } else {
              const fieldName = seasonSelectorFields.some(s => s.field == field.field && !field.def?.type) ? `${field.field}.name`   // For things like collection version we group all the various same named collection version IDs under the same 'id' which is actually the name in the filter store, so that's what we need to actually match on here
                                                                                                           : filter.filterFieldFor(field)

              const resultFor = (value) => {
                const val = field.def?.type === 'Boolean' ? (value?.val ? 'true' : 'false')
                                                          : field.def && field.def?.type !== 'String' ? value.val : value.valAsString
                let r = selected.size ? (!_.isNull(val)
                                         ? (selected.has(val) || selected.has(value.valAsString))
                                         : selected.has(FILTER_UNASSIGNED_ID)) : true

                return r && (excluded.size == 0 || !excluded.has(_.isNull(val) ? FILTER_UNASSIGNED_ID : value.valAsString))
              }

              if (sectionConfig?.viewOptions?.field) {
                const nestedData = _.get(item, sectionConfig.path),
                  nestedField = fieldName.substring(sectionConfig.path.length + 1)

                if (_.isArray(nestedData)) {
                  return _.some(nestedData, arrItem => {
                    return resultFor(es.readField(arrItem, nestedField, field.group?.field))
                  })
                }
              }
              const value = es.readField(item, fieldName, field.group?.field)
              return resultFor(value)
            }
            return false
          }),
          matchesHierarchy = seasonSelectorFields?.every((f) => {
            // switch (f.field) {
            //   case 'plan': {
            //     if (selected.plan) {
            //       return _.get(item, 'plan.id') == selected.plan
            //     }
            //     break
            //   }
            //   case 'folder': {
            //     //return _.isEmpty(selected.folders) || selected.folders.every(folder => )
            //     break
            //   }
            //   case 'collection': {
            //     break
            //   }
            //
            //   default: {
            //     const filterState = filter.state.groups[f.field]
            //     if (filterState?.selected?.size) {
            //       const valueId = _.get(item, `${f.field}.id`)
            //       return Array.from(filterState?.selected?.keys()).some(id => id == valueId)
            //     }
            //
            //     break
            //   }
            // }
            //
            // return true

            const SUBCATEGORIES = 'subCategories',
              filterGroup = filter.state.groups[f.field]

            // Generally we let the filters win instead of specialized season selector logic, however there are cases where say 'plan' is a filterable field AND a vlp field
            if (filterGroup && (!hasVlpHierarchy || !_.includes(['plan', SUBCATEGORIES, 'collection'], f.field))) return true

            const {vlp: {hierarchy: {selected}}} = this

            const v = item[f.field === 'folder' ? SUBCATEGORIES : f.field]
            let r = false

            if (!v) {
              r = true
            } else {
              if (f.field === 'plan') {
                r = _.isEmpty(selected.plans) || _.includes(selected.plans, v.id ?? v.exId)
              } else if (f.field === 'collection') {
                r = _.isEmpty(selected.collections) || _.includes(selected.collections, v.id ?? v.exId)
              } else if (f.field === 'folder') {
                if (!selected.folders || selected.folders?.length === 0 || v.length === 0) { r = true } else {

                  // Is there a matching hierarchy in selected folders that matches all the selected items in this Pending Item?
                  const foldersForOfflineItem = v.map(sc => sc.id)

                  r = (selected.folders ?? []).some(h => foldersForOfflineItem.every(id => _.includes(h, id)) || (h ?? []).every(id => _.includes(foldersForOfflineItem, id)))
                }
              } else {
                if (f.align == 'right') return false // will be initialized later as filter dropdown

                console.warn(`${this.debugId} - Unhandled filter Pending Item case:  '${f.field}', value:`, toJS(v))
                return false
              }
            }

            // !r && console.debug(`Filtered out Pending Item`, {item, f})
            return r
          })
        acc.push(matchesFilters && matchesHierarchy ? item : null)
        return acc
      }, [])).filter(_.identity)
    }

    if (hasVlpHierarchy && isGroupedInVlpHierarchy) {
      // Remove anyone put into a folder that doesn't match our available levels
      vlpHierarchyGroups.forEach(hg => {
        if (hg.vlpType?.type === 'folder') {
          const {vlpType: {folderDepth}} = hg
          items = items.filter(item => {
            const folders = _.get(item, VLP_SUBCATEGORIES)
            return _.isEmpty(folders) || folders.length > folderDepth
          })
        }
      })
    }
    // Verify that searchText is matched as well
    const {searchFieldsEs} = this
    if (searchText && !_.isEmpty(searchFieldsEs)) {
      const regex = new RegExp(es.escapeString(searchText).trim(), 'i')

      items = items.filter(item => searchFieldsEs.some(field => {
        const value = es.readField(item, field.replace('.text', ''))
        if (value?.valAsString) {
          return value.valAsString.match(regex)
        }
        return false
      }))
    }

    return items
  }, {keepAlive: true})

  /**
   * The user can click 'only inline expanded items' and filter out
   */
  @computed get filteredOfflineItems(): Array<{ _id: string } & any> {
    const {offlineItemsOfCurrentWorkspace, settings: {activeSearchText}} = this
    return this._filterOfflineItems(null, offlineItemsOfCurrentWorkspace, activeSearchText, false)
  }

  @computed get filteredOfflineItemsIgnorePerspectives(): Array<{ _id: string } & any> {
    const {offlineItemsOfCurrentWorkspace, settings: {activeSearchText}} = this
    return this._filterOfflineItems(null, offlineItemsOfCurrentWorkspace, activeSearchText, true)
  }

  /*@action toggleShowOfflineItems = () => {
    const {showOfflineItems, offlineItemsForCatalog, selection: {selectedIds}} = this
    offlineItemsForCatalog.forEach(id => showOfflineItems ? selectedIds.delete(id) : selectedIds.add(id))
    this.showOfflineItems = !showOfflineItems
  }*/

  @action navigateToFocusedCatalogItemSeason = () => {
    console.log(`${this.debugId} - nyi navigate to focused season`)
  }

  @action toggleLocalLOD = (id: string, defaultLod?: number) => {
    const {settings: {perItem, levelOfDetail}} = this, localLOD = perItem[id]?.lod ?? undefined
    perItem[id] = _.merge(
      {},
      {
        lod: localLOD === undefined
             ? (defaultLod ?? levelOfDetail !== 1 ? 1 : 0)
             : localLOD === 0 ? 1 : 0
      })
  }

  @action onCycleLevelOfDetail = () => {
    const {settings, groupBy: {hierarchy, groupingByName}} = this
    settings.levelOfDetail++
    if (settings.levelOfDetail > 1) { settings.levelOfDetail = 0}

    for (let i = 0; i < hierarchy.length; i++) {
      const {nodes, setLevelOfDetail} = groupingByName.get(hierarchy[i])
      if (nodes.size) {
        Array.from(nodes.keys()).forEach(k => setLevelOfDetail(k, settings.levelOfDetail))
        for (let j = i + 1; j < hierarchy.length; j++) {
          const {nodes, setLevelOfDetail} = groupingByName.get(hierarchy[j])
          Array.from(nodes.keys()).forEach(k => setLevelOfDetail(k, settings.levelOfDetail))
        }
        break
      }
    }
  }

  // region Custom View Load

  @computed get fieldsByCustomView(): { [customViewId: string]: { [fieldName: string]: ICatalogFieldConfig } } {
    const {catalogConfig: {fields}, workspaceConfig: {customViews}} = this
    return _.merge(
      // Make sure to populate the dict even if no fields are set
      (customViews ?? []).reduce((acc, cv, i) => _.assign(acc, {[cv._id ?? i.toString()]: {}}), {}),

      (fields ?? []).reduce((acc, field) => _.merge(
        acc,
        (field.card?.customViews ?? []).reduce((acc, cv) => _.assign(acc, {[cv]: {[field.field]: field}}), {})
      ), {}) as any
    )
  }

  @action loadCustomViewFields = (view: IWorkspacePreset) => {
    const {fieldsByCustomView, cv: {cardView_clear, cardView}, cv} = this

    if (view) {
      const fields = fieldsByCustomView[view._id]
      cardView_clear()
      _.keys(fields).forEach(f => _.assign(cardView[f], {show: true}))

      if (view.catalog?.fields) {
        view.catalog.fields.forEach(f => _.assign(cardView[f], {show: true}))
      }
    }
  }

  // endregion

  /**
   * Produce the complete set of images suitable for a given image source
   * This can be multiple images spanning multiple children item in an array off of the main catalog item such as style.colorways.images
   * @param data
   * @param imageSource
   * @param selectedImageSource
   */
  public imagesForCatalogItem = computedFn(({data, imageSource, selectedImageSource, takePer, workspace}: { data: any, takePer?: number, imageSource?: string, workspace?: Partial<WorkspaceConfig>, selectedImageSource?: CatalogImageSourceConfig }) => {
    //selectedImageSource = this.selectedImageSource // workspace?.catalog?.imageSources ? _.first(workspace.catalog.imageSources) : (selectedImageSource ?? this.imageSource)

    const fields = (!workspace || workspace._id === this.workspace?.id ? this.selectedImageSourceFields : workspace?.catalog?.imageSources ? workspace.catalog.imageSources.map(is => is.field) : null) ?? ['images'], //imageSource ? [imageSource] : (selectedImageSource.fields ?? (selectedImageSource.field ? [selectedImageSource.field] : ['images'])),
      {stores: {es}} = this

    // console.log({data: toJS(data), fields: toJS(fields), workspace: workspace?._id, imageSource, selectedImageSource})

    let r = _.flatMap(
      (fields ?? []).reduce((acc, field) => {
        const p = this.pathToFieldName(field),
          fk = this.findFieldInTree(field, true),
          ws = this.associatedWorkspaceFor(fk),
          autoTransparency = ws?.catalog?.features?.autoTransparency

        let images: string | Array<ImageRecord> = es.readField(data, field, null, {takePer: ws?.catalog?.features?.card?.maxStackImages ?? 999}).val ?? []

        if (_.isString(images)) {
          images = [{exId: images, autoTransparency}]
        }

        if (!_.isArray(images)) {
          console.error(`images expected to be an array - YFTS-9248`, {images, data, imageSource, selectedImageSource, fields})
          return []
        }
        if (p.length) {
          images = images.filter(_.identity).map(img => {
            if (_.isString(img)) {
              img = {exId: img, autoTransparency}
            }

            const __path = _.last(p).field,
              __parent = img.__parent ?? _.get(data, __path)

            return {...img, __path, __parent, autoTransparency}
          })
        }

        // logJS({p, field, images})
        return [...acc, ...images]
      }, [])).filter(_.identity)

    // let r = imageSource ? es.readField(data, imageSource).val
    //                     : _.flatMap(selectedImageSource.fields, f => es.readField(data, f ?? 'images').val).filter(_.identity)
    //
    // // Add parent data to the images
    // selectedImageSource.fields.filter(f => f.endsWith('.images')).forEach(f => {
    //   const parentId = catalog.idFor(_.get(data, f.replace('.images', '')))
    //   r
    // })

    return r
  })

  idFor = (data: any, prefixWithParentId = true) => {
    if (!data) { return null }

    // Pending Item
    if (data.item?.data?._id) {
      return data.item.data._id
    }
    if (data.item?._id) {
      return data.item?._id
    }
    let id = data?._id ?? data?.id ?? data?.exId

    if (id && prefixWithParentId && data.__parent && data.__parent != id) {
      const parentId = this.idFor(data.__parent)
      return parentId ? `${parentId}${CATALOG_ID_SEPARATOR}${id}` : id
    } else {
      if (data.exId) {
        id = data.exId
      }

      return id
    }
  }

  /**
   * Which builder card is toggled to show its popover details
   */
  @observable hoverPopoverItemId: string
  @action hideCatalogPopover = () => {
    // console.trace(`hideCatalogPopover`)
    this.hoverPopoverItemId = null
  }

  // private _hideHoverPopoverTimeout
  //
  // @action cancelHideHoverPopover() {
  //   this._hideHoverPopoverTimeout && clearTimeout(this._hideHoverPopoverTimeout)
  // }
  //
  // @action waitHideHoverPopover() {
  //   const {hoverPopoverItemId} = this
  //   this.cancelHideHoverPopover()
  //   this._hideHoverPopoverTimeout = setTimeout(() => {
  //     if (hoverPopoverItemId == this.hoverPopoverItemId) {
  //       this.hoverPopoverItemId = null
  //     }
  //   }, 1000)
  // }

  @observable.ref activeDragCardStore: BuilderCardStore

  labelFor_workspace = (field: string, workspace: Partial<WorkspaceConfig>, nullValue: string) => {
    const fieldDef = this.fieldForWorkspace(field, workspace)
    return fieldDef?.label ?? fieldDef?.def?.displayName?.singular ?? nullValue ?? this.strings.noValue
  }

  calculateRollup = ({rollupDef, quantity, data, spec, bomFieldsByName}: { rollupDef: BillOfMaterialsRollupConfig, data, quantity: number, spec: any, bomFieldsByName: { [field: string]: any } }) => {
    const {localFields, specFields, rollup, label} = rollupDef,
      fields = [...(localFields ?? []), ...(specFields ?? [])],
      fieldValues = {
        ...(localFields ?? []).reduce((acc, field) => _.assign(acc, {[field]: _.get(data, field)}), {}),
        ...(specFields ?? []).reduce((acc, field) => _.assign(acc, {[field]: bomFieldsByName[field]}), {})
      },
      missingFieldValues = (fields ?? []).filter(k => fieldValues[k] === undefined || fieldValues[k] === null),
      rollupResult = (!spec || !rollup || !_.isEmpty(missingFieldValues)) ? null : rollup(fieldValues, quantity)

    if (rollupResult && typeof (rollupResult) != 'number') { throw new Error(`Rollup expression '${label}; is supposed to return a number, instead it returned a ${typeof (rollupResult)}`)}
    return {def: rollupDef, result: rollupResult, missingFieldValues, fieldValues, quantity}
  }

  fieldForWorkspace = (field: string, workspace: Partial<WorkspaceConfig>) => {
    if (!workspace) return this._fieldByName[field]

    const ws = this.workspaces.wrapperById[workspace._id]

    return ws.catalog._fieldByName[field]
  }

  isTextField = (fieldName: string, workspace?: Partial<WorkspaceConfig>): boolean => {
    let field = !workspace ? this._fieldByName[fieldName] : workspace.catalog?.fields?.find(f => f.field == fieldName)
    return !field?.def || field?.def?.type == 'String'
  }

  @computed get workspaceRenderLibraryTab() {
    if (!this.workspaceConfig) return false
    const {features: {actionButtons}, stores} = this
    if (_.isFunction(actionButtons?.shouldRenderControlTab)) {
      return actionButtons.shouldRenderControlTab({stores, data: null, workspace: this.workspaceConfig})
    }
    return actionButtons?.shouldRenderControlTab
  }

  @computed get shouldRenderLibraryTab() {
    const {workspaceConfig, assignableFields, workspaceRenderLibraryTab} = this
    return workspaceRenderLibraryTab !== false && (!_.isEmpty(workspaceConfig?.libraries) || !_.isEmpty(assignableFields))
  }

  findConstraintRelatedField = (f: ICatalogFieldConfig): { relatedField?: ICatalogFieldConfig, relatedProperty?: string } => {
    const path = f.constraint?.from.split('.')
    let relatedField = null, relatedProperty
    for (let i = 0; i < path.length; i++) {
      const p = path.slice(0, i + 1).join('.')
      relatedField = this._fieldByName[p]

      if (relatedField) {
        relatedProperty = path.slice(i + 1).join('.')
        break
      }
    }
    return {relatedField, relatedProperty}
  }

  get libraryByIndex() { return _.keyBy(this.libraries, l => l.index) }

  @observable.shallow libraryItemsByIndex: { [esIndex: string]: ObservableMap<string, any> } = {}
  registerLibraryItems = ({library: {index}, items}: { library: LibraryItemConfig; items: Array<any> }) => {
    const {libraryItemsByIndex, idFor} = this
    if (!libraryItemsByIndex[index]) {
      libraryItemsByIndex[index] = observable.map<string, any>({}, {deep: false})
    }

    (items ?? []).forEach(v => libraryItemsByIndex[index].set(idFor(v), v))
  }

  getLibraryItem = async ({index, id}: { index: 'images' | 'plain-text' | string; id: string | 'EXTERNAL' }) => {
    const {libraryItemsByIndex, stores: {es}} = this
    let item = libraryItemsByIndex[index]?.get(id)

    if (!item) {
      // Could have been drag and drop from other window or it could be a library item we haven't scrolled to yet
      return await es.get({index, id})
    }

    return item
  }

  findFieldInTree = (fieldName: string, onlyMatchFKs = false) => this._findFieldInTree(fieldName, onlyMatchFKs)

  _findFieldInTree = computedFn((fieldName: string, onlyMatchFKs: boolean) => {
    if (!fieldName) return null

    const {_fieldByName} = this

    while (fieldName && fieldName.length && (!_fieldByName[fieldName] || _fieldByName[fieldName]?.agg || (!_fieldByName[fieldName]?.esIndex && onlyMatchFKs))) {
      fieldName = fieldName.substring(0, fieldName.lastIndexOf('.'))
    }

    return _fieldByName[fieldName]
  })

  isSeasonSelectorField = (group: string) => {
    return this.seasonSelectorFields.some(f => f.field == group)
  }

  @computed get ignoreSeasonSelectorRightFields() {
    return this.seasonSelectorFields.filter(f => f.align == 'right').reduce((acc, f) => _.assign(acc, {[f.field]: true}), {})
  }

  groupOfflineItems = (fieldName: string, ignoreSelfFilter = false) => {
    const {_filterOfflineItems, _fieldByName, offlineItemsOfCurrentWorkspace, sectionConfigFor, settings: {activeSearchText}} = this,
      field = _fieldByName[fieldName],
      esField = this.esFieldFor_byField(fieldName, true),
      hasSection = sectionConfigFor(field?.section)?.viewOptions?.field,
      filteredOfflineItems = _filterOfflineItems(ignoreSelfFilter ? fieldName : null, offlineItemsOfCurrentWorkspace, activeSearchText, true)

    let groups: any = {}
    if (!!hasSection) {
      const offlineObjects = {}

      _.forEach(filteredOfflineItems, offline => {
        const val = _.get(offline, field.section),
          groupValue = _.groupBy(val, fieldName.replace(`${field.section}.`, ''))
        _.forEach(Object.keys(groupValue), key => {
          if (!offlineObjects[key]) {
            offlineObjects[key] = []
          }
          offlineObjects[key].push(offline)
        })

        groups = {...groups, ...offlineObjects}
      })
    } else {
      groups = _.groupBy(filteredOfflineItems, g => {
        const isExists = field?.agg === 'exists',
          d = isExists ? !_.isEmpty(_.get(g, fieldName)) : (_.get(g, esField) || _.get(g, fieldName))
        return d === null || d === undefined ? (field?.def?.type === 'Boolean' ? 'false' : UNASSIGNED)
                                             : _.isObject(d) ? _.isArray(d) ? (d.length ? d[0]?.name?.toString() || d[0]?.toString() : UNASSIGNED) : d?.['name']?.toString() : d.toString()
      })
    }
    return filteredOfflineItems.length ? groups : {}
  }

  @action toggleShowInlineItemsFirst = () => {
    const {settings} = this
    settings.showInlineCatalogItemsFirst = !settings.showInlineCatalogItemsFirst
    if (settings.showInlineCatalogItemsFirst) {
      settings.showOnlyInlineCatalogItems = false
    }
  }

  @action toggleShowOnlyInlineCatalogItems = () => {
    const {settings} = this
    settings.showOnlyInlineCatalogItems = !settings.showOnlyInlineCatalogItems
    if (settings.showOnlyInlineCatalogItems) {
      settings.showInlineCatalogItemsFirst = false
    }
  }

  /**
   * Specialized loading state that can be set deeper in the hierarchy depending on the use case
   */
  @observable loadingState: { loading?: boolean, loadingMessage?: string, previousMessage?: string } = {loading: false}

  @observable _isFirstLoad = true

  @computed get hasFinishedFirstLoad() {
    const {isCatalogStores} = this
    return !this._isFirstLoad &&
           (!isCatalogStores || _.values(this.seasonSelectors).every(ss => !(ss instanceof SeasonSelectorFilterDropdownStore) || !ss.filterGroupStore?.initialLoad))
  }

  @computed get isActiveWorkspace() {
    return this.workspaceConfig?._id == globalStores.workspaces.current
  }

  private loadingValues: { [id: string]: { start?: number, end?: number } } = {}

  traceLoadingValue = (id: string, b: boolean, text?: string) => {
    if (!LOG_GLOBAL_CATALOG_LOAD) {
      return
    }

    const {loadingValues} = this
    if (!b) {
      if (!loadingValues[id]?.start) {
        LOG_GLOBAL_CATALOG_LOAD && console.debug(`${this.debugId} - [${id}] Start loading... ${text ? ` - ${text}` : ''}`)
        loadingValues[id] = Object.assign({}, loadingValues[id], {start: performance.now()})
      }
    } else {
      const {start} = loadingValues[id] ?? {}

      if (!start) {
        // console.debug(`[${id}] already done (${text})`)
      } else {
        LOG_GLOBAL_CATALOG_LOAD && console.debug(`${this.debugId} - [${id}] took ${((performance.now() - start) / 1000).toFixed(2)}s`)
        loadingValues[id].start = null
      }
    }
  }

  @computed get seasonsToLoad() {
    const {hasLoadedConfig, seasonSelectorFields, hasVlpHierarchy} = this
    if (!hasLoadedConfig) return null

    return seasonSelectorFields.filter(s => !s.hideInCatalog)
  }

  @computed get hasLoadedSeasons() {
    //_.values(seasonSelectors).some(v => v.isLoadingFromC8 || v.filterGroupStore?.initialLoad)
    const {seasonsToLoad, seasonSelectorsCatalog : ss, hasInitializedSeasonStores} = this
    if (!hasInitializedSeasonStores) return false

    return _.isEmpty(seasonsToLoad) || (_.size(ss) === seasonsToLoad.length && _.values(ss).every(v => !v.isLoadingFromC8 && v.filterGroupStore?.initialLoad === false))
  }

  @computed get hasInitializedSeasonStores() {
    //_.values(seasonSelectors).some(v => v.isLoadingFromC8 || v.filterGroupStore?.initialLoad)
    const {seasonSelectorFields, seasonSelectorsCatalog: ss} = this
    if (!_.isEmpty(seasonSelectorFields) && _.isEmpty(ss)) return true

    return _.values(ss).every(v => !v.isLoadingFromC8 && v.filterGroupStore != null)
  }

  get offlineItemsLoaded() { return CatalogStore.offlineItemsLoaded}

  @computed get __whyLoading() {
    const {
      c8, hasLoadedConfig, loadingState, sidebar, groupBy, vlp, switchingWorkspaces, offlineItemsLoaded, hasLoadedSeasons, hasVlpHierarchy,
      stores: {isInlineDetailsStore, isFilterLibrariesStore},
      filter, loadingCounts, itemCount, mutations, presets: {hasLoadedPresets}
    } = this
    return {
      hasLoadedConfig,
      loadingState: toJS(loadingState),
      offlineItemsLoaded,
      groupByInitialized:
      groupBy.initialized,
      filter: {hasLoaded: filter.hasLoaded, hasFilterTerms: filter.hasFilterTerms},
      switchingWorkspaces,
      hasLoadedSeasons,
      countLoading: {
        loadingCounts,
        itemCount,
        isInlineDetailsStore,
        isFilterLibrariesStore
      },
      hasLoadedPresets,
      vlp: {
        hasVlpHierarchy,
        isRefreshingSeasons: vlp?.isRefreshingSeasons,
        hasLoadedHierarchy: vlp?.hierarchy?.hasLoadedHierarchy
      }, mutations: {
        loadingCatalogTag: mutations?.loadingCatalogTag
      },
      autoFocusDetailItem: sidebar.right.autoFocusDetailItem,
      c8: {
        hasLoadedEnumValues: c8.hasLoadedEnumValues
      }
    }
  }

  /**
   * Composite catalog loading state as we need certain things before we can do our first real render of system data
   */
  @computed get loading() {
    const {stores, filter, workspaceConfig, traceLoadingValue} = this

    try {
      LOG_GLOBAL_CATALOG_LOAD && traceLoadingValue('config', workspaceConfig != null)

      if (!workspaceConfig) {
        return true
      }

      if (!stores.tags?.hasLoadedTags) return true

      const {loadingState} = this
      LOG_GLOBAL_CATALOG_LOAD && traceLoadingValue('loading', !loadingState.loading, `Loading State '${loadingState.loadingMessage}'`)
      if (loadingState.loading) {
        return true
      }

      const {c8, isGlobalStore, dialogs, isCatalogStores, sidebar, groupBy, vlp, switchingWorkspaces, offlineItemsLoaded, loadingCounts, itemCount, mutations, presets: {hasLoadedPresets}} = this
      let r = !workspaceConfig || loadingState.loading || !offlineItemsLoaded

      if (!isGlobalStore && isCatalogStores) {
        r = r || !groupBy.initialized || (!filter.hasLoaded && filter.hasFilterTerms) || switchingWorkspaces || (loadingCounts && !itemCount) || !hasLoadedPresets
            //|| !c8.hasLoadedHierarchy
            || vlp.isRefreshingSeasons || mutations.loadingCatalogTag || sidebar.right.autoFocusDetailItem || !c8.hasLoadedEnumValues
      }


      LOG_GLOBAL_CATALOG_LOAD && traceLoadingValue('groupBy', groupBy.initialized)
      LOG_GLOBAL_CATALOG_LOAD && traceLoadingValue('filter', !filter.hasFilterTerms || filter.hasLoaded)
      LOG_GLOBAL_CATALOG_LOAD && traceLoadingValue('counts', !loadingCounts || itemCount)
      LOG_GLOBAL_CATALOG_LOAD && traceLoadingValue('c8', c8.hasLoadedHierarchy)
      LOG_GLOBAL_CATALOG_LOAD && traceLoadingValue('c8', c8.hasLoadedEnumValues)

      // console.log({
      //   catalogLoading: r,
      //   loadingState: loadingState.loading, groupBy_initialized: groupBy.initialized,
      //   filter_hasLoaded: filter.hasLoaded, switchingWorkspaces, offlineItemsLoaded, loadingCounts, itemCount,
      //   hasLoadedC8Hierarchy: c8.hasLoadedHierarchy
      // })

      return r
    } finally {
    }
  }

  @action updateLoadingState({loadingMessage, loading}: { loadingMessage?: any; loading: boolean }) {
    LOG_GLOBAL_CATALOG_LOAD && console.debug(`updateLoadingState`, {loading, loadingMessage})
    const {loadingState} = this
    if (loading) {
      if (loadingMessage) {
        loadingState.previousMessage = loadingState.loadingMessage
        loadingState.loadingMessage = loadingMessage
      }
      loadingState.loading = true
    } else {
      loadingState.loading = false
      if (loadingState.previousMessage) {
        loadingState.loadingMessage = loadingState.previousMessage
        loadingState.previousMessage = null
      }
    }
  }

  @computed get exportConfig(): WorkspaceExportConfig {
    if (this.catalogConfig.export === false || deployments?.deploymentConfig?.settings?.defaults?.catalog?.export === false) {
      return {}
    }
    return _.merge(
      {
        images: {
          filePrefix: 'VLP_Export_Images_',
          enabled: true,
          exportType: 'images',
          modal: {
            modalTitle: 'Export to Images',
            exportOptions: [
              {
                name: 'Small',
                value: 'Thumbnail',
                default: true
              }, {
                name: 'Medium',
                value: 'SmallImage',
                default: false
              }, {
                name: 'Large',
                value: 'Viewable',
                default: false
              }
            ]
          },
          helperParams: {defaultExportFields: [{field: 'images', label: 'Images', type: 'image'}], nameFields: ['name']}
        },
        excel: {
          filePrefix: 'VLP_Export_Excel_',
          enabled: true,
          exportType: 'excel',
          modal: {
            modalTitle: 'Export to Excel',
            exportOptions: [
              {
                name: 'Images',
                value: 'includeImage',
                default: false
              }
            ]
          },
          helperParams: {
            image: {include: false, height: 50, width: 50, fields: [], label: 'Image', type: 'image'},
            defaultExportFields: [{field: 'exCreatedAt', label: 'Date Created', type: 'DateTime'}, {field: 'exLastSync', label: 'Date Modified', type: 'DateTime'}]
          }
        }
      } as WorkspaceExportConfig,
      deployments?.deploymentConfig?.settings?.defaults?.catalog?.export ?? {},
      this.catalogConfig.export ?? {})
  }

  @computed get features(): ICatalogFeaturesConfig {
    const defaultCardSize = 150

    return _.merge(
      {
        createOneItemPerImage: true,
        library: {},
        actionButtons: {create: false, duplicate: false, delete: false, uploadImages: false, toggleActive: false, changeColor: false},
        card: {
          alignSelf: 'flex-start', objectFit: 'contain', imagePadding: '0', cardSizes: {
            lodMultiplier: 2,
            square: {aspectRatio: 1, size: defaultCardSize},
            landscape: {aspectRatio: 16 / 9, size: defaultCardSize},
            portrait: {aspectRatio: 1.333, size: defaultCardSize}
          }
        },
        sectionOverrides: {},
        changelog: {}
      } as ICatalogFeaturesConfig,
      deployments?.deploymentConfig?.settings?.defaults?.catalog?.features,
      this.catalogConfig?.features ?? {}
    )
  }

  isDrilldown = (idWithPath: string) => {
    return false
  }

  _getDynamicSectionValues = async () => {
    if (!authStore.userIsAuthenticated) return

    const {sectionById, stores: {es}} = this
    await Promise.all(
      _.values(sectionById)
        .filter(s => s.viewOptions?.field && s.visible !== false)
        .map(async section => {
          const {aggregations: {nested: {fields}}} = await es.search({
            index: this.index,
            meta: `_getDynamicSectionValues(${section.path})`,
            body: {
              size: 0,
              aggs: {
                nested: {
                  nested: {
                    path: section.path
                  },
                  aggs: {
                    fields: {
                      terms: {
                        size: 10000,
                        field: `${section.path ? `${section.path}.` : ''}${section.viewOptions.field}`
                      }
                    }
                  }
                }
              }
            }
          }, {log: {query: false, result: false}})

          const buckets = fields?.buckets?.filter(b => b?.key) ?? []

          runInAction(`Save dynamic section values (like secondaries) for ${section.path}`, () => {
            this.sectionValues.set(section.id, buckets)
          })
          return buckets
        }))
  }

  sectionConfigFor = (section: string): ISectionConfig => {
    return !section ? null : this.sectionById[section]
  }
  sectionConfigForField = (field: string): ISectionConfig => {
    return !field ? null : this.sectionByField[field]
  }

  @observable sectionValues = observable.map<string, Array<{ key, doc_count }>>({}, {deep: false})

  sectionValuesFor = (section: string) => {
    if (!section) return undefined
    const {sectionConfigFor, sectionValues} = this,
      sectionConfig = sectionConfigFor(section)

    if (!sectionConfig?.viewOptions?.field) return undefined

    return sectionValues.get(section) ?? null
  }

  isNestedField = (field: ICatalogFieldConfig) => {
    if (field.esNested) return true
    const section = this.sectionConfigFor(field.section)
    return section?.viewOptions?.field != null
  }

  booleanLabelFor(def: CustomAttributeDefinition, maybeBool: any) {
    const b = maybeBool === true || maybeBool === 'true'

    const {strings} = this
    if (def?.booleanLabels) {
      if (b) return def.booleanLabels.trueLabel ?? strings.yes
      if (!b) return def.booleanLabels.falseLabel ?? strings.no
    }

    return b ? strings.yes : strings.no
  }

  @observable viewOptionIsDragging = false

  /**
   * Some libraries have their own built in where clauses that need to be added to the regular filter terms
   **/
  whereForLibrary = (l: LibraryItemConfig, includeDeleteItemsFilter = true) => {
    if (!l) return []

    const {stores: {es}} = this
    return [
      ...(l.where ?? []),
      ...(l.whereDynamic ? removeUndefinedsNested(l.whereDynamic(this.stores)) : [])
      //includeDeleteItemsFilter && es.filterForNonDeletedItems
    ].filter(_.identity)
  }

  itemDetailStores = observable.map<string, CatalogItemDetailsStore>({}, {deep: false})

  builderCardStoreFor = (idWithPath: string) => {
    return this.componentStores.cards[idWithPath]
  }

  tableDragDropStores = observable.map<string, DragDropEventHandlers>({}, {deep: false})

  @action tableDragDropStoreFor = (id: string, def: IDragDropElement): DragDropEventHandlers => {
    const {tableDragDropStores} = this

    if (!tableDragDropStores.get(id)) {
      const dragDropStore = new DragDropEventHandlers(this, def)
      tableDragDropStores.set(id, dragDropStore)
    }

    return tableDragDropStores.get(id)
  }

  /**
   * TODO - this method is very expensive and clones objects and diffs them, switch to something like immer instead to track original vs changes
   * @param id
   * @param originalData
   * @param index
   */
  @action itemDetailStoreFor = (id: string, originalData: any, index?: string): CatalogItemDetailsStore => {
    const {itemDetailStores, settings: {perItem}} = this,
      localData = perItem[id]

    if (!itemDetailStores.has(id)) {
      const store = new CatalogItemDetailsStore(this, index ?? this.index, false)
      this._toDispose.push(store.registerEffects())

      store.replaceData(originalData)

      // Apply any saved diff

      const diff = localData?.unsavedChanges
      if (diff && store.data) {
        try {
          jsondiff.patch(store.data, diff)
        } catch (err) {
          console.error(err)
          debugger
        }
      }

      itemDetailStores.set(id, store)
    } else {
      // Did the outside data change but we have no outstanding changes?
      const store = itemDetailStores.get(id)
      store.replaceData(originalData)

      // if (originalData && !_.isEqual(store.trueOriginalData, originalData)) {
      //   if (!store.isDirty) {
      //     store.replaceData(originalData)
      //   } else if (!_.isEqual(store.trueOriginalData, originalData)) {
      //     // For example the view options may have just changed
      //     // We have our own changes in data but we need to pick up the new fields too
      //     const merged = _.cloneDeep({...originalData, ...store.data})
      //
      //     //console.log(toJS(originalData), toJS(store.data), merged)
      //     store._data = merged
      //     store._originalData = _.cloneDeep(originalData)
      //     store.trueOriginalData = store._originalData
      //   }
      // }
    }

    const store = itemDetailStores.get(id)
    return store
  }

  builderCardRefByID = observable.map<string, HTMLDivElement>({}, {deep: false})
  setBuilderCardRef = (id: any) => ref => {
    const {builderCardRefByID} = this
    if (ref) {
      builderCardRefByID.set(id, ref)
    } else {
      builderCardRefByID.delete(id)
    }
  }

  isActionButtonEnabled = (type: keyof CatalogActionConfig, data?: any) => {
    if (this.isOfflineItem(data)) return true

    const {features: {actionButtons}, stores: {deployments: {deploymentConfig}}} = this

    // switch(type) {
    //   case ACTION_BUTTONS.TOGGLE_ACTIVE:
    //   case 'create':
    //   case 'delete':
    //   case 'uploadImage':
    //   case 'duplicate': {
    //
    //   }
    //   default: console.debug(`No isActionButtonEnabled yet support for ${type}`)
    // }
    // [type] duplicate/toggleActive is not present in config
    if (actionButtons?.[type] === undefined) return deploymentConfig?.settings?.defaults?.catalog?.features?.actionButtons?.[type] ?? false
    // [type] duplicate/toggleActive is a boolean value
    if (_.isBoolean(actionButtons?.[type])) return actionButtons?.[type]
    // [type] duplicate/toggleActive is a DuplicateConfig
    if (actionButtons?.[type]) {
      const typeActionButton = actionButtons[type] as ActionButtonsConfig
      let isEnabled = false
      try {
        const hasIsEnabledFunction = _.isFunction(typeActionButton?.isEnabled)
        if (!hasIsEnabledFunction) console.debug(`${this.debugId} - No isEnabled function provided on ActionButtons ${type}Config`)
        isEnabled = hasIsEnabledFunction ? typeActionButton.isEnabled({data, stores: this.stores}) : true
      } catch (e) {
        console.error(`${this.debugId} - Error in executing isEnabled function on ActionButtons ${type}Config::`, e)
      }
      return isEnabled
    }
    return false
  }

  @observable selectAll = false

  /**
   * Cmd+a - could be in a specific group or in regular card view
   */
  @action onSelectAll = (e: KeyboardEvent) => {
    const {isProductView, groupBy: {hierarchyGroups}, selection: {selectedIds}} = this
    if (isProductView) {
      this.selectAll = true
    } else {
      const g = _.last(hierarchyGroups.filter(g => g?.focusedGroup))
      if (g?.focusedGroup?.store) {
        selectedIds.clear()
        g.focusedGroup.store.selectAll(e)
      }
    }

    e.stopPropagation()
    e.preventDefault()
  }

  @action isFieldListType = (fieldType: string): boolean => {
    return (fieldType === 'EnumList' || fieldType === 'TagList')
  }

  /**
   * The same card can show up under multiple groups but we need to treat selection separately.
   * @param path
   */
  parseIdFromPath = _.memoize((path?: string) => {
    return path ? _.last(path.split(CATALOG_ID_SEPARATOR)) : path
  })

  @action toggleDataGridView = () => {
    const {settings, settings: {viewMode}} = this
    settings.viewMode = viewMode != 'table' ? 'table' : 'card'
  }


  @action openPDFInTab = () => {
    const {stores: {flexLayoutCatalogRoot: {isActiveCatalogTab, tab: activeTab, tab: {asJson}, addNewChildTab}}} = this,
      newTab: any = _.cloneDeep(asJson)

    if (activeTab.storesToInject.catalog.settings.viewMode === 'pdf' && isActiveCatalogTab) {
      activeTab.storesToInject.catalog.settings.viewMode = 'card'
    } else {
      if (!newTab.config.settings?.catalog) {
        newTab.config.settings.catalog = {}
        newTab.config.settings.catalog.assortment = activeTab?.catalogRootConfig?.settings?.catalog?.assortment
      }
      newTab.config.settings.catalog.viewMode = 'pdf'
      newTab.config.settings.parentTab = newTab.id
      delete newTab.id

      newTab.name = 'pdfInNewTab'
      addNewChildTab(newTab)
    }
  }

  @action getDefaultValueByType = (type: CustomAttributeType) => {
    switch (type) {
      case 'Boolean':
        return false
      case 'Int':
      case 'Float':
        return 0
      case 'String':
        return ''
    }
  }

  strongPasswordChecker(password: string): number {
    let steps = 0,
      arr = Array.from(password)

    let test = this.isStrong(arr)
    while (steps < 15 && test.tooShort || test.tooLong || !test.oneLower || !test.oneUpper || !test.oneNumber || test.threeInARowIndex != -1) {
      const before = [...arr]
      let operation

      //console.log('before', {before: before.join(''), steps, test})

      const {tooShort, tooLong, oneLower, oneUpper, oneNumber, threeInARowIndex} = test
      if (threeInARowIndex != -1) {
        if (tooShort) {
          const char = !oneLower ? 'a' : !oneUpper ? 'A' : arr[threeInARowIndex] === '0' ? '1' : '0'
          operation = `Three in a row - Insert ${char} at ${threeInARowIndex + 2}`
          arr.splice(threeInARowIndex + 2, 0, char)
        } else if ((threeInARowIndex + 3 !== arr.length || !oneLower || !oneUpper || !oneNumber) && (!oneLower || !oneUpper || !oneNumber || arr[threeInARowIndex] === arr[threeInARowIndex + 3])) {
          const c = arr[threeInARowIndex],
            code = c.charCodeAt(0),
            char = !oneLower ? 'a' : !oneUpper ? 'A' : !oneNumber ? '0' :
                                                       c >= 'a' && c <= 'z'
                                                       ? (c === 'z' ? 'a' : String.fromCharCode(code + 1))
                                                       : c >= 'A' && c <= 'Z' ? (c === 'Z' ? 'A' : String.fromCharCode(code + 1))
                                                                              : c == '9' ? '0' : String.fromCharCode(code + 1)

          operation = `Three in a row - Replace ${arr[threeInARowIndex + 2]} at ${threeInARowIndex + 2} with ${char}`

          arr.splice(threeInARowIndex + 2, 1, char)
        } else {
          operation = `Three in a row - delete ${arr[threeInARowIndex + 2]} at ${threeInARowIndex + 2}`
          arr.splice(threeInARowIndex + 2, 1)
        }
      } else if (!tooLong) {
        var char = !oneLower ? 'a' : !oneUpper ? 'A' : !oneNumber ? '0' : arr[arr.length - 1] === 'a' ? 'b' : 'a'
        if (arr.length < 20) {
          operation = `Append ${char}`
          arr.push(char)
        } else {
          operation = `Replace ${arr[Math.floor(arr.length / 2)]} with ${char}`
          arr.splice(Math.floor(arr.length / 2), 1, char)
        }
      } else {
        operation = `Delete ${arr[0]} at index ${0}`
        arr.splice(0, 1)
      }

      steps++
      // DEBUG && console.log('after', {operation, before: before.join(''), after: arr.join(''), length: arr.length, steps, test})
      test = this.isStrong(arr)
    }

    return steps
  };

  isStrong(password: Array<any>): { tooShort: boolean, tooLong: boolean, oneLower: boolean, oneUpper: boolean, oneNumber: boolean, threeInARowIndex: number } {
    let tooShort = password.length < 6
    let tooLong = password.length > 20

    let oneLower = false, oneUpper = false, oneNumber = false, threeInARowIndex = -1
    password.forEach((c, i) => {
      if (!oneLower && c >= 'a' && c <= 'z') {
        oneLower = true
      }

      if (!oneUpper && c >= 'A' && c <= 'Z') {
        oneUpper = true
      }

      if (!oneNumber && c >= '0' && c <= '9') {
        oneNumber = true
      }

      if (threeInARowIndex === -1 && i >= 2 && password[i] === password[i - 1] && password[i] === password[i - 2]) {
        threeInARowIndex = i - 2
      }
    })

    return {tooShort, tooLong, oneLower, oneUpper, oneNumber, threeInARowIndex}
  }

  isCatalogItemEditableByUser = computedFn((item: string | any) => {
    if (!this.workspaceConfig) return false

    const {catalogItemById, stores, workspaceConfig: {isItemEditable}} = this,
      data = _.isString(item) ? catalogItemById[item] : item

    if (this.isOfflineItem(data)) { return true }

    if ((!data && item) || isItemEditable === false) return false

    if (_.isFunction(isItemEditable)) {
      const r = isItemEditable({data, stores})
      // console.log(`isCatalogItemEditableByUser`, {r, data})
      return r
    }
    return true
  })

  verifyCatalogItemIsDraggable = (def: IDragDropElement) => {
    const {isProductView, groupBy: {hierarchyGroups}, isFieldEditable} = this
    if (isProductView) return false

    // Can't reassign secondary name for example
    if (hierarchyGroups.every(hg => hg.isDynamicNestedField)) return false

    const {store} = def.targetExtra as CatalogItemDragItemExtra
    if (!store?.groupByStore?.props?.info?.keys) return false

    // TODO - need a way to tell if a catalog item is modifiable, currently asics has a bunch of action button methods that all duplicate code to check an attribute.  There should be a single isCatalogItemEditable() call that we can use to tell if drag is allowed in the future
    return true
  }

  isOfflineItem = (data: any) => {
    return isOfflineId(_.isString(data) ? data : this.idFor(data))
  }

  @action onPublishSelectedItems = () => {
    const {selection: {offlineSelectedIds, offlineValidSelectedIds, uploadSelectedToC8}} = this
    offlineValidSelectedIds?.length === offlineSelectedIds.length && uploadSelectedToC8()
  }

  @action mergeIntoOfflineItem(id: string, data: any) {
    const {offlineItemsMap, parseIdFromPath} = this
    const oi = offlineItemsMap[parseIdFromPath(id)]
    if (!oi) throw new Error(`No Pending Item exists with id ${id}`)

    _.merge(oi.item.data, data)
  }

  offlineItemsAssignedToLibrary = (library: LibraryItemConfig, value: any) => {
    const {offlineItemsByIndex, index} = this
    return _.values(offlineItemsByIndex[index]).filter(item => {
      const attrValue = _.get(item, library?.assignTo ?? library?.name)
      return Array.isArray(attrValue) ? _.some(attrValue, attr => attr?.id === value?.id) : attrValue?.id === value?.id
    })
  }

  visibleCardFieldsFor = computedFn((field: Partial<ICatalogFieldConfig>, parentField?: Partial<ICatalogFieldConfig>): Array<ICatalogFieldConfig> => {
    const {cv: {visibleCardFields}, esFieldFor} = this

    if (!field && !parentField) return visibleCardFields

    if (field?.field) {
      // console.log(toJS({field, nested}, {recurseEverything: true}))
      const prefix = `${parentField ? `${esFieldFor(parentField)}.` : ''}${field.refField ?? field.field}.`
      let r = visibleCardFields.filter(f => f.field.startsWith(prefix)).map(f => ({...f, field: f.field.substring(prefix.length)}))
      if (!_.isEmpty(r)) {
        return r
      }
    }

    const workspace = this.workspaces.wrapperByIndex[field?.esIndex]
    return !workspace ? [] : this.defaultFieldsFor(workspace)
  })

  defaultFieldsFor = computedFn((workspace: Workspace): Array<ICatalogFieldConfig> => {
    return workspace?.config?.catalog?.fields ? workspace.config?.catalog.fields.filter(f => f?.card?.default) : []
  })

  objectFitFor = (associatedWorkspace: Partial<WorkspaceConfig>) => {
    if (!associatedWorkspace) return undefined
    return associatedWorkspace?.catalog?.features?.card?.objectFit ?? 'contain'
  }

  @observable currentClickPopoverBuilderCardField: BuilderCardFieldStore
  onClickPopoverOpening = (store: BuilderCardFieldStore) => {
    this.currentClickPopoverBuilderCardField = store
  }

  onClickPopoverClosing = (store: BuilderCardFieldStore) => {
    if (this.currentClickPopoverBuilderCardField == store) {
      this.currentClickPopoverBuilderCardField = null
    }
  }

  toggleCardField = (field: ICatalogFieldConfig) => {
    this.cv.toggleCardViewFieldVisible(field.field)
  }

  @computed get minCardSize() { return 0 }

  @computed get maxCardSize() { return 1000 }

  @computed get defaultCardSize() {
    const {minCardSize, maxCardSize} = this
    return (maxCardSize - minCardSize) / 2
  }

  @computed get cardSize() {
    return this.settings.cardSize ?? this.defaultCardSize
  }

  @computed get cardSizeSliderAsMultiplier() {
    const {cardSize, defaultCardSize, minCardSize, maxCardSize, settings: {levelOfDetail}, percentIncreaseInSizeForCardSizePoint} = this,
      percent = Math.max(cardSize, minCardSize) / (maxCardSize - minCardSize)

    if (percent === .5) {
      return 1
    } else if (percent < .5) {
      return .5 + ((percent / .5) * .5)
    } else {
      return Math.max(1, 1 + ((cardSize - defaultCardSize) * percentIncreaseInSizeForCardSizePoint))
    }

    return Math.max(.25, Math.min(1, percent / 2 + .25))

    if (percent < .5) {
      return Math.max(.25, Math.min(1, percent / 2 + .25))
    } else if (percent === .5) {
      return 1
    } else {
      return Math.max(1, 1 + ((cardSize - defaultCardSize) * percentIncreaseInSizeForCardSizePoint))
    }
  }

  @computed get rootStyle() {
    if (!this.stores || this.isGlobalStore) return {}
    const {styleCssVariables, workspaceConfig} = this
    return {
      ...styleCssVariables,
      ...(workspaceConfig?.style ?? {})
    }
  }

  @computed get imageSourceScale() {
    if (!this.stores || this.isGlobalStore) return {}
    const {settings: {imageSource: {scaleOnHover}}} = this
    return {'--image-source-stack-scale': scaleOnHover}
  }

  @computed get imageSourceCssVariables() {
    if (!this.stores || this.isGlobalStore) return {}
    const {imageSourceScale, settings: {imageSource, imageSource: {view}}} = this

    switch (view) {
      case 'stack':
        const {stackedCards: {spread, opacity}} = imageSource
        return {
          '--image-source-stack-spread': `${spread}`,
          '--image-source-stack-opacity': `${opacity}`,
          ...imageSourceScale
        }

      case 'grid':
        const {grid: {orientation, columns, rows}} = imageSource
        return {
          '--image-source-grid-rows': rows,
          '--image-source-grid-columns': columns,
          '--image-source-grid-orientation': orientation,
          ...imageSourceScale
        }

      default:
        return {}
    }
  }

  @computed get styleCssVariables() {
    if (!this.stores || this.isGlobalStore) return {}

    const {
        groupBy, imageBackgroundStyle,
        features: {card: {cssStyles, cardSizes}},
        settings: {
          imageSource: {stackedCards}, imageRatio, groupBy: {flatModeColumns}, showCardLabels
        }, settings,
        cardSizeSliderAsMultiplier, cardSpacingAsMultiplier,
        imageSourceCssVariables
      } = this,
      ratioSize = cardSizes[imageRatio]

    return {
      ...(cssStyles ?? {}),
      '--catalog-image-background': imageBackgroundStyle,
      '--lod0-default-card-size': ratioSize?.size ? `${ratioSize?.size}px` : undefined,
      '--lod1-default-card-size': ratioSize?.size ? `${ratioSize?.size * cardSizes.lodMultiplier}px` : undefined,
      '--max-visible-stack-items': stackedCards?.maxVisible,
      '--hierarchy-length': groupBy.hierarchy.length,
      '--deepest-group-expansion-level': groupBy.deepestExpansionLevel,
      '--catalog-columns': flatModeColumns,
      '--catalog-card-size-value': settings.cardSize,
      '--catalog-card-size-multiplier': cardSizeSliderAsMultiplier,
      '--catalog-spacing-multiplier': cardSpacingAsMultiplier,
      ...this.cssCardImage,
      // '--catalog-spacing-slider-value': catalog.settings.cardSpacing.value,
      // '--catalog-spacing-slider-max': catalog.settings.cardSpacing.max,
      // '--catalog-spacing-slider-min': catalog.settings.cardSpacing.min,
      ...imageSourceCssVariables
    } as any
  }

  @computed get cssCardImage() {
    const {imageBackgroundStyle, features: {card: {alignSelf}}} = this
    return {
      '--catalog-image-background': imageBackgroundStyle,
      '--card-image-align-self': alignSelf
    } as any
  }

  get isShowingClientPDF() { return this.pdfCatalogStore?.isShowingClientPDF}

  @computed get imageBackgroundUrl() {
    const {features, workspace, imageUrlFor, isShowingClientPDF} = this
    const r = features?.imageBackground ?? workspace?.deploymentConfig?.settings?.imageBackground
    if (_.isString(r)) return r
    else if (r) {
      return imageUrlFor(r, {c8Size: isShowingClientPDF ? 'smallImage' : 'largeImage', imageSize: {width: 800, height: 800}}) //, isBackgroundImage: true})
    }
    return null
  }

  @computed get imageBackgroundStyle() {
    const {imageBackgroundUrl: r} = this
    if (r) {
      return `url('${r}')`
    }
    return 'var(--default-catalog-image-background)'
  }

  @observable minCardSpacing = 0
  @observable maxCardSpacing = 4

  @computed get cardSpacingAsMultiplier() {
    const {defaultSpacing, settings, minCardSpacing: min, maxCardSpacing: max} = this,
      value = settings?.cardSpacing ?? defaultSpacing

    return value

    // let percent = Math.max(0, (Math.max(value, min) / (max - min)))  // Should be 0 <= p <= 1
    // if (isNaN(percent)) {
    //   percent = .5
    // }
    //
    // if (percent < .5) {
    //   return Math.max(0, percent * 2)
    // } else if (percent === .5) {
    //   return 1
    // } else {
    //   return Math.max(1, 1 + ((value - defaultSpacing) * .025))
    // }
  }

  @computed get percentIncreaseInSizeForCardSizePoint() {
    const {maxCardSize, defaultCardSize, settings: {levelOfDetail}} = this

    // The css variable needs to be put on to LOD 1 cards in order to not have them become giant when the user wants to zoom in all the way on their lod 0 cards
    // We really want LOD 0 cards scallable to like 25x or even more, but LOD 1 cards limited to like 3 times their original size
    return (levelOfDetail ? 1.5 : 2) / (maxCardSize - defaultCardSize)
  }

  @action resetCardSize = () => {
    this.settings.cardSize = this.defaultCardSize
    this._justResetCardSize = true
    setTimeout(() => this._justResetCardSize = false, 50)
  }

  @observable _justResetCardSize = false

  @action onSetCardSize_throttled = _.throttle((e, value: number) => {
    if (!this._justResetCardSize) {
      this.onSetCardSize(e, value)
    }
  }, 60)

  @action onSetCardSize = (e, value: number) => {
    this.settings.cardSize = value as number
  }

  @action resetCardSpacing = () => {
    const {settings, settings: {cardSpacing: oldValue}} = this
    settings.cardSpacing = this.defaultSpacing
    this._justResetCardSpacing = true
    setTimeout(() => this._justResetCardSpacing = false, 50)
    // console.log(`Reset card spacing from ${oldValue} -> ${settings.cardSpacing}`)
  }

  @computed get defaultSpacing() {
    return 1 //(this.maxCardSpacing - this.minCardSpacing) / 4
  }

  private _justResetCardSpacing = false
  // Below will cause issues with double click getting overridden
  @action onSetCardSpacing_debounced = _.debounce((e, value: number) => {
    if (!this._justResetCardSpacing) {
      this.onSetCardSpacing(e, value)
    }
  }, 20)

  @action onSetCardSpacing = (e, value: number) => {
    const {settings, settings: {cardSpacing, cardSpacing: oldValue}} = this
    settings.cardSpacing = value as number
    // console.log(`Updated card spacing from ${oldValue} -> ${cardSpacing}`)
  }

  @action tryFocusToHierarchy = async () => {
    const {settings, settings: {focusedCatalogItemId}, stores: {es}} = this
    settings.focusToHierarchy = false

    if (focusedCatalogItemId) {
      try {
        const data = await es.get({id: focusedCatalogItemId, index: this.index})
        this.tryMatchSeasonalHierarchyFor(data)
      } catch (err) {
        console.error(err)
      }
    }
  }

  @action tryMatchSeasonalHierarchyFor = (data: any) => {
    if (!data) return

    const {seasonSelectorFields, esFieldFor, idFor, filter, vlp} = this

    seasonSelectorFields.forEach((f) => {
      let esField = esFieldFor(f),
        item = _.get(data, esField)

      switch (f.field) {
        case 'plan':
        case 'collection': {
          if (item) {
            const id = idFor(item)
            if (id) {
              console.debug(`${this.debugId} - Autofocusing to ${f.field} : ${id}`)
              vlp.hierarchy.selected[pluralize(f.field)] = [id]
            }
          }
          break
        }

        case 'folder': {
          item = _.get(data, 'subCategories')
          if (_.isArray(item)) {
            const folders = [item.map(i => this.idFor(i))]
            console.debug(`${this.debugId} - Autofocusing to ${f.field}`, folders)
            vlp.hierarchy.selected.folders = folders
          }
          break
        }

        default: {
          let filterField = filter.filterFieldFor(f)
          if (_.has(data, filterField)) {
            let v: any = _.get(data, filterField)

            // if (f.idField) {
            //   item.id = _.get(v, f.idField)
            // }

            // Season selectors tend not to have their subField set so filter field doesn't give us the real path to the filter key - which is generally name and not id as we want to group the various instances of the same season name into a single dropdown entry in filters
            if (_.isObject(v)) {
              v = v['name'] ?? idFor(v)
            }

            //console.log(toJS({filterField, v, item}, {recurseEverything: true}))

            console.debug(`${this.debugId} - Autofocusing to filter ${f.field}`, {v, item})

            filter.toggleFilter({key: f.field, value: v, item, forceSet: true})
          }
        }
      }
    })
  }

  @computed get workspacesThatUseItemsFromThisCatalog() {
    const {workspaceConfig} = this
    return this.relationships.otherWorkspaceFieldsThatReferTo(workspaceConfig)
  }

  @computed get canShowColumnsSlider() {
    const {isProductView, groupBy: {hierarchyGroups, settings: {columnView}}, settings: {viewMode}} = this

    if (this.isGlobalStore) {
      return Array.from(StoresInstance.allStores.values()).filter(s => !s.isGlobalStore && s.isCurrentWorkspace).some(s => s.catalog.canShowColumnsSlider)
    }

    return viewMode == 'card' && !(isProductView || this.isShowingClientPDF || !hierarchyGroups.some(hg => hg?.isFlat) || columnView)
  }

  /**
   * YFTS-9805 If the user pastes multiple images with no card selected we want to prompt if they want to create
   * one item with many images or 1 item per image - save off the clipboard contents so we have it ready to go after they answer the modal question
   */
  @observable.ref filesToMaybePaste: Array<File>

  /**
   * YFTS-8034 Paste image into card
   * @param e
   */
  @action onPasteImages = async (files: Array<File>) => {
    files = files.filter(_.identity)
    const {addImageToCatalogItem, createItem, dragDrop: {ddHandlers}, isActionButtonEnabled, focusedCatalogItemStore, flexLayout: {isMaximized}, sidebar} = this

    // console.log(`onPaste`, img)
    let catalogItemId: string, index: string

    if (isMaximized && sidebar.right.isShowingModal) {
      catalogItemId = sidebar.right.selectedPathItem?.idWithPath
      index = sidebar.right.selectedPathItem?.esIndex
    }

    if (focusedCatalogItemStore) {
      catalogItemId = focusedCatalogItemStore.idWithPath
      index = focusedCatalogItemStore.index
    }

    if (catalogItemId) {
      try {
        const isOfflineItem = isOfflineId(catalogItemId)

        if (_.isEmpty(files)) {
          console.debug(`Ignoring paste event - no files to upload`, files)
          return
        } else if (!isOfflineItem && !isActionButtonEnabled('uploadImages', {id: catalogItemId})) {
          console.debug(`Ignoring paste event - upload images is not allowed`, files)
          return
        }

        const ddh = ddHandlers.get(catalogItemId),
          realId = this.parseIdFromPath(catalogItemId)

        ddh && (ddh.state.status = 'loading')
        // Perform these sequentially so we can start showing images as they finish uploading
        for (const file of files) {
          await this.addImageToCatalogItem({id: realId, file, index})
        }

        ddh && (ddh.state.status = null)
        // dragDropEventHandlers.state.status = 'success'
        // setTimeout(() => dragDropEventHandlers.state.status = null, ui.settings.updateIndicatorMs)
      } catch (err) {
        console.error(err)
      }
    } else {
      // Todo - What about sidebar focus?

      if (!this.isActionButtonEnabled('create')) {
        console.log(`User cannot create items - ignoring paste image`)
        return
      }

      if (files.length === 1) {
        const offlineItem = await this.createItem.createOfflineItem()
        this.focusedCatalogItemId = offlineItem.item.data._id
        await this.onPasteImages(files)
      } else {
        this.filesToMaybePaste = files
      }
    }
  }

  @action addImageToCatalogItem = async ({id, ...params}: { img?: DataPng, id: string, index?: string, file?: File }) => {
    const index = params.index ?? this.index,
      workspace = this.workspaces.wrapperByIndex[index],
      {sidebar: {right}, img: {addImagesToOfflineItem, uploadImages, dataURLtoFile}} = this, isOfflineItem = isOfflineId(id)

    if (!isOfflineItem && (!workspace || !workspace.catalog.isActionButtonEnabled('uploadImages'))) {
      console.info(`Add Image is disabled for index - ${index}`)
      return
    }

    // DEBUG && console.debug(`addImageToCatalogItem`, {img, id, index})

    if (!params.file && !params.img) {
      console.error(`Invalid call to addImageToCatalogItem - either file or img needs to be specified`, {id, params})
      return
    }

    const file = params.file ?? dataURLtoFile(params.img),
      c8Image: { exId: string } = await uploadImages([{file}], isOfflineItem ? null : [id])

    if (isOfflineItem) {
      addImagesToOfflineItem(id, [c8Image])
    } else {
      await this.refetch('catalogItems')
    }

    if (this.isDataTableView) this._tableRefreshDataByIds = [id]
  }

  workspaceWrapperForField = (f: ICatalogFieldConfig) => {
    const ws = this.workspaceForField(f)
    return ws ? this.workspaces.wrapperById[ws._id] : this.workspace
  }

  workspaceForField = computedFn((f: ICatalogFieldConfig): Workspace => {
    if (!f) return this.workspace

    const {workspace, esIndex, section} = f,
      {workspaces: {wrapperById, wrapperByIndex}} = this

    if (workspace) {
      return wrapperById[workspace]
    } else if (esIndex) {
      const r = wrapperByIndex[esIndex]
      return r

      // if (_.isEmpty(r)) return null
      // else if (r.length === 1) {
      //   return _.first(r)
      // } else {
      //   console.warn(`${this.debugId} - Multiple workspaces were found for the same index: ${esIndex} - using ${_.first<WorkspaceConfig>(r).title} - consider specifying a workspace parameter on this field ${f.field} instead.`)
      //   return _.first(r)
      // }
    } else if (section) {
      const {sectionById} = this
      return wrapperById[sectionById[section]?.workspace?._id]
    }

    return this.workspace
  }, {keepAlive: true})

  sectionIdForField = computedFn((f: ICatalogFieldConfig, ws?: WorkspaceConfig) => {
    if (!f.esIndex) return ws?._id ?? this.workspaceConfig?._id

    const matchingSectionId = `${f.field}.${f.esIndex}`

    if (!ws) {
      ws = this.sectionById[f.section]?.workspace

      if (!ws) {
        const {workspaceConfig} = this

        const s = workspaceConfig.sections.find(s => s.id === matchingSectionId) ?? workspaceConfig.sections.find(s => s.esIndex == f.esIndex)
        if (s) return s.id
      }
    }
    return matchingSectionId // ws ? `${f.field}.${ws._id}.${f.esIndex}` : matchingSectionId
  }, {keepAlive: true})

  libraryForSection = (section: ISectionConfig) => {
    const {libraryById} = this
    return section ? libraryById[section.id] : null
  }

  newSectionFor = (f: ICatalogFieldConfig, {depth}: { depth: number }): ISectionConfig => {
    const ws = this.workspaceForField(f)

    if (!ws) { return null }

    const {features: {sectionOverrides}} = this,
      id = this.sectionIdForField(f, ws?.config)

    return {
      id,
      depth,
      label: pluralize(this.labelForField(f) ?? this.workspaceConfig.itemName, 1),
      field: f.field,
      esIndex: ws.catalog?.index,
      workspace: ws?.config ?? this.workspace.config,
      ...(sectionOverrides[id] ?? {})
    }
  }

  activeFieldFor = (ws: Partial<WorkspaceConfig>) => {
    return (ws?.catalog?.fields ?? []).find(f => !f.section && (f.___isActiveField || f.field === 'attributes.Active'))?.field
  }

  pathToField = (field: ICatalogFieldConfig): Array<ICatalogFieldConfig> => {
    if (!field) return []

    return this.pathToFieldName(field.field)
  }

  pathToFieldName = (field: string): Array<ICatalogFieldConfig> => {
    if (!field) return []

    const {_fieldByName} = this
    const r = field.split('.').reduce((acc, v) => {
      const p = `${acc.path ? `${acc.path}.` : ''}${v}`,
        r = acc.r,
        field = _fieldByName[p]

      return {path: p, r: !field ? r : [...r, field]}
    }, {path: '', r: []})

    // console.log(r)
    return r.r
  }

  sectionForField = (f: ICatalogFieldConfig) => {
    if (!f?.section) return null

    return this.sectionById[f.section]
  }

  @action clearFocusedItem = () => {
    this.focusedCatalogItemId = null
  }
  ref: HTMLDivElement
  onSetRef = r => {
    this.ref = r
  }

  @computed get placeholderImg() {
    const {vlp, workspaceConfig} = this
    return workspaceConfig?.customImagePlaceholders ? vlp?.placeholderImg : null
  }

  placeholderImgFor = (thing) => {
    const {vlp, workspaceConfig} = this
    if (workspaceConfig?.customImagePlaceholders && vlp) {
      return vlp.placeholderImgFor(thing)
    }

    return null
  }

  libraryForField = (field: ICatalogFieldConfig): LibraryItemConfigWrapper => {
    return this.libraryByIndex[field?.esIndex]
  }

  @computed get isShowingHeroCards() {
    const {groupBy: {hierarchyGroups}} = this
    return hierarchyGroups.some(h => h?.fieldDefinition?.group?.subGroup)
  }

  associatedWorkspaceFor = (catalogField: ICatalogFieldConfig) => {
    return catalogField?.esIndex ? this.workspaces.wrapperById[this.workspaceByIndex[catalogField.esIndex]?._id] : null
  }

  updateTableViewData({ids, index}: { ids: string[]; index: string }) {
    StoresInstance.allStores.forEach(({catalog}) => {
      if (catalog.isDataTableView && catalog.index === index)
        catalog._tableRefreshDataByIds = ids
    })
  }

  onSwitchToPdfView = () => this.pdfCatalogStore.onSwitchToPdfView()
  @action onSwitchToCardView = () => {
    this.pdfCatalogStore.isShowingClientPDF = false
    this.settings.viewMode = 'card'
  }

  @action onSwitchToGridView = () => {
    this.pdfCatalogStore.isShowingClientPDF = false
    this.settings.viewMode = 'table'
  }

  get pdfConfig() { return this.pdfCatalogStore.config}
  get arePdfTemplatesAvailable() { return this.pdfCatalogStore.arePdfTemplatesAvailable}
  toggleShowPdf = () => this.pdfCatalogStore.toggleShowPdf()
  get isClientPdfAvailable() { return this.pdfCatalogStore.isClientPdfAvailable}
  get isServerPDFAvailable() { return this.pdfCatalogStore.isServerPdfAvailable}

  assignToForIndex = (index: string) => {
    const {fieldByIndex} = this,
      fields = fieldByIndex[index],
      candidates = (fields ?? []).filter(f => f?.assignTo !== false)

    return _.first(candidates)
  }

  indexForImageSource = (imageSource: CatalogImageSourceConfig) => {
    const {findFieldInTree} = this
    if (imageSource.index) return imageSource.index

    return findFieldInTree(imageSource.field, true)?.esIndex ?? this.index
  }
}

export interface CatalogItemDragItemExtra {
  store: BuilderCardStore
}

export const useCatalog = () => {
  const {catalog} = useStores() ?? globalStores
  return catalog
}

export const isTagField = (field: Partial<ICatalogFieldConfig>) => {
  return _.includes(['Tag', 'TagList'], field?.def?.type)
}

export const isArrayCatalogField = (field: Partial<ICatalogFieldConfig>) => {
  return _.includes(['RefList', 'TagList', 'EnumList'], field?.def?.type)
}



// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogStore.ts